import React, { useState, useMemo, useCallback, useRef } from 'react';
import posthog from 'posthog-js';
import { useDataViz } from '../../hooks/useDataViz';
import { progressService } from '../../services/progressService';
import { AnimatePresence, motion } from 'framer-motion';
import {
    buildSQL,
    getAvailableColumns,
    getColumnCategory,
    getDisplayName,
    AGGREGATION_FUNCTIONS,
    FILTER_OPERATORS,
    JOIN_TYPES,
    DATE_GRANULARITIES,
    RELATIVE_DATE_OPTIONS,
} from '../../utils/queryBuilder';
import SuccessOverlay from '../exercises/SuccessOverlay';
import {
    BarChart, Bar, Line, PieChart, Pie, Cell,
    Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, LabelList, Label, ComposedChart,
    ScatterChart, Scatter
} from 'recharts';
import { ComposableMap, Geographies, Geography, ZoomableGroup, Marker } from 'react-simple-maps';
import {
    Database, Play, X, Plus, Table, BarChart3,
    LineChart as LineChartIcon, PieChart as PieChartIcon,
    AreaChart as AreaChartIcon, Code, Loader2, AlertCircle,
    Filter, Layers, Group, Link2, CheckCircle2, ChevronRight,
    Lightbulb, ArrowRight, Menu, Trophy,
    ArrowLeft, ChevronDown, Check,
    Hash, Type as TypeIcon, Calendar, CheckSquare, Circle,
    Sigma, LayoutList, ArrowUp, ArrowDown, Activity, Target,
    Eye, Settings2, BoxSelect,
    HelpCircle, ChevronLeft, Clock,
    Map, Crosshair, AlignJustify, Combine, ArrowLeftRight, Grid3x3, GripVertical
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import ThemeToggle from '../ThemeToggle';
import ParticleBackground from '../common/ParticleBackground';
import logo from '../../assets/logo.png';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';

// ─── Country Name Normalizer (for Map chart) ────────────────────────────────
const COUNTRY_ALIASES = {
    'us': 'united states of america',
    'usa': 'united states of america',
    'united states': 'united states of america',
    'u.s.a.': 'united states of america',
    'u.s.': 'united states of america',
    'uk': 'united kingdom',
    'gb': 'united kingdom',
    'great britain': 'united kingdom',
    'britain': 'united kingdom',
    'russia': 'russian federation',
    'south korea': 'south korea',
    'north korea': 'north korea',
    'iran': 'iran (islamic republic of)',
    'czech republic': 'czechia',
    'vietnam': 'viet nam',
    'syria': 'syrian arab republic',
    'tanzania': 'united republic of tanzania',
    'bolivia': 'bolivia (plurinational state of)',
    'venezuela': 'venezuela (bolivarian republic of)',
    'laos': "lao pdr",
    'moldova': 'republic of moldova',
    'congo': 'dem. rep. congo',
    'dr congo': 'dem. rep. congo',
    'drc': 'dem. rep. congo',
    'taiwan': 'taiwan',
};
const normalizeCountryName = (name) => {
    const lower = (name || '').toLowerCase().trim();
    return COUNTRY_ALIASES[lower] || lower;
};

// ─── City Coordinate Lookup ─────────────────────────────────────────────────
const CITY_COORDS = {
  // UK
  london:[51.5074,-0.1278],manchester:[53.4808,-2.2426],birmingham:[52.4862,-1.8904],
  edinburgh:[55.9533,-3.1883],glasgow:[55.8642,-4.2518],liverpool:[53.4084,-2.9916],
  leeds:[53.8008,-1.5491],bristol:[51.4545,-2.5879],sheffield:[53.3811,-1.4701],
  oxford:[51.752,-1.2577],cambridge:[52.2053,0.1218],bath:[51.381,-2.3597],
  york:[53.9600,-1.0873],coventry:[52.4068,-1.5197],brighton:[50.8229,-0.1363],
  // USA
  'new york':[40.7128,-74.006],'new york city':[40.7128,-74.006],nyc:[40.7128,-74.006],
  'los angeles':[34.0522,-118.2437],la:[34.0522,-118.2437],
  chicago:[41.8781,-87.6298],houston:[29.7604,-95.3698],phoenix:[33.4484,-112.074],
  philadelphia:[39.9526,-75.1652],dallas:[32.7767,-96.797],
  'san antonio':[29.4241,-98.4936],'san diego':[32.7157,-117.1611],
  'san jose':[37.3382,-121.8863],'san francisco':[37.7749,-122.4194],sf:[37.7749,-122.4194],
  austin:[30.2672,-97.7431],jacksonville:[30.3322,-81.6557],
  'fort worth':[32.7555,-97.3308],columbus:[39.9612,-82.9988],
  charlotte:[35.2271,-80.8431],indianapolis:[39.7684,-86.1581],
  seattle:[47.6062,-122.3321],denver:[39.7392,-104.9903],
  boston:[42.3601,-71.0589],miami:[25.7617,-80.1918],
  atlanta:[33.749,-84.388],minneapolis:[44.9778,-93.265],
  portland:[45.5231,-122.6765],'las vegas':[36.1699,-115.1398],
  nashville:[36.1627,-86.7816],baltimore:[39.2904,-76.6122],
  memphis:[35.1495,-90.049],louisville:[38.2527,-85.7585],
  milwaukee:[43.0389,-87.9065],albuquerque:[35.0844,-106.651],
  tucson:[32.2226,-110.9747],fresno:[36.7378,-119.7871],
  sacramento:[38.5816,-121.4944],kansas:[39.0997,-94.5786],
  'kansas city':[39.0997,-94.5786],mesa:[33.4152,-111.8315],
  omaha:[41.2565,-95.9345],raleigh:[35.7796,-78.6382],
  richmond:[37.5407,-77.436],'virginia beach':[36.8529,-75.978],
  honolulu:[21.3069,-157.8583],detroit:[42.3314,-83.0458],
  // Canada
  toronto:[43.6532,-79.3832],montreal:[45.5017,-73.5673],
  vancouver:[49.2827,-123.1207],calgary:[51.0447,-114.0719],
  ottawa:[45.4215,-75.6972],edmonton:[53.5461,-113.4938],
  winnipeg:[49.8951,-97.1384],quebec:[46.8139,-71.2082],
  // Germany
  berlin:[52.52,13.405],munich:[48.1351,11.582],hamburg:[53.5753,10.0153],
  frankfurt:[50.1109,8.6821],cologne:[50.9333,6.95],stuttgart:[48.7758,9.1829],
  dusseldorf:[51.2217,6.7762],dortmund:[51.5136,7.4653],
  // France
  paris:[48.8566,2.3522],lyon:[45.764,4.8357],marseille:[43.2965,5.3698],
  toulouse:[43.6047,1.4442],nice:[43.7102,7.262],nantes:[47.2184,-1.5536],
  strasbourg:[48.5734,7.7521],bordeaux:[44.8378,-0.5792],
  // Spain
  madrid:[40.4168,-3.7038],barcelona:[41.3851,2.1734],
  valencia:[39.4699,-0.3763],seville:[37.3891,-5.9845],
  bilbao:[43.263,-2.935],malaga:[36.7213,-4.4213],
  // Italy
  rome:[41.9028,12.4964],milan:[45.4654,9.1859],naples:[40.8518,14.2681],
  turin:[45.0703,7.6869],florence:[43.7696,11.2558],venice:[45.4408,12.3155],
  // Netherlands
  amsterdam:[52.3676,4.9041],rotterdam:[51.9225,4.4792],
  'the hague':[52.0705,4.3007],'den haag':[52.0705,4.3007],
  // Portugal
  lisbon:[38.7169,-9.1399],porto:[41.1496,-8.6109],
  // Belgium
  brussels:[50.8503,4.3517],antwerp:[51.2194,4.4025],
  // Switzerland
  zurich:[47.3769,8.5417],geneva:[46.2044,6.1432],bern:[46.948,7.4474],
  // Austria
  vienna:[48.2082,16.3738],graz:[47.0707,15.4395],salzburg:[47.8095,13.055],
  // Scandinavia
  stockholm:[59.3293,18.0686],oslo:[59.9139,10.7522],copenhagen:[55.6761,12.5683],
  helsinki:[60.1699,24.9384],reykjavik:[64.1355,-21.8954],
  // Eastern Europe
  warsaw:[52.2297,21.0122],prague:[50.0755,14.4378],budapest:[47.4979,19.0402],
  bucharest:[44.4268,26.1025],sofia:[42.6977,23.3219],zagreb:[45.815,15.9819],
  belgrade:[44.7866,20.4489],athens:[37.9838,23.7275],
  // Turkey
  istanbul:[41.0082,28.9784],ankara:[39.9334,32.8597],izmir:[38.4192,27.1287],
  // Russia
  moscow:[55.7558,37.6173],'saint petersburg':[59.9311,30.3609],
  'st. petersburg':[59.9311,30.3609],
  // Middle East
  dubai:[25.2048,55.2708],'abu dhabi':[24.4539,54.3773],riyadh:[24.6877,46.7219],
  'tel aviv':[32.0853,34.7818],jerusalem:[31.7683,35.2137],
  beirut:[33.8938,35.5018],doha:[25.2854,51.531],kuwait:[29.3759,47.9774],
  manama:[26.2285,50.5857],muscat:[23.5859,58.4059],amman:[31.9539,35.9106],
  // Africa
  lagos:[6.5244,3.3792],cairo:[30.0444,31.2357],nairobi:[1.2921,36.8219],
  johannesburg:[-26.2041,28.0473],'cape town':[-33.9249,18.4241],
  accra:[5.6037,-0.187],dakar:[14.6937,-17.4441],addis:[9.145,40.4897],
  // Asia Pacific
  tokyo:[35.6762,139.6503],osaka:[34.6937,135.5022],kyoto:[35.0116,135.7681],
  beijing:[39.9042,116.4074],shanghai:[31.2304,121.4737],shenzhen:[22.5429,114.0596],
  guangzhou:[23.1291,113.2644],chengdu:[30.5728,104.0668],
  'hong kong':[22.3193,114.1694],taipei:[25.033,121.5654],
  seoul:[37.5665,126.978],busan:[35.1796,129.0756],
  singapore:[1.3521,103.8198],'kuala lumpur':[3.1390,101.6869],
  bangkok:[13.7563,100.5018],jakarta:[-6.2088,106.8456],
  manila:[14.5995,120.9842],'ho chi minh':[10.8231,106.6297],
  hanoi:[21.0285,105.8542],yangon:[16.8661,96.1951],
  mumbai:[19.076,72.8777],delhi:[28.6139,77.209],'new delhi':[28.6139,77.209],
  bangalore:[12.9716,77.5946],bengaluru:[12.9716,77.5946],
  hyderabad:[17.385,78.4867],chennai:[13.0827,80.2707],
  kolkata:[22.5726,88.3639],pune:[18.5204,73.8567],
  karachi:[24.8607,67.0011],lahore:[31.5204,74.3587],
  islamabad:[33.6844,73.0479],dhaka:[23.8103,90.4125],
  colombo:[6.9271,79.8612],kathmandu:[27.7172,85.324],
  // Australia / NZ
  sydney:[-33.8688,151.2093],melbourne:[-37.8136,144.9631],
  brisbane:[-27.4705,153.026],perth:[-31.9505,115.8605],
  adelaide:[-34.9285,138.6007],canberra:[-35.2809,149.13],
  auckland:[-36.8485,174.7633],wellington:[-41.2865,174.7762],
  // South America
  'sao paulo':[-23.5505,-46.6333],rio:[-22.9068,-43.1729],
  'rio de janeiro':[-22.9068,-43.1729],'buenos aires':[-34.6037,-58.3816],
  bogota:[4.711,-74.0721],lima:[-12.046,-77.0428],
  santiago:[-33.4489,-70.6693],caracas:[10.4806,-66.9036],quito:[-0.1807,-78.4678],
  // Caribbean / Central America
  havana:[23.1136,-82.3666],'panama city':[8.9936,-79.5197],
  'mexico city':[19.4326,-99.1332],guadalajara:[20.6597,-103.3496],monterrey:[25.6866,-100.3161],
};

const getCityCoords = (cityName) => {
    const key = (cityName || '').toLowerCase().trim()
        .replace(/[^a-z ]/g, '').trim();
    return CITY_COORDS[key] || null;
};

// ─── Map Chart (Choropleth + City Markers) ──────────────────────────────────
const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const MapChart = ({ data, dimensionCol, seriesCols, isDark }) => {
    const [tooltip, setTooltip] = useState({ visible: false, label: '', value: '', x: 0, y: 0 });
    const [position, setPosition] = useState({ coordinates: [0, 20], zoom: 1 });
    const valueCol = seriesCols[0];

    // Detect if data has explicit lat/lng columns
    const cols = data && data.length > 0 ? Object.keys(data[0]) : [];
    const latCol = cols.find(c => /^lat(itude)?$/i.test(c));
    const lngCol = cols.find(c => /^(lon|lng|long)(itude)?$/i.test(c));

    // Detect city mode: has lat/lng cols, OR none of the dimension values match a country
    const hasCoordsInData = !!(latCol && lngCol);
    const isCityMode = hasCoordsInData || (() => {
        if (!data || data.length === 0) return false;
        const sample = data.slice(0, 10);
        const countryMatches = sample.filter(r => !!normalizeCountryName(String(r[dimensionCol] || '')));
        // If more than half match cities in lookup but not countries → city mode
        const cityMatches = sample.filter(r => !!getCityCoords(String(r[dimensionCol] || '')));
        return cityMatches.length > 0 && cityMatches.length >= countryMatches.length * 0.5;
    })();

    const valueMap = useMemo(() => {
        const map = {};
        (data || []).forEach(row => {
            const key = normalizeCountryName(String(row[dimensionCol] || ''));
            map[key] = Number(row[valueCol]) || 0;
        });
        return map;
    }, [data, dimensionCol, valueCol]);

    const maxVal = Math.max(...Object.values(valueMap), 1);

    // Build marker data for city mode
    const cityMarkers = useMemo(() => {
        if (!isCityMode) return [];
        return (data || []).map(row => {
            const label = String(row[dimensionCol] || '');
            const val = Number(row[valueCol]) || 0;
            let coords;
            if (hasCoordsInData) {
                coords = [Number(row[lngCol]), Number(row[latCol])];
            } else {
                const raw = getCityCoords(label);
                coords = raw ? [raw[1], raw[0]] : null; // CITY_COORDS is [lat,lng]; Marker needs [lng,lat]
            }
            return coords ? { label, val, coords } : null;
        }).filter(Boolean);
    }, [data, dimensionCol, valueCol, isCityMode, hasCoordsInData, latCol, lngCol]);

    const maxCityVal = useMemo(() => Math.max(...cityMarkers.map(m => m.val), 1), [cityMarkers]);

    const fmtVal = (v) => {
        const n = Number(v);
        if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
        if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
        return n.toLocaleString();
    };

    const getCountryColor = (geoName) => {
        const key = normalizeCountryName(geoName);
        const val = valueMap[key];
        if (!val) return isDark ? '#1e293b' : '#dde4ec';
        const intensity = val / maxVal;
        const g = Math.round(115 * (1 - intensity * 0.75));
        return `rgba(249, ${Math.max(0, g)}, 22, ${0.25 + intensity * 0.75})`;
    };

    const handleMoveEnd = (pos) => setPosition(pos);
    const handleZoomIn = () => setPosition(p => ({ ...p, zoom: Math.min(p.zoom * 1.5, 12) }));
    const handleZoomOut = () => setPosition(p => ({ ...p, zoom: Math.max(p.zoom / 1.5, 1) }));
    const handleReset = () => setPosition({ coordinates: [0, 20], zoom: 1 });

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Map canvas */}
            <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
                <ComposableMap
                    projection="geoMercator"
                    projectionConfig={{ scale: 130 }}
                    viewBox="0 0 800 420"
                    preserveAspectRatio="xMidYMid meet"
                    style={{ width: '100%', height: '100%', display: 'block' }}
                >
                    <ZoomableGroup
                        zoom={position.zoom}
                        center={position.coordinates}
                        onMoveEnd={handleMoveEnd}
                        translateExtent={[[-200, -100], [1000, 520]]}
                    >
                        <Geographies geography={GEO_URL}>
                            {({ geographies }) =>
                                geographies.map(geo => {
                                    const geoName = geo.properties.name || '';
                                    const key = normalizeCountryName(geoName);
                                    const val = valueMap[key];
                                    return (
                                        <Geography
                                            key={geo.rsmKey}
                                            geography={geo}
                                            fill={isCityMode ? (isDark ? '#1e293b' : '#dde4ec') : getCountryColor(geoName)}
                                            stroke={isDark ? '#2d3f55' : '#c5d0de'}
                                            strokeWidth={0.5 / position.zoom}
                                            onMouseMove={!isCityMode ? (e) => {
                                                setTooltip({
                                                    visible: true,
                                                    label: geoName,
                                                    value: val ? fmtVal(val) : 'No data',
                                                    x: e.clientX,
                                                    y: e.clientY,
                                                });
                                            } : undefined}
                                            onMouseLeave={!isCityMode ? () => setTooltip(t => ({ ...t, visible: false })) : undefined}
                                            style={{
                                                hover: { fill: isCityMode ? (isDark ? '#1e293b' : '#dde4ec') : '#fb923c', outline: 'none' },
                                                default: { outline: 'none', transition: 'fill 180ms ease' },
                                                pressed: { fill: isCityMode ? (isDark ? '#1e293b' : '#dde4ec') : '#f97316', outline: 'none' },
                                            }}
                                        />
                                    );
                                })
                            }
                        </Geographies>

                        {/* City Markers */}
                        {isCityMode && cityMarkers.map((marker, i) => {
                            const intensity = marker.val / maxCityVal;
                            const r = 3 + intensity * 10; // radius 3-13px
                            return (
                                <Marker
                                    key={i}
                                    coordinates={marker.coords}
                                    onMouseMove={(e) => setTooltip({ visible: true, label: marker.label, value: fmtVal(marker.val), x: e.clientX, y: e.clientY })}
                                    onMouseLeave={() => setTooltip(t => ({ ...t, visible: false }))}
                                >
                                    <circle
                                        r={r / position.zoom}
                                        fill={`rgba(249,115,22,${0.3 + intensity * 0.7})`}
                                        stroke={isDark ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.8)'}
                                        strokeWidth={1 / position.zoom}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </Marker>
                            );
                        })}
                    </ZoomableGroup>
                </ComposableMap>

                {/* Zoom controls */}
                <div className="absolute bottom-3 right-3 flex flex-col gap-1">
                    <button
                        onClick={handleZoomIn}
                        title="Zoom in"
                        className={`w-7 h-7 flex items-center justify-center rounded-md text-sm font-bold shadow border transition-colors ${
                            isDark ? 'bg-[#1e293b] border-white/10 text-gray-300 hover:text-white hover:bg-[#334155]' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                    >+</button>
                    <button
                        onClick={handleZoomOut}
                        title="Zoom out"
                        disabled={position.zoom <= 1}
                        className={`w-7 h-7 flex items-center justify-center rounded-md text-sm font-bold shadow border transition-colors ${
                            position.zoom <= 1
                                ? isDark ? 'bg-[#1e293b] border-white/5 text-gray-700 cursor-not-allowed' : 'bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed'
                                : isDark ? 'bg-[#1e293b] border-white/10 text-gray-300 hover:text-white hover:bg-[#334155]' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                    >−</button>
                    {position.zoom > 1 && (
                        <button
                            onClick={handleReset}
                            title="Reset view"
                            className={`w-7 h-7 flex items-center justify-center rounded-md text-[10px] font-bold shadow border transition-colors ${
                                isDark ? 'bg-[#1e293b] border-white/10 text-gray-400 hover:text-white hover:bg-[#334155]' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                            }`}
                        >
                            ↺
                        </button>
                    )}
                </div>

                {/* Drag hint - disappears once zoomed */}
                {position.zoom === 1 && (
                    <div className={`absolute bottom-3 left-3 text-[10px] font-medium pointer-events-none ${
                        isDark ? 'text-gray-600' : 'text-gray-400'
                    }`}>
                        Scroll to zoom · Drag to pan
                    </div>
                )}
            </div>

            {/* Legend */}
            <div className={`flex items-center justify-center gap-2 py-1.5 flex-shrink-0 border-t ${
                isDark ? 'border-white/5' : 'border-gray-100'
            }`}>
                {isCityMode ? (
                    <>
                        <circle className="inline-block" style={{ display:'inline-block', width:8, height:8, borderRadius:'50%', background:'rgba(249,115,22,0.4)', verticalAlign:'middle', marginRight:2 }} />
                        <span className={`text-[10px] font-medium ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Small value</span>
                        <circle style={{ display:'inline-block', width:14, height:14, borderRadius:'50%', background:'rgba(249,115,22,1)', verticalAlign:'middle', margin:'0 2px' }} />
                        <span className={`text-[10px] font-medium ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Large value</span>
                        <span className={`text-[10px] mx-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`}>·</span>
                        <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{cityMarkers.length} cities</span>
                    </>
                ) : (
                    <>
                        <span className={`text-[10px] font-medium ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Low</span>
                        <div className="w-28 h-2 rounded-full" style={{ background: 'linear-gradient(to right, rgba(249,115,22,0.2), rgba(249,115,22,1))' }} />
                        <span className={`text-[10px] font-medium ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>High</span>
                        <span className={`text-[10px] mx-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`}>·</span>
                        <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{Object.keys(valueMap).length} countries with data</span>
                    </>
                )}
            </div>

            {/* Tooltip */}
            {tooltip.visible && (
                <div
                    className="fixed z-[9999] pointer-events-none px-2.5 py-1.5 rounded-lg text-xs font-medium shadow-xl border"
                    style={{
                        left: tooltip.x + 14,
                        top: tooltip.y - 44,
                        backgroundColor: isDark ? '#1e293b' : 'rgba(255,255,255,0.97)',
                        borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0',
                        color: isDark ? '#e2e8f0' : '#1e293b',
                    }}
                >
                    <span className="font-bold">{tooltip.label}</span>
                    <span className={`ml-2 font-bold ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>{tooltip.value}</span>
                </div>
            )}
        </div>
    );
};


// ─── Schema Reference Panel ─────────────────────────────
// ─── Schema Reference Panel ─────────────────────────────
const SchemaPanel = ({ schemas, executeQuery }) => {
    const tables = Object.keys(schemas || {}).map(t => ({
        key: t, display: t, desc: `Data for ${t}`
    }));
    const [previewData, setPreviewData] = useState({});

    React.useEffect(() => {
        let mounted = true;
        const loadPreviews = async () => {
            if (!executeQuery) return;
            for (const t of tables) {
                try {
                    const { rows } = await executeQuery(`SELECT * FROM "${t.key}" LIMIT 3`);
                    if (mounted) {
                        setPreviewData(prev => ({ ...prev, [t.key]: rows }));
                    }
                } catch (e) {
                    // Ignore initialization errors
                }
            }
        };
        loadPreviews();
        return () => { mounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run once on mount to avoid loops if executeQuery is unstable

    return (
        <div className="bg-white dark:bg-[#1e293b] rounded-xl border border-gray-200 dark:border-white/10 p-4">
            <div className="flex items-center gap-2 mb-3">
                <Database size={14} className="text-orange-500" />
                <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">Schema Reference</h3>
            </div>
            <div className="space-y-4">
                {tables.map(t => (
                    <div key={t.key}>
                        <div className="flex items-center gap-2 mb-1.5">
                            <span className="text-xs font-bold text-orange-500 font-mono">{t.display}</span>
                            <span className="text-[10px] text-gray-400">- {t.desc}</span>
                        </div>

                        <div className="bg-gray-50 dark:bg-black/20 rounded border border-gray-200 dark:border-white/10 overflow-hidden">
                            {previewData[t.key] && previewData[t.key].length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="bg-gray-100 dark:bg-white/5 border-b border-gray-200 dark:border-white/10">
                                                {Object.keys(previewData[t.key][0]).map(k => (
                                                    <th key={k} className="px-2 py-1 text-[9px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{k}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-white/5">
                                            {previewData[t.key].map((row, i) => (
                                                <tr key={i} className="hover:bg-black/5 dark:hover:bg-white/5">
                                                    {Object.values(row).map((val, j) => (
                                                        <td key={j} className="px-2 py-1 text-[10px] text-gray-600 dark:text-gray-300 font-mono whitespace-nowrap max-w-[150px] truncate" title={String(val)}>
                                                            {String(val)}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="p-2 text-center text-[10px] text-gray-400 italic">
                                    {schemas[t.key] ? 'Loading preview...' : 'Initializing...'}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};


// ─── Fixed dropdown: position:fixed so it escapes overflow containers ────────
// Position is calculated once on open. No scroll listeners = no lag/jitter.
const useFixedDropdown = (open, triggerRef) => {
    // Start hidden if opening to prevent flash of unpositioned content
    const [style, setStyle] = React.useState(open ? { opacity: 0, position: 'fixed' } : {});

    React.useLayoutEffect(() => {
        if (open && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setStyle({
                position: 'fixed',
                bottom: window.innerHeight - rect.top + 4,
                left: Math.max(4, Math.min(rect.left, window.innerWidth - 320)),
                zIndex: 9999,
            });
        } else if (!open) {
            setStyle({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // If open but not yet measured (no coords), keep hidden
    if (open && !style.left && !style.bottom) {
        return { opacity: 0, position: 'fixed', pointerEvents: 'none' };
    }

    return style;
};

// ─── Custom Select Component ─────────────────────────────
const CustomSelect = ({ value, onChange, options, placeholder, className, buttonClass, dropdownPosition = 'top' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = React.useRef(null);
    const btnRef = React.useRef(null);
    const dropStyle = useFixedDropdown(isOpen, btnRef);

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedOption = options.find(o => o.value === value);

    return (
        <div className={`relative min-w-0 ${className}`} ref={ref}>
            <button
                ref={btnRef}
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center justify-between w-full text-left 
                    bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm 
                    border border-gray-200/60 dark:border-white/10 
                    hover:border-orange-500/50 dark:hover:border-orange-400/50
                    rounded-lg px-2.5 py-2 text-xs 
                    text-gray-700 dark:text-gray-200 font-medium 
                    focus:outline-none focus:ring-2 focus:ring-orange-500/20 
                    transition-all shadow-sm group ${buttonClass}`}
            >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    {selectedOption?.icon && !selectedOption.labelNode && <span className="opacity-70 group-hover:opacity-100 transition-opacity flex-shrink-0">{selectedOption.icon}</span>}
                    <span className="block">
                        {selectedOption ? (selectedOption.labelNode || selectedOption.label) : <span className="text-gray-400 font-normal">{placeholder}</span>}
                    </span>
                </div>
                <ChevronDown size={14} className={`text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && (
                <div style={dropStyle} className={`min-w-[180px] w-max max-w-[90vw] md:max-w-[400px] 
                    bg-white/95 dark:bg-[#0f172a]/95 backdrop-blur-xl 
                    border border-gray-200 dark:border-white/10 
                    rounded-xl shadow-2xl max-h-64 overflow-y-auto 
                    animate-in fade-in zoom-in-95 duration-100 
                    ring-1 ring-black/5 dark:ring-white/5`}>
                    <div className="p-1 space-y-0.5">
                        {options.map((opt) => (
                            <button
                                key={opt.value}
                                onClick={() => { onChange(opt.value); setIsOpen(false); }}
                                className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 rounded-lg transition-all
                                    ${opt.value === value
                                        ? 'bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 font-semibold'
                                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white'
                                    }`}
                            >
                                {opt.icon && !opt.labelNode && <span className={`${opt.value === value ? 'text-orange-500' : 'text-gray-400'} flex-shrink-0 transition-colors`}>{opt.icon}</span>}
                                <span className="flex-1 truncate">{opt.labelNode || opt.label}</span>
                                {opt.value === value && <Check size={14} className="text-orange-500 flex-shrink-0" />}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Custom Combobox Component ───────────────────────────
const Combobox = ({ value, onChange, options, placeholder, className, loading }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState(value || '');
    const ref = React.useRef(null);
    const inputRef = React.useRef(null);

    React.useEffect(() => {
        setSearchTerm(value || '');
    }, [value]);

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                setIsOpen(false);
                // On close, ensure search term matches value if selected from options, 
                // or keep as custom value if allowed
                if (value) setSearchTerm(value);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [value]);

    const isSearching = searchTerm !== value;
    const filteredOptions = isSearching
        ? options.filter(opt => opt.toString().toLowerCase().includes(searchTerm.toLowerCase()))
        : options;

    const handleSelect = (val) => {
        onChange(val);
        setSearchTerm(val);
        setIsOpen(false);
    };

    const handleInputFocus = () => {
        setIsOpen(true);
    };

    const handleInputChange = (e) => {
        const newVal = e.target.value;
        setSearchTerm(newVal);
        onChange(newVal); // Allow free text
        setIsOpen(true);
    };

    return (
        <div className={`relative min-w-0 ${className}`} ref={ref}>
            <div className="relative">
                <input
                    ref={inputRef}
                    type="text"
                    value={searchTerm}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    placeholder={loading ? "Loading..." : placeholder}
                    className={`w-full text-xs bg-white dark:bg-slate-800 border 
                        ${isOpen ? 'border-orange-500 ring-1 ring-orange-500/20' : 'border-gray-200 dark:border-white/10'} 
                        rounded-lg px-2.5 py-2 text-gray-900 dark:text-white 
                        placeholder-gray-400 outline-none transition-all shadow-sm`}
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 pointer-events-none text-gray-400">
                    {loading && <Loader2 size={12} className="animate-spin" />}
                    {!loading && <ChevronDown size={14} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />}
                </div>
            </div>

            {isOpen && filteredOptions.length > 0 && (
                <div className="absolute top-full left-0 w-full mt-1.5 
                    bg-white/95 dark:bg-[#0f172a]/95 backdrop-blur-xl 
                    border border-gray-200 dark:border-white/10 
                    rounded-xl shadow-2xl z-50 max-h-64 overflow-y-auto 
                    animate-in fade-in zoom-in-95 duration-100 origin-top-left
                    ring-1 ring-black/5 dark:ring-white/5">
                    <div className="p-1 space-y-0.5">
                        {filteredOptions.map((opt, i) => (
                            <button
                                key={i}
                                onClick={() => handleSelect(opt)}
                                className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 rounded-lg transition-all
                                    ${opt === value
                                        ? 'bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 font-semibold'
                                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white'
                                    }`}
                            >
                                <span className="flex-1 truncate block">{opt}</span>
                                {opt === value && <Check size={14} className="text-orange-500 flex-shrink-0" />}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// Helper for Icons
const getIconForType = (type) => {
    switch (getColumnCategory(type)) {
        case 'number': return <Hash size={12} className="text-blue-500" />;
        case 'text': return <TypeIcon size={12} className="text-orange-500" />;
        case 'date': return <Calendar size={12} className="text-purple-500" />;
        case 'boolean': return <CheckSquare size={12} className="text-green-500" />;
        default: return <Circle size={12} className="text-gray-400" />;
    }
};

const getIconForAgg = (fn) => {
    switch (fn) {
        case 'COUNT': return <LayoutList size={12} className="text-purple-500" />;
        case 'COUNT_DISTINCT': return <Target size={12} className="text-indigo-500" />;
        case 'AVG_PER_USER': return <Activity size={12} className="text-orange-500" />;
        case 'SUM': return <Sigma size={12} className="text-blue-500" />;
        case 'AVG': return <Activity size={12} className="text-teal-500" />;
        case 'MIN': return <ArrowDown size={12} className="text-red-500" />;
        case 'MAX': return <ArrowUp size={12} className="text-green-500" />;
        case 'CUSTOM': return <Settings2 size={12} className="text-amber-500" />;
        default: return <Circle size={12} className="text-gray-400" />;
    }
};

// ─── Filter Row ─────────────────────────────────────────
const FilterRow = ({ filter, index, columns, onUpdate, onRemove, onFetchValues, join }) => {
    const [open, setOpen] = useState(!filter.column);
    const [step, setStep] = useState(filter.column ? 'detail' : 'col');
    const ref = React.useRef(null);
    const chipRef = React.useRef(null);
    const dropStyle = useFixedDropdown(open, chipRef);

    const col = columns.find(c => c.name === filter.column);
    const category = col ? getColumnCategory(col.type) : 'text';
    const operators = FILTER_OPERATORS[category] || FILTER_OPERATORS.text;
    const [suggestions, setSuggestions] = useState([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);

    React.useEffect(() => {
        if (category === 'text' && filter.column) {
            setLoadingSuggestions(true);
            onFetchValues(filter.column).then(values => { setSuggestions(values); setLoadingSuggestions(false); });
        } else setSuggestions([]);
    }, [filter.column, category, onFetchValues]);

    React.useEffect(() => {
        const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const opLabel = operators.find(o => o.value === filter.operator)?.label || filter.operator;
    const valDisplay = filter.operator === 'IS NOT NULL' ? '' :
        filter.operator === 'BETWEEN' ? `${filter.value?.split('|')[0] || '?'} - ${filter.value?.split('|')[1] || '?'}` :
            filter.operator === 'IN' ? (filter.value || '?') :
                filter.value || '?';
    const chipLabel = filter.column
        ? `${filter.column} ${opLabel}${filter.operator !== 'IS NOT NULL' ? ` ${valDisplay}` : ''}`
        : 'Choose column…';

    const needsValue = filter.operator && filter.operator !== 'IS NOT NULL';

    const inputClass = 'w-full text-xs bg-white/50 dark:bg-slate-800/50 border border-gray-200/60 dark:border-white/10 rounded-lg px-2.5 py-2 text-gray-700 dark:text-gray-200 placeholder-gray-400 outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500/50 transition-all shadow-sm';
    const miniLabel = 'text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider block mb-1';

    const renderValueInput = () => {
        if (!needsValue) return null;

        // BETWEEN - date: From / To stacked
        if (filter.operator === 'BETWEEN' && category === 'date') return (
            <div className="space-y-2">
                <div>
                    <label className={miniLabel}>From</label>
                    <input type="date" value={filter.value?.split('|')[0] || ''}
                        onChange={e => { const [, end] = (filter.value || '|').split('|'); onUpdate(index, { ...filter, value: `${e.target.value}|${end || ''}` }); }}
                        className={inputClass} />
                </div>
                <div>
                    <label className={miniLabel}>To</label>
                    <input type="date" value={filter.value?.split('|')[1] || ''}
                        onChange={e => { const [start] = (filter.value || '|').split('|'); onUpdate(index, { ...filter, value: `${start || ''}|${e.target.value}` }); }}
                        className={inputClass} />
                </div>
            </div>
        );

        // BETWEEN - number: Min / Max stacked
        if (filter.operator === 'BETWEEN' && category === 'number') return (
            <div className="space-y-2">
                <div>
                    <label className={miniLabel}>Min</label>
                    <input type="number" value={filter.value?.split('|')[0] || ''} placeholder="0"
                        onChange={e => { const [, end] = (filter.value || '|').split('|'); onUpdate(index, { ...filter, value: `${e.target.value}|${end || ''}` }); }}
                        className={inputClass} />
                </div>
                <div>
                    <label className={miniLabel}>Max</label>
                    <input type="number" value={filter.value?.split('|')[1] || ''} placeholder="100"
                        onChange={e => { const [start] = (filter.value || '|').split('|'); onUpdate(index, { ...filter, value: `${start || ''}|${e.target.value}` }); }}
                        className={inputClass} />
                </div>
            </div>
        );

        // RELATIVE - date: Custom Picker
        if (filter.operator === 'RELATIVE') {
            const val = filter.value || 'last_7_days';
            const lastMatch = val.match(/^last_(\d+)_([a-zA-Z]+)$/i);
            const thisMatch = val.match(/^this_([a-zA-Z]+)$/i);
            let type = 'last', num = 7, unit = 'days';

            if (lastMatch) {
                type = 'last';
                num = parseInt(lastMatch[1], 10) || 1;
                unit = lastMatch[2].toLowerCase();
            } else if (thisMatch) {
                type = 'this';
                unit = thisMatch[1].toLowerCase();
            } else if (val === 'last_month') {
                type = 'last'; num = 1; unit = 'months';
            } else if (val === 'last_year') {
                type = 'last'; num = 1; unit = 'years';
            }

            // Ensure unit has an 's' on the end for the UI select value
            if (!unit.endsWith('s')) unit += 's';

            const updateRelative = (newType, newNum, newUnit) => {
                let u = newUnit.replace(/s$/, ''); // singularize before saving (day, week, month, year)
                if (newType === 'this') {
                    onUpdate(index, { ...filter, value: `this_${u}` });
                } else {
                    onUpdate(index, { ...filter, value: `last_${newNum}_${u}s` });
                }
            };

            return (
                <div className="space-y-2">
                    {/* Pre-defined Options Grid */}
                    <div className="grid grid-cols-2 gap-1">
                        {RELATIVE_DATE_OPTIONS.map(o => (
                            <button key={o.value} onClick={() => onUpdate(index, { ...filter, value: o.value })}
                                className={`px-2 py-1.5 text-[11px] rounded-lg border font-medium transition-all text-left leading-tight ${filter.value === o.value
                                    ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                                    : 'border-gray-200/60 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:border-amber-400/50 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-500/10'
                                    }`}>
                                {o.label}
                            </button>
                        ))}
                    </div>

                    {/* Custom Picker */}
                    <div className="flex flex-col gap-1">
                        <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Custom</span>
                        <div className="flex items-center w-full bg-white dark:bg-slate-800 border border-gray-200/60 dark:border-white/10 rounded-lg overflow-hidden focus-within:ring-1 focus-within:ring-amber-500/50 shadow-sm transition-all duration-200 h-8">
                            <select
                                value={type}
                                onChange={e => updateRelative(e.target.value, num, unit)}
                                className="bg-transparent h-full text-xs px-2 text-gray-700 dark:text-gray-200 font-medium outline-none cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5 border-r border-gray-200/60 dark:border-white/5"
                            >
                                <option value="last">Last</option>
                                <option value="this">This</option>
                            </select>

                            {type === 'last' && (
                                <input
                                    type="number" min="1" max="999"
                                    value={num}
                                    onChange={e => updateRelative(type, Math.max(1, parseInt(e.target.value) || 1), unit)}
                                    className="bg-transparent h-full w-12 text-center text-xs focus:outline-none text-gray-900 dark:text-gray-100 font-mono border-r border-gray-200/60 dark:border-white/5 placeholder-gray-400"
                                />
                            )}

                            <select
                                value={unit}
                                onChange={e => updateRelative(type, num, e.target.value)}
                                className="bg-transparent h-full flex-1 text-xs px-2 text-gray-700 dark:text-gray-200 font-medium outline-none cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5"
                            >
                                <option value="days">Days</option>
                                <option value="weeks">Weeks</option>
                                <option value="months">Months</option>
                                <option value="years">Years</option>
                            </select>
                        </div>
                    </div>
                </div>
            );
        }

        // date: single date picker
        if (category === 'date') return (
            <input type="date" value={filter.value || ''} onChange={e => onUpdate(index, { ...filter, value: e.target.value })} className={inputClass} />
        );

        // text LIKE (contains): free text
        if (category === 'text' && filter.operator === 'LIKE') return (
            <input type="text" value={filter.value || ''} placeholder="e.g. premium" onChange={e => onUpdate(index, { ...filter, value: e.target.value })} className={inputClass} />
        );

        // text IN: checkable list from suggestions
        if (category === 'text' && filter.operator === 'IN') {
            const selected = filter.value ? filter.value.split(',').map(v => v.trim()).filter(Boolean) : [];
            const toggle = (val) => {
                const next = selected.includes(val)
                    ? selected.filter(v => v !== val)
                    : [...selected, val];
                onUpdate(index, { ...filter, value: next.join(', ') });
            };
            return (
                <div className="max-h-44 overflow-y-auto space-y-0.5 border border-gray-200/60 dark:border-white/10 rounded-lg p-1">
                    {loadingSuggestions && <p className="text-[11px] text-gray-400 italic px-2 py-1">Loading…</p>}
                    {!loadingSuggestions && suggestions.length === 0 && <p className="text-[11px] text-gray-400 italic px-2 py-1">No values found</p>}
                    {suggestions.map(opt => {
                        const val = typeof opt === 'string' ? opt : opt.value;
                        const isChecked = selected.includes(val);
                        return (
                            <button key={val} onClick={() => toggle(val)}
                                className={`w-full text-left px-2.5 py-1.5 text-xs rounded-md flex items-center gap-2 transition-all font-mono ${isChecked
                                    ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 font-semibold'
                                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5'
                                    }`}>
                                <span className={`w-3.5 h-3.5 flex-shrink-0 rounded border flex items-center justify-center transition-colors ${isChecked ? 'bg-amber-500 border-amber-500' : 'border-gray-300 dark:border-white/20'
                                    }`}>
                                    {isChecked && <Check size={9} className="text-white" />}
                                </span>
                                {val}
                            </button>
                        );
                    })}
                </div>
            );
        }

        // text = / !=: Combobox with suggestions
        if (category === 'text') return (
            <Combobox value={filter.value} onChange={val => onUpdate(index, { ...filter, value: val })}
                options={suggestions} placeholder="Choose or type a value…" loading={loadingSuggestions} className="w-full" />
        );

        // number: numeric input
        return (
            <input type="number" value={filter.value || ''} placeholder="Enter a number…" onChange={e => onUpdate(index, { ...filter, value: e.target.value })} className={inputClass} />
        );
    };

    // Operator selector state (mirrors CustomSelect pattern)
    const [opOpen, setOpOpen] = useState(false);
    const opRef = React.useRef(null);
    const opBtnRef = React.useRef(null);
    const opDropStyle = useFixedDropdown(opOpen, opBtnRef);
    React.useEffect(() => {
        const handler = e => { if (opRef.current && !opRef.current.contains(e.target)) setOpOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div ref={ref} className="relative">
            {/* Chip */}
            <div
                ref={chipRef}
                onClick={() => setOpen(o => !o)}
                className="flex items-center gap-1 bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30 rounded-lg pl-2 pr-1 py-1 cursor-pointer hover:border-amber-400 transition-colors"
            >
                <Filter size={10} className="flex-shrink-0 opacity-60" />
                <span className="text-[11px] font-semibold whitespace-nowrap font-mono">{chipLabel}</span>
                <ChevronDown size={11} className={`ml-0.5 flex-shrink-0 opacity-50 transition-transform ${open ? 'rotate-180' : ''}`} />
                <button
                    onClick={e => { e.stopPropagation(); onRemove(index); }}
                    className="ml-0.5 text-amber-300 hover:text-red-500 transition-colors flex-shrink-0 p-0.5 rounded hover:bg-amber-100 dark:hover:bg-amber-500/20"
                >
                    <X size={10} />
                </button>
            </div>

            {/* Popover */}
            {open && (
                <div style={dropStyle} className="bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-white/10 rounded-xl shadow-2xl ring-1 ring-black/5 dark:ring-white/5 w-[280px] animate-in fade-in zoom-in-95 duration-100">

                    {/* ── Column picker ── */}
                    {step === 'col' && (<>
                        <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-100 dark:border-white/10">
                            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Choose column</span>
                            <button onClick={() => setOpen(false)} className="text-gray-300 hover:text-gray-500 dark:hover:text-gray-200"><X size={12} /></button>
                        </div>
                        <div className="p-1.5 space-y-0.5 max-h-56 overflow-y-auto">
                            {columns.map(c => {
                                const newCat = getColumnCategory(c.type);
                                const newOps = FILTER_OPERATORS[newCat] || FILTER_OPERATORS.text;
                                return (
                                    <button key={c.name}
                                        onClick={() => {
                                            onUpdate(index, { ...filter, column: c.name, operator: newOps[0]?.value || '=', value: '' });
                                            setStep('detail');
                                        }}
                                        className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 rounded-lg transition-all font-mono ${filter.column === c.name
                                            ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 font-semibold'
                                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5'
                                            }`}
                                    >
                                        <span className="flex-shrink-0 opacity-70">{getIconForType(c.type)}</span>
                                        {join ? <><span className="text-amber-500 dark:text-amber-400 text-[10px] font-bold uppercase">{c.source}</span><span className="text-gray-400 mx-px">.</span></> : null}
                                        <span className="flex-1 truncate">{c.name}</span>
                                        {filter.column === c.name && <Check size={13} className="text-amber-500 flex-shrink-0" />}
                                    </button>
                                );
                            })}
                        </div>
                    </>)}

                    {/* ── Detail: operator dropdown + adaptive value ── */}
                    {step === 'detail' && (<>
                        {/* Header */}
                        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100 dark:border-white/10">
                            <button onClick={() => setStep('col')} className="text-gray-400 hover:text-amber-500 transition-colors flex-shrink-0">
                                <ChevronLeft size={13} />
                            </button>
                            <span className="flex items-center gap-1.5 flex-1 min-w-0">
                                <span className="flex-shrink-0 opacity-60">{getIconForType(col?.type)}</span>
                                <span className="text-xs font-bold text-gray-800 dark:text-gray-100 font-mono truncate">{filter.column}</span>
                            </span>
                            <button onClick={() => setOpen(false)} className="text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 flex-shrink-0"><X size={12} /></button>
                        </div>

                        <div className="p-3 space-y-3.5">
                            {/* Operator - custom select matching app style */}
                            <div>
                                <label className={miniLabel}>Condition</label>
                                <div ref={opRef} className="relative">
                                    <button
                                        ref={opBtnRef}
                                        onClick={() => setOpOpen(o => !o)}
                                        className="flex items-center justify-between w-full text-left bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm border border-gray-200/60 dark:border-white/10 hover:border-amber-500/50 dark:hover:border-amber-400/50 rounded-lg px-2.5 py-2 text-xs text-gray-700 dark:text-gray-200 font-medium focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all shadow-sm group"
                                    >
                                        <span>{operators.find(o => o.value === filter.operator)?.label || filter.operator}</span>
                                        <ChevronDown size={14} className={`text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-transform duration-200 ${opOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    {opOpen && (
                                        <div style={opDropStyle} className="w-[260px] bg-white/95 dark:bg-[#0f172a]/95 backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-xl shadow-2xl ring-1 ring-black/5 dark:ring-white/5 animate-in fade-in zoom-in-95 duration-100">
                                            <div className="p-1 space-y-0.5">
                                                {operators.map(op => (
                                                    <button key={op.value}
                                                        onClick={() => { onUpdate(index, { ...filter, operator: op.value, value: '' }); setOpOpen(false); }}
                                                        className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between rounded-lg transition-all ${filter.operator === op.value
                                                            ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 font-semibold'
                                                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5'
                                                            }`}
                                                    >
                                                        {op.label}
                                                        {filter.operator === op.value && <Check size={13} className="text-amber-500 flex-shrink-0" />}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Adaptive value */}
                            {needsValue && (
                                <div>
                                    <label className={miniLabel}>
                                        {filter.operator === 'BETWEEN' ? 'Range' : filter.operator === 'RELATIVE' ? 'Period' : 'Value'}
                                    </label>
                                    {renderValueInput()}
                                </div>
                            )}

                            {/* IS NOT NULL confirmation */}
                            {!needsValue && (
                                <p className="text-[11px] text-gray-400 dark:text-gray-500 italic text-center py-1">No value needed for this condition</p>
                            )}

                            {/* Apply */}
                            <button
                                onClick={() => setOpen(false)}
                                className="w-full text-[11px] font-bold text-white bg-amber-500 hover:bg-amber-600 active:bg-amber-700 rounded-lg py-2 transition-colors shadow-sm tracking-wide"
                            >
                                Apply filter
                            </button>
                        </div>
                    </>)}
                </div>
            )}
        </div>
    );
};

// ─── Metric Row ──────────────────────────────────────────
const MetricRow = ({ metric, index, columns, onUpdate, onRemove, join, canRemove }) => {
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState('agg'); // 'agg' | 'col' | 'custom'
    const ref = React.useRef(null);
    const chipRef = React.useRef(null);
    const dropStyle = useFixedDropdown(open, chipRef);

    // Auto-open after mount when this is a fresh (unconfigured) metric.
    // Must be deferred so chipRef is populated before useFixedDropdown reads its rect.
    React.useEffect(() => {
        if (!metric.fn) setOpen(true);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const isCustom = metric.fn === 'CUSTOM';
    const aggInfo = metric.fn ? AGGREGATION_FUNCTIONS.find(f => f.value === metric.fn) : null;
    const aggLabel = aggInfo?.label || metric.fn || '';
    const colLabel = (!metric.column || metric.column === '*') ? 'all' : metric.column;

    // Close on outside click
    React.useEffect(() => {
        const handler = e => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setStep('agg'); } };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const openDropdown = () => { setStep('agg'); setOpen(true); };

    const pickAgg = (fn) => {
        onUpdate(index, {
            fn: fn.value,
            column: fn.value !== 'CUSTOM' ? (metric.column || '*') : undefined,
            expression: fn.value === 'CUSTOM' ? (metric.expression || '') : undefined,
            alias: fn.value === 'CUSTOM' ? (metric.alias || '') : undefined,
        });
        if (fn.value === 'CUSTOM') {
            // Go to dedicated custom step
            setStep('custom');
        } else {
            setStep('col');
        }
    };

    const pickCol = (val) => {
        onUpdate(index, { ...metric, column: val });
        setOpen(false);
        setStep('agg');
    };

    // ── Chip label ──────────────────────────────────────────
    const chipLabel = !metric.fn
        ? 'Choose metric…'
        : isCustom
            ? (metric.alias || metric.expression || 'Custom expression')
            : `${aggLabel} of ${colLabel}`;

    const variants = {
        enter: (direction) => ({
            x: direction > 0 ? 20 : -20,
            opacity: 0,
        }),
        center: {
            x: 0,
            opacity: 1,
        },
        exit: (direction) => ({
            x: direction < 0 ? 20 : -20,
            opacity: 0,
        }),
    };

    return (
        <div ref={ref} className="relative">
            {/* Chip */}
            <div className="flex items-center gap-1 bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30 rounded-lg pl-2 pr-1 py-1 cursor-pointer hover:border-amber-400 transition-colors"
                ref={chipRef}
                onClick={openDropdown}>
                {metric.fn && <span className="flex-shrink-0 opacity-60">{getIconForAgg(metric.fn)}</span>}
                <span className="text-[11px] font-semibold whitespace-nowrap">{chipLabel}</span>
                <ChevronDown size={11} className={`ml-0.5 flex-shrink-0 opacity-50 transition-transform ${open ? 'rotate-180' : ''}`} />
                {canRemove && (
                    <button
                        onClick={e => { e.stopPropagation(); onRemove(index); }}
                        className="ml-0.5 text-amber-300 hover:text-red-500 transition-colors flex-shrink-0 p-0.5 rounded hover:bg-amber-100 dark:hover:bg-amber-500/20"
                    >
                        <X size={10} />
                    </button>
                )}
            </div>

            {/* Single dropdown that transitions between steps */}
            {open && (
                <div style={dropStyle} className="bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-white/10 rounded-xl shadow-2xl ring-1 ring-black/5 dark:ring-white/5 min-w-[200px] max-w-[260px] animate-in fade-in zoom-in-95 duration-100 overflow-hidden flex flex-col">
                    {/* Header */}
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 dark:border-white/10 z-10 relative bg-inherit">
                        {(step === 'col' || step === 'custom') && (
                            <button onClick={() => setStep('agg')} className="text-gray-400 hover:text-amber-500 transition-colors flex-shrink-0">
                                <ChevronLeft size={13} />
                            </button>
                        )}
                        <span className="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider flex-1">
                            {step === 'agg' ? 'Choose Aggregation' : step === 'custom' ? 'Custom Formula' : `${aggLabel} of…`}
                        </span>
                        <button onClick={() => { setOpen(false); setStep('agg'); }} className="text-gray-300 hover:text-gray-500 dark:hover:text-gray-200">
                            <X size={12} />
                        </button>
                    </div>

                    <div className="relative">
                        <AnimatePresence initial={false} mode="wait" custom={step === 'col' ? 1 : -1}>
                            {step === 'agg' ? (
                                <motion.div
                                    key="agg"
                                    custom={-1}
                                    variants={variants}
                                    initial="enter"
                                    animate="center"
                                    exit="exit"
                                    transition={{ duration: 0.15 }}
                                    className="p-1.5 space-y-0.5"
                                >
                                    {AGGREGATION_FUNCTIONS.map(fn => (
                                        <button
                                            key={fn.value}
                                            onClick={() => pickAgg(fn)}
                                            className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 rounded-lg transition-all
                                                ${metric.fn === fn.value
                                                    ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 font-semibold'
                                                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5'}`}
                                        >
                                            <span className="flex-shrink-0">{getIconForAgg(fn.value)}</span>
                                            {fn.label}
                                            {metric.fn === fn.value && <Check size={13} className="ml-auto text-amber-500 flex-shrink-0" />}
                                        </button>
                                    ))}
                                    {/* Custom inputs moved to their own step */}
                                </motion.div>
                            ) : step === 'custom' ? (
                                <motion.div
                                    key="custom"
                                    custom={1}
                                    variants={variants}
                                    initial="enter"
                                    animate="center"
                                    exit="exit"
                                    transition={{ duration: 0.15 }}
                                    className="p-2 space-y-2"
                                >
                                    <div className="space-y-1">
                                        <label className="text-[9px] font-bold text-gray-400 uppercase">Formula</label>
                                        <textarea
                                            value={metric.expression || ''}
                                            onChange={e => onUpdate(index, { ...metric, expression: e.target.value })}
                                            placeholder="e.g. COUNT(id) / COUNT(DISTINCT user_id)"
                                            autoFocus
                                            className="w-full text-xs bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-white/10 rounded-lg px-2 py-2 font-mono text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-amber-500/20 outline-none resize-none h-16 leading-relaxed"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[9px] font-bold text-gray-400 uppercase">Alias (Optional)</label>
                                        <input
                                            type="text"
                                            value={metric.alias || ''}
                                            onChange={e => onUpdate(index, { ...metric, alias: e.target.value })}
                                            placeholder="e.g. engagement_score"
                                            className="w-full text-xs bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-white/10 rounded-lg px-2 py-1.5 font-mono text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-amber-500/20 outline-none"
                                        />
                                    </div>
                                    <button
                                        onClick={() => setOpen(false)}
                                        className="w-full mt-1 text-[10px] font-bold text-center text-amber-600 bg-amber-50 hover:bg-amber-100 dark:bg-amber-500/10 dark:hover:bg-amber-500/20 py-1.5 rounded-lg transition-colors"
                                    >
                                        Done
                                    </button>
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="col"
                                    custom={1}
                                    variants={variants}
                                    initial="enter"
                                    animate="center"
                                    exit="exit"
                                    transition={{ duration: 0.15 }}
                                    className="p-1.5 space-y-0.5 max-h-52 overflow-y-auto"
                                >
                                    {[{ value: '*', label: '* (all rows)', icon: <Hash size={12} className="text-gray-400" /> },
                                    ...columns.map(c => ({
                                        value: c.name,
                                        label: join ? `${c.source}.${c.name}` : c.name,
                                        icon: getIconForType(c.type),
                                    }))
                                    ].map(opt => (
                                        <button
                                            key={opt.value}
                                            onClick={() => pickCol(opt.value)}
                                            className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 rounded-lg transition-all font-mono
                                                ${metric.column === opt.value
                                                    ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 font-semibold'
                                                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5'}`}
                                        >
                                            <span className="flex-shrink-0">{opt.icon}</span>
                                            {opt.label}
                                            {metric.column === opt.value && <Check size={13} className="ml-auto text-amber-500 flex-shrink-0" />}
                                        </button>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            )
            }
        </div >
    );
};

// ─── Join Table Picker (left selector: table → join type) ────────────────────
const JoinTablePicker = ({ join, tables, joinTypes, onChangeTable, onChangeType, mainTable }) => {
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState('table');
    const ref = React.useRef(null);
    React.useEffect(() => {
        const h = e => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setStep('table'); } };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);

    const shortType = { 'JOIN': 'Inner', 'LEFT JOIN': 'Left', 'FULL OUTER JOIN': 'Full' }[join?.type] || join?.type || '';
    const joinedTable = tables.find(t => t.value === join?.table);
    const chipLabel = joinedTable ? `${joinedTable.label} · ${shortType}` : 'Choose table';

    const btnRef = React.useRef(null);
    const dropStyle = useFixedDropdown(open, btnRef);

    return (
        <div ref={ref} className="relative flex-1 min-w-0">
            <button
                ref={btnRef}
                onClick={() => { setStep('table'); setOpen(o => !o); }}
                className="flex items-center justify-between w-full text-left bg-white/50 dark:bg-slate-800/50 border border-gray-200/60 dark:border-white/10 hover:border-amber-500/50 rounded-lg px-2.5 py-2 text-xs text-gray-700 dark:text-gray-200 font-medium focus:outline-none transition-all shadow-sm"
            >
                <span className="truncate">{chipLabel}</span>
                <ChevronDown size={13} className={`ml-1 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div style={dropStyle} className="min-w-[180px] bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-white/10 rounded-xl shadow-2xl ring-1 ring-black/5 dark:ring-white/5 animate-in fade-in zoom-in-95 duration-100">
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 dark:border-white/10">
                        {step === 'type' && (
                            <button onClick={() => setStep('table')} className="text-gray-400 hover:text-amber-500 flex-shrink-0"><ChevronLeft size={13} /></button>
                        )}
                        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider flex-1">
                            {step === 'table' ? 'Join table' : 'Join type'}
                        </span>
                        <button onClick={() => { setOpen(false); setStep('table'); }} className="text-gray-300 hover:text-gray-500 dark:hover:text-gray-200"><X size={12} /></button>
                    </div>
                    {step === 'table' && (
                        <div className="p-1.5 space-y-0.5">
                            {tables.filter(t => t.value !== mainTable).map(t => (
                                <button key={t.value}
                                    onClick={() => { onChangeTable(t.value); setStep('type'); }}
                                    className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between rounded-lg transition-all ${join?.table === t.value
                                        ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 font-semibold'
                                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5'
                                        }`}>
                                    {t.label}
                                    {join?.table === t.value && <Check size={13} className="text-amber-500" />}
                                </button>
                            ))}
                        </div>
                    )}
                    {step === 'type' && (
                        <div className="p-1.5 space-y-0.5">
                            {joinTypes.map(jt => (
                                <button key={jt.value}
                                    onClick={() => { onChangeType(jt.value); setOpen(false); setStep('table'); }}
                                    className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 rounded-lg transition-all ${join?.type === jt.value
                                        ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 font-semibold'
                                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5'
                                        }`}>
                                    <div className="flex-1">
                                        <div>{jt.label}</div>
                                        <div className="text-[9px] text-gray-400 -mt-0.5 font-normal">
                                            {jt.value === 'JOIN' ? 'Matching rows only' : jt.value === 'LEFT JOIN' ? 'All left + matching right' : 'All rows from both'}
                                        </div>
                                    </div>
                                    {join?.type === jt.value && <Check size={13} className="text-amber-500 flex-shrink-0" />}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ─── Join Column Picker (right selector: left col + right col) ───────────────
const JoinColumnPicker = ({ join, leftCols, rightCols, onChangeLeftCol, onChangeRightCol, mainTableLabel, joinTableLabel }) => {
    const [open, setOpen] = useState(false);
    const ref = React.useRef(null);
    const btnRef = React.useRef(null);
    const dropStyle = useFixedDropdown(open, btnRef);
    React.useEffect(() => {
        const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);

    const chipLabel = `${join?.leftCol || '…'} = ${join?.rightCol || '…'}`;

    return (
        <div ref={ref} className="relative flex-1 min-w-0">
            <button
                ref={btnRef}
                onClick={() => setOpen(o => !o)}
                className="flex items-center justify-between w-full text-left bg-white/50 dark:bg-slate-800/50 border border-gray-200/60 dark:border-white/10 hover:border-amber-500/50 rounded-lg px-2.5 py-2 text-xs text-gray-700 dark:text-gray-200 font-mono font-medium focus:outline-none transition-all shadow-sm"
            >
                <span className="truncate">{chipLabel}</span>
                <ChevronDown size={13} className={`ml-1 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div style={{ ...dropStyle, minWidth: '320px' }} className="bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-white/10 rounded-xl shadow-2xl ring-1 ring-black/5 dark:ring-white/5 animate-in fade-in zoom-in-95 duration-100">
                    <div className="px-3 py-2 border-b border-gray-100 dark:border-white/10">
                        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Match columns</span>
                    </div>
                    <div className="flex">
                        {/* Left table cols */}
                        <div className="flex-1 p-1.5 border-r border-gray-100 dark:border-white/10">
                            <p className="text-[9px] font-bold text-amber-500 uppercase tracking-wider px-2 pb-1">{mainTableLabel}</p>
                            <div className="space-y-0.5 max-h-48 overflow-y-auto">
                                {leftCols.map(c => (
                                    <button key={c.value} onClick={() => onChangeLeftCol(c.value)}
                                        className={`w-full text-left px-2 py-1.5 text-xs flex items-center justify-between rounded-lg transition-all font-mono ${join?.leftCol === c.value
                                            ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 font-semibold'
                                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5'
                                            }`}>
                                        {c.label}
                                        {join?.leftCol === c.value && <Check size={11} className="text-amber-500 flex-shrink-0 ml-1" />}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {/* = divider */}
                        <div className="flex items-center px-1.5">
                            <span className="text-[10px] font-bold text-gray-300 dark:text-gray-600">=</span>
                        </div>
                        {/* Right table cols */}
                        <div className="flex-1 p-1.5">
                            <p className="text-[9px] font-bold text-amber-500 uppercase tracking-wider px-2 pb-1">{joinTableLabel}</p>
                            <div className="space-y-0.5 max-h-48 overflow-y-auto">
                                {rightCols.map(c => (
                                    <button key={c.value} onClick={() => onChangeRightCol(c.value)}
                                        className={`w-full text-left px-2 py-1.5 text-xs flex items-center justify-between rounded-lg transition-all font-mono ${join?.rightCol === c.value
                                            ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 font-semibold'
                                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5'
                                            }`}>
                                        {c.label}
                                        {join?.rightCol === c.value && <Check size={11} className="text-amber-500 flex-shrink-0 ml-1" />}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Group By Picker ──────────────────────────────────────
const GroupByPicker = ({ columns, groupBy, onToggle, join, getColumnCategory }) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = React.useRef(null);
    const btnRef = React.useRef(null);
    const dropStyle = useFixedDropdown(isOpen, btnRef);

    React.useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const available = columns.filter(c =>
        !groupBy.some(g => g.column === c.name)
    );

    if (available.length === 0 && groupBy.length === 0) {
        return <p className="text-[11px] text-gray-400 dark:text-gray-500 italic">No groupable columns.</p>;
    }
    if (available.length === 0) return null;

    return (
        <div className="relative" ref={ref}>
            <button
                ref={btnRef}
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-1 text-[11px] text-amber-500 hover:text-amber-600 font-semibold px-2 py-1 border border-dashed border-amber-200 dark:border-amber-500/30 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-500/10 transition-colors"
            >
                <Plus size={10} /> {groupBy.length === 0 ? 'Add Column to Group By' : 'Add'}
            </button>
            {isOpen && (
                <div style={dropStyle} className="min-w-[180px] bg-white/95 dark:bg-[#0f172a]/95 backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-xl shadow-2xl max-h-64 overflow-y-auto animate-in fade-in zoom-in-95 duration-100 ring-1 ring-black/5 dark:ring-white/5">
                    <div className="p-1 space-y-0.5">
                        {available.map(c => (
                            <button
                                key={c.name}
                                onClick={() => { onToggle(c.name); setIsOpen(false); }}
                                className="w-full text-left px-3 py-2 text-xs flex items-center gap-1.5 rounded-lg transition-all text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white font-mono"
                            >
                                <span className="flex-shrink-0">{getIconForType(c.type)}</span>
                                {join && c.source && (
                                    <span className="text-indigo-500 dark:text-indigo-400 text-[10px] font-bold uppercase tracking-wide not-italic">{c.source}.</span>
                                )}
                                {c.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Results Table ──────────────────────────────────────
const ResultsTable = ({ rows, executionTime }) => {
    if (!rows || rows.length === 0) return null;
    const columns = Object.keys(rows[0]);
    const displayRows = rows.slice(0, 100);
    return (
        <div className="overflow-auto max-h-[400px] border border-gray-200 dark:border-white/10 rounded-lg">
            <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-slate-800 sticky top-0 z-10">
                    <tr>
                        <th className="px-3 py-2 text-left text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-white/10">#</th>
                        {columns.map(col => {
                            const parts = col.split('.');
                            const hasPrefix = parts.length > 1;
                            const prefix = hasPrefix ? parts[0] : '';
                            const name = hasPrefix ? parts[1] : col;

                            return (
                                <th key={col} className="px-3 py-2 text-left text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-white/10 whitespace-nowrap">
                                    {hasPrefix ? (
                                        <>
                                            <span className="text-indigo-500 dark:text-indigo-400">{prefix}</span>
                                            <span className="text-gray-300 dark:text-gray-600 mx-px">.</span>
                                            {name}
                                        </>
                                    ) : col}
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-white/5">
                    {displayRows.map((row, i) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-white dark:bg-slate-900' : 'bg-gray-50/50 dark:bg-slate-800/50'}>
                            <td className="px-3 py-2 text-xs text-gray-400 font-mono">{i + 1}</td>
                            {columns.map(col => {
                                const raw = row[col];
                                let display;
                                if (raw === null || raw === undefined) {
                                    display = <span className="text-gray-400 italic">null</span>;
                                } else {
                                    const n = Number(raw);
                                    if ((typeof raw === 'number' || (typeof raw === 'string' && raw !== '' && !isNaN(n))) && !Number.isInteger(n) && isFinite(n)) {
                                        display = parseFloat(n.toFixed(2)).toString();
                                    } else {
                                        display = String(raw);
                                    }
                                }
                                return (
                                    <td key={col} className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                                        {display}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="px-3 py-2 bg-gray-50 dark:bg-slate-800 border-t border-gray-200 dark:border-white/10 flex justify-between text-[11px] text-gray-500 dark:text-gray-400 font-mono">
                <span>{rows.length} row{rows.length !== 1 ? 's' : ''}</span>
                {executionTime && <span>{executionTime}s</span>}
            </div>
        </div>
    );
};

// ─── Chart Renderer ─────────────────────────────────────
const ChartRenderer = ({
    data, chartType, xAxis, yAxis, metrics,
    showGrid = true, showLabels = false, stacked = false, orientation = 'vertical', legendPosition = 'top'
}) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    // ─── Multi-Series Transformation ───
    const { chartData, seriesCols, isMultiSeries, dimensionCol } = useMemo(() => {
        if (!data || data.length === 0) {
            return { chartData: [], seriesCols: [], isMultiSeries: false, columns: [], dimensionCol: '' };
        }

        const cols = Object.keys(data[0]);
        const dimCol = xAxis || cols[0];

        // If user manually selected Y-Axis, respect it (single series mode usually)
        if (yAxis && cols.includes(yAxis)) {
            return { chartData: data, seriesCols: [yAxis], isMultiSeries: false, columns: cols, dimensionCol: dimCol };
        }

        // Identify roles - treat number, bigint, and numeric-looking strings as numeric
        const isNumericVal = (v) => {
            if (v === null || v === undefined) return false;
            if (typeof v === 'number') return !isNaN(v);
            if (typeof v === 'bigint') return true;
            if (typeof v === 'string') return v.trim() !== '' && !isNaN(Number(v));
            return false;
        };
        const metricCol = cols.find(c => c !== dimCol && isNumericVal(data[0][c]) && !(data[0][c] instanceof Date));
        const seriesCol = cols.find(c => c !== dimCol && c !== metricCol && (typeof data[0][c] === 'string' || data[0][c] instanceof Date));

        if (seriesCol && metricCol) {
            const pivoted = {};
            const seriesKeys = new Set();

            data.forEach(row => {
                const xVal = row[dimCol];
                const seriesVal = row[seriesCol];
                const metricVal = row[metricCol];

                if (!pivoted[xVal]) {
                    pivoted[xVal] = { [dimCol]: xVal };
                }
                pivoted[xVal][seriesVal] = metricVal;
                seriesKeys.add(seriesVal);
            });

            return {
                chartData: Object.values(pivoted),
                seriesCols: Array.from(seriesKeys).sort(),
                isMultiSeries: true,
                columns: cols,
                dimensionCol: dimCol
            };
        }

        // Fallback: Single Series
        const measureCols = yAxis ? [yAxis] : cols.filter(c => c !== dimCol);
        return {
            chartData: data,
            seriesCols: measureCols,
            isMultiSeries: false,
            columns: cols,
            dimensionCol: dimCol
        };
    }, [data, xAxis, yAxis]);

    if (!data || data.length === 0) return null;

    const axisStyle = { fontSize: 11, fill: isDark ? '#94a3b8' : '#6b7280' };

    // Horizontal Bar logic
    const isHorizontal = chartType === 'bar' && orientation === 'horizontal';

    // ─── Smart X-Axis Tick Config ─────────────────────────────────────────────
    // Computes rotation angle, height, and tick interval using pixel geometry so
    // labels never overlap regardless of data density, label length, or date grain.
    // ─── Smart X-Axis Tick Config ─────────────────────────────────────────────
    // Computes tick interval to ensure we only show ~6-8 ticks max.
    // This prevents rotation and keeps the chart clean.
    const xAxisConfig = (() => {
        if (!chartData || chartData.length === 0) {
            return { angle: 0, textAnchor: 'middle', height: 30, dx: 0, dy: 4, interval: 0, bottomMargin: 5 };
        }

        const count = chartData.length;
        const targetTicks = 8; // Aim for about 8 ticks max
        const interval = Math.ceil(count / targetTicks) - 1;

        return {
            angle: 0, // Never rotate
            textAnchor: 'middle',
            height: 30, // Fixed minimal height
            dx: 0,
            dy: 5,
            interval: interval, // Sparse ticks
            bottomMargin: 10,
        };
    })();

    const commonProps = {
        data: chartData,
        margin: { top: 10, right: 30, left: 30, bottom: xAxisConfig.bottomMargin + 40 }, // Increased margin for standard titles
        layout: isHorizontal ? 'vertical' : 'horizontal'
    };

    const renderGrid = () => showGrid ? (
        <CartesianGrid strokeDasharray="3 3" vertical={false} horizontal={true} stroke={isDark ? '#334155' : '#e2e8f0'} />
    ) : null;

    const renderLegend = () => legendPosition === 'none' ? null : (
        <Legend
            verticalAlign="top"
            align="right"
            height={28}
            iconType="circle"
            iconSize={6}
            wrapperStyle={{ top: 0, right: 0, fontSize: '11px', fontWeight: 600 }}
            formatter={(v) => <span style={{ color: isDark ? '#94a3b8' : '#6b7280' }}>{v}</span>}
        />
    );

    // ─── Axis Formatting ───
    const formatAxisTick = (value) => {
        if (value === null || value === undefined) return '';
        if (chartType === 'stacked-bar' && typeof value === 'number') return `${Math.round(value * 100)}%`;
        const str = String(value);

        // 1. Numbers: Compact K/M/B + smart decimal rounding
        const num = Number(value);
        if (!isNaN(num) && !isNaN(parseFloat(value))) {
            if (num === 0) return '0';
            if (Math.abs(num) >= 1_000_000_000) return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
            if (Math.abs(num) >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
            if (Math.abs(num) >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
            // Smart decimal: show at most 2 decimal places, strip trailing zeros
            if (!Number.isInteger(num)) return parseFloat(num.toFixed(2)).toString();
            return str;
        }

        // 2. Dates: "Jan 1" or "2023"
        // Heuristic: contains dash/slash and looks roughly like a date
        if (str.length >= 8 && /[-/]/.test(str) && !/^\d+$/.test(str)) {
            const d = new Date(str);
            if (!isNaN(d.getTime())) {
                return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            }
        }

        // 3. Strings: Smart Truncate
        if (str.length > 12) {
            return str.substring(0, 10).trim() + '..';
        }
        return str;
    };

    const formatAxisTitle = (key) => {
        // If we have an explicit metric config, use it for Y-Axis when key matches a series
        if (metrics && metrics.length > 0) {
            // If the key is one of the series columns, it means we are in multi-series mode.
            // The axis should usually be labeled by the Metric (e.g. "Count" or "Average"), not the Series Name (e.g. "Android")
            if (seriesCols && seriesCols.includes(key) && !yAxis) {
                const m = metrics[0];
                if (!m.fn) return '';
                if (m.fn === 'COUNT' && m.column === '*') return 'Count';
                if (m.fn === 'COUNT_DISTINCT') return `Count Distinct (${m.column})`;
                if (m.fn.includes('AVG')) return `Average ${m.column}`;
                return `${m.fn} ${m.column}`;
            }
        }

        if (!key) return '';
        // Standard Title Case
        return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const renderAxes = () => (
        <>
            {isHorizontal ? (
                <>
                    <XAxis type="number" tick={axisStyle} axisLine={false} tickLine={false}>
                        <Label value={formatAxisTitle(yAxis || (seriesCols && seriesCols[0]))} offset={0} position="insideBottom" dy={10} style={{ ...axisStyle, textAnchor: 'middle', fontWeight: 600 }} />
                    </XAxis>
                    <YAxis type="category" dataKey={dimensionCol} tick={axisStyle} axisLine={false} tickLine={false} width={100} tickFormatter={formatAxisTick}>
                        <Label value={formatAxisTitle(dimensionCol)} angle={-90} position="insideLeft" dx={-8} style={{ ...axisStyle, textAnchor: 'middle', fontWeight: 600 }} />
                    </YAxis>
                </>
            ) : (
                <>
                    <XAxis
                        dataKey={dimensionCol}
                        tick={{ ...axisStyle, textAnchor: xAxisConfig.textAnchor, fill: isDark ? '#94a3b8' : '#64748b' }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={formatAxisTick}
                        angle={xAxisConfig.angle}
                        height={xAxisConfig.height}
                        dx={xAxisConfig.dx}
                        dy={xAxisConfig.dy}
                        interval={xAxisConfig.interval}
                        padding={{ left: 10, right: 10 }}
                    >
                        <Label value={formatAxisTitle(dimensionCol)} offset={0} position="insideBottom" dy={10} style={{ ...axisStyle, textAnchor: 'middle', fontWeight: 600 }} />
                    </XAxis>
                    <YAxis
                        tick={{ ...axisStyle, fill: isDark ? '#94a3b8' : '#64748b' }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={formatAxisTick}
                        width={60}
                    >
                        <Label value={formatAxisTitle(yAxis || (seriesCols && seriesCols[0]))} angle={-90} position="insideLeft" dx={-8} style={{ ...axisStyle, textAnchor: 'middle', fontWeight: 600 }} />
                    </YAxis>
                </>
            )}
        </>
    );

    const chartColors = ['#f97316', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#ec4899', '#64748b'];
    // Disable animation when labels are on so LabelList renders immediately (no post-animation delay)
    const noAnimation = showLabels;

    if (chartType === 'pie') {
        const pieMetric = seriesCols[0]; // Simple guess
        const PIE_RADIAN = Math.PI / 180;
        const renderPieLabel = ({ cx, cy, midAngle, outerRadius, percent, name }) => {
            if (percent < 0.03) return null; // skip tiny slices
            const radius = outerRadius + 34;
            const x = cx + radius * Math.cos(-midAngle * PIE_RADIAN);
            const y = cy + radius * Math.sin(-midAngle * PIE_RADIAN);
            return (
                <text
                    x={x}
                    y={y}
                    fill={isDark ? '#e2e8f0' : '#1e293b'}
                    textAnchor={x > cx ? 'start' : 'end'}
                    dominantBaseline="central"
                    fontSize={11}
                    fontWeight={500}
                >
                    {`${name} ${(percent * 100).toFixed(1)}%`}
                </text>
            );
        };
        const renderPieTooltip = ({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const entry = payload[0];
            const total = chartData.reduce((sum, d) => sum + (Number(d[pieMetric]) || 0), 0);
            const pct = total > 0 ? ((Number(entry.value) / total) * 100).toFixed(1) : '0.0';
            return (
                <div style={{
                    backgroundColor: isDark ? '#1e293b' : 'rgba(255,255,255,0.97)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                    padding: '8px 12px',
                    fontSize: '12px',
                    minWidth: '130px'
                }}>
                    <p style={{ color: isDark ? '#fff' : '#1e293b', fontWeight: 700, marginBottom: '4px' }}>{entry.name}</p>
                    <p style={{ color: isDark ? '#94a3b8' : '#64748b', margin: '2px 0' }}>Value: <span style={{ color: isDark ? '#e2e8f0' : '#1e293b', fontWeight: 600 }}>{entry.value?.toLocaleString()}</span></p>
                    <p style={{ color: '#f97316', fontWeight: 600, margin: '2px 0' }}>{pct}%</p>
                </div>
            );
        };
        return (
            <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 20, right: 50, left: 50, bottom: 20 }}>
                    <Pie
                        data={chartData}
                        dataKey={pieMetric}
                        nameKey={dimensionCol}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        isAnimationActive={false}
                        label={showLabels ? renderPieLabel : false}
                        labelLine={showLabels}
                    >
                        {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} stroke={isDark ? '#1e293b' : '#fff'} strokeWidth={2} />
                        ))}
                    </Pie>
                    <Tooltip content={renderPieTooltip} />
                    {renderLegend()}
                </PieChart>
            </ResponsiveContainer>
        );
    }

    // ─── Histogram ──────────────────────────────────────────────────────────────
    if (chartType === 'histogram') {
        return (
            <ResponsiveContainer width="100%" height="100%">
                <BarChart {...commonProps} barCategoryGap="1%" barGap={0}>
                    {renderGrid()}
                    {renderAxes()}
                    <Tooltip
                        labelFormatter={formatAxisTick}
                        contentStyle={{ backgroundColor: isDark ? '#1e293b' : 'rgba(255,255,255,0.95)', borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0', borderRadius: '8px', fontSize: '12px', padding: '8px 12px' }}
                        itemStyle={{ fontWeight: 600 }}
                        labelStyle={{ color: isDark ? '#94a3b8' : '#64748b', marginBottom: '4px' }}
                    />
                    {seriesCols.map((col, i) => (
                        <Bar key={col} dataKey={col} fill={chartColors[i % chartColors.length]} radius={0} name={col} isAnimationActive={!noAnimation}>
                            {showLabels && <LabelList dataKey={col} position="top" style={{ fill: isDark ? '#e2e8f0' : '#475569', fontSize: 10, fontWeight: 600 }} formatter={(v) => typeof v === 'number' && v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v} />}
                        </Bar>
                    ))}
                    {renderLegend()}
                </BarChart>
            </ResponsiveContainer>
        );
    }

    // ─── Heatmap ─────────────────────────────────────────────────────────────────
    if (chartType === 'heatmap') {
        // Expects rows with 2 dimension cols (e.g. city, trip_hour) + 1 numeric value col.
        // Auto-detects: Y-dim = first string col, X-dim = second col (numeric ordinal or string), value = numeric.
        const cols = Object.keys(data[0]);
        const numericCols = cols.filter(c => {
            const v = data[0][c];
            return typeof v === 'number' || typeof v === 'bigint' || (!isNaN(Number(v)) && v !== null && v !== '');
        });
        const stringCols = cols.filter(c => !numericCols.includes(c));

        const yDim = stringCols[0] || cols[0];
        const xDim = stringCols[1] || numericCols[0] || cols[1];
        const valueCol = numericCols.find(c => c !== xDim) || numericCols[0];

        const xVals = [...new Set(data.map(r => r[xDim]))].sort((a, b) => {
            const na = Number(a), nb = Number(b);
            return !isNaN(na) && !isNaN(nb) ? na - nb : String(a).localeCompare(String(b));
        });
        const yVals = [...new Set(data.map(r => r[yDim]))].sort((a, b) => String(a).localeCompare(String(b)));

        const lookup = {};
        const allHeatVals = [];
        data.forEach(row => {
            const v = Number(row[valueCol]);
            lookup[`${row[yDim]}||${row[xDim]}`] = v;
            if (!isNaN(v)) allHeatVals.push(v);
        });
        const heatMin = Math.min(...allHeatVals);
        const heatMax = Math.max(...allHeatVals);

        const getHeatColor = (v) => {
            if (v === undefined || isNaN(v)) return isDark ? '#1e293b' : '#f1f5f9';
            const ratio = heatMax === heatMin ? 0.5 : (v - heatMin) / (heatMax - heatMin);
            if (isDark) {
                const r = Math.round(30  + (249 - 30)  * ratio);
                const g = Math.round(58  + (115 - 58)  * ratio);
                const b = Math.round(95  + (22  - 95)  * ratio);
                return `rgb(${r},${g},${b})`;
            } else {
                const r = Math.round(240 + (249 - 240) * ratio);
                const g = Math.round(249 + (115 - 249) * ratio);
                const b = Math.round(255 + (22  - 255) * ratio);
                return `rgb(${r},${g},${b})`;
            }
        };

        const cellW = Math.max(26, Math.min(52, Math.floor(560 / (xVals.length + 1))));
        const cellH = 26;
        const fmtHeat = (v) => { if (isNaN(v) || v === undefined) return ''; const n = Number(v); if (n >= 1000) return (n/1000).toFixed(1)+'k'; return n.toLocaleString(); };

        return (
            <div className="w-full h-full overflow-auto p-4 flex flex-col items-start">
                <div className="inline-flex flex-col gap-[3px]">
                    {/* X-axis header */}
                    <div className="flex gap-[3px]" style={{ marginLeft: 88 }}>
                        {xVals.map(xv => (
                            <div key={String(xv)} style={{ width: cellW, flexShrink: 0 }}
                                className={`text-center text-[9px] font-semibold truncate ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                                {String(xv)}
                            </div>
                        ))}
                    </div>
                    {/* Data rows */}
                    {yVals.map(yv => (
                        <div key={String(yv)} className="flex items-center gap-[3px]">
                            <div style={{ width: 80, flexShrink: 0 }}
                                className={`text-right pr-2 text-[11px] font-medium truncate ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                                {String(yv)}
                            </div>
                            {xVals.map(xv => {
                                const v = lookup[`${yv}||${xv}`];
                                const bg = getHeatColor(v);
                                const textBright = v !== undefined && !isNaN(v) && ((v - heatMin) / (heatMax - heatMin || 1)) > 0.55;
                                return (
                                    <div key={String(xv)}
                                        title={`${yv} @ ${xv}: ${fmtHeat(v)}`}
                                        className="flex items-center justify-center rounded-sm cursor-default hover:opacity-75 transition-opacity"
                                        style={{ width: cellW, height: cellH, backgroundColor: bg, flexShrink: 0 }}>
                                        {showLabels && v !== undefined && (
                                            <span style={{ fontSize: 8, fontWeight: 700, color: textBright ? '#fff' : (isDark ? '#e2e8f0' : '#1e3a5f') }}>
                                                {fmtHeat(v)}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
                {/* Gradient scale legend */}
                <div className="flex items-center gap-2 mt-4" style={{ marginLeft: 88 }}>
                    <span className={`text-[10px] tabular-nums ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{fmtHeat(heatMin)}</span>
                    <div className="h-2 rounded-full" style={{
                        width: Math.min(160, xVals.length * cellW),
                        background: isDark
                            ? 'linear-gradient(to right, #1e3a5f, #f97316)'
                            : 'linear-gradient(to right, #f0f9ff, #f97316)',
                    }} />
                    <span className={`text-[10px] tabular-nums ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{fmtHeat(heatMax)}</span>
                </div>
            </div>
        );
    }

    // ─── Scatter Plot ────────────────────────────────────────────────────────────
    if (chartType === 'scatter') {
        const cols = Object.keys(data[0]);
        const numericCols = cols.filter(c => {
            const v = data[0][c];
            return typeof v === 'number' || typeof v === 'bigint' || (!isNaN(Number(v)) && v !== null && v !== '');
        });
        const xCol = xAxis && numericCols.includes(xAxis) ? xAxis : numericCols[0] || dimensionCol;
        const yCol = yAxis && numericCols.includes(yAxis) && yAxis !== xCol
            ? yAxis
            : (numericCols.find(c => c !== xCol) || numericCols[0]);
        const scatterData = data.map(row => ({
            x: Number(row[xCol]) || 0,
            y: Number(row[yCol]) || 0,
        }));
        // Use plain title-case for scatter axis labels to avoid metric[0]-only confusion
        const scatterLabel = (col) => col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        return (
            <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 20, right: 30, left: 30, bottom: 50 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e2e8f0'} />
                    <XAxis type="number" dataKey="x" name={scatterLabel(xCol)} tick={axisStyle} axisLine={false} tickLine={false} tickFormatter={formatAxisTick}>
                        <Label value={scatterLabel(xCol)} position="insideBottom" offset={-15} style={{ ...axisStyle, textAnchor: 'middle', fontWeight: 600 }} />
                    </XAxis>
                    <YAxis type="number" dataKey="y" name={scatterLabel(yCol)} tick={axisStyle} axisLine={false} tickLine={false} tickFormatter={formatAxisTick} width={60}>
                        <Label value={scatterLabel(yCol)} angle={-90} position="insideLeft" dx={-8} style={{ ...axisStyle, textAnchor: 'middle', fontWeight: 600 }} />
                    </YAxis>
                    <Tooltip
                        cursor={{ strokeDasharray: '3 3', stroke: isDark ? '#475569' : '#cbd5e1' }}
                        contentStyle={{ backgroundColor: isDark ? '#1e293b' : 'rgba(255,255,255,0.95)', borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0', borderRadius: '8px', fontSize: '12px', padding: '8px 12px' }}
                        itemStyle={{ fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}
                        labelStyle={{ display: 'none' }}
                    />
                    <Scatter data={scatterData} fill={chartColors[0]} fillOpacity={0.65} />
                </ScatterChart>
            </ResponsiveContainer>
        );
    }

    // ─── Box Plot ────────────────────────────────────────────────────────────────
    if (chartType === 'boxplot') {
        const cols = Object.keys(data[0]);
        const isNumericVal = (v) => typeof v === 'number' || typeof v === 'bigint' || (!isNaN(Number(v)) && v !== null && v !== '');
        const numericCols = cols.filter(c => isNumericVal(data[0][c]));
        const nonNumericCols = cols.filter(c => !numericCols.includes(c));
        const catCol = (xAxis && cols.includes(xAxis)) ? xAxis : (nonNumericCols[0] || cols[0]);
        const valCol = (yAxis && numericCols.includes(yAxis)) ? yAxis : (numericCols.find(c => c !== catCol) || numericCols[0]);
        if (!valCol) return <p className="text-xs text-gray-400 p-4">Box plot needs at least one numeric column.</p>;

        const quantile = (sorted, p) => {
            const idx = p * (sorted.length - 1);
            const lo = Math.floor(idx), hi = Math.ceil(idx);
            return sorted[lo] + (idx - lo) * (sorted[hi] - sorted[lo]);
        };
        const computeStats = (values) => {
            const sorted = values.map(Number).filter(v => !isNaN(v)).sort((a, b) => a - b);
            if (sorted.length === 0) return null;
            return { n: sorted.length, min: sorted[0], q1: quantile(sorted, 0.25), median: quantile(sorted, 0.5), q3: quantile(sorted, 0.75), max: sorted[sorted.length - 1] };
        };

        const groupMap = {};
        data.forEach(row => {
            const key = String(row[catCol] ?? '(blank)');
            if (!groupMap[key]) groupMap[key] = [];
            groupMap[key].push(row[valCol]);
        });
        const boxData = Object.entries(groupMap)
            .map(([cat, vals]) => ({ cat, ...computeStats(vals) }))
            .filter(r => r.min !== undefined)
            .sort((a, b) => b.median - a.median);

        if (boxData.length === 0) return null;

        const allVals = boxData.flatMap(r => [r.min, r.max]);
        const bpGlobalMin = Math.min(...allVals);
        const bpGlobalMax = Math.max(...allVals);
        const bpPad  = (bpGlobalMax - bpGlobalMin) * 0.1 || 2;
        const domMin = Math.max(0, bpGlobalMin - bpPad);
        const domMax = bpGlobalMax + bpPad;

        const nCats   = boxData.length;
        // Legend sits at bottom: 28px high + 16px gap above cat labels
        const legH    = 28;
        const padT    = 24, padB = 68 + legH, padL = 68, padR = 24;
        const svgH    = 340;
        const plotH   = svgH - padT - padB;
        const svgW    = Math.max(420, nCats * 100 + padL + padR);
        const catStep = (svgW - padL - padR) / nCats;
        const boxW    = Math.min(52, catStep * 0.50);
        const yPx     = (v) => padT + plotH - ((v - domMin) / (domMax - domMin)) * plotH;
        const xCat    = (i) => padL + catStep * i + catStep / 2;
        const fmtShort = (v) => { const n = Number(v); return n >= 1000 ? (n/1000).toFixed(1)+'k' : n.toFixed(1); };

        const colors = ['#f97316','#3b82f6','#10b981','#f59e0b','#8b5cf6','#06b6d4','#ec4899','#64748b'];
        const bpWColor    = isDark ? '#475569' : '#94a3b8';
        const bpTxtColor  = isDark ? '#94a3b8' : '#64748b';
        const bpGridColor = isDark ? '#334155' : '#e2e8f0';
        const metricLabel = formatAxisTitle(valCol);
        const yGridCount  = 6;

        // Legend data - bottom-centered, plain English
        const legendItems = [
            { type: 'box',  label: 'Middle 50% of values' },
            { type: 'line', label: 'Median (midpoint)' },
            { type: 'whisk', label: 'Min / Max range' },
        ];
        // Measure legend width to centre it
        const legItemW = 148;
        const legTotalW = legendItems.length * legItemW;
        const legX = (svgW - legTotalW) / 2;
        const legY = svgH - legH + 4;

        return (
            <div className="w-full overflow-x-auto">
                <svg width="100%" viewBox={`0 0 ${svgW} ${svgH}`} style={{ display: 'block', minWidth: 320 }}>
                    <defs>
                        {boxData.map((_, i) => {
                            const c = colors[i % colors.length];
                            return (
                                <linearGradient key={i} id={`bp-grad-${i}`} x1="0" y1="0" x2="1" y2="0">
                                    <stop offset="0%" stopColor={c} stopOpacity={isDark ? 0.35 : 0.18} />
                                    <stop offset="100%" stopColor={c} stopOpacity={isDark ? 0.20 : 0.08} />
                                </linearGradient>
                            );
                        })}
                    </defs>

                    {/* Chart area background */}
                    <rect x={padL} y={padT} width={svgW - padL - padR} height={plotH}
                        fill={isDark ? 'rgba(255,255,255,0.015)' : 'rgba(0,0,0,0.01)'} rx={4} />

                    {/* Y gridlines + labels */}
                    {Array.from({ length: yGridCount }).map((_, i) => {
                        const val = domMin + (domMax - domMin) * (i / (yGridCount - 1));
                        const y   = yPx(val);
                        return (
                            <g key={i}>
                                <line x1={padL} y1={y} x2={svgW - padR} y2={y}
                                    stroke={bpGridColor} strokeWidth={i === 0 ? 1.5 : 1}
                                    strokeDasharray={i === 0 ? '0' : '4 3'} />
                                <text x={padL - 7} y={y + 4} textAnchor="end" fontSize={10} fill={bpTxtColor} fontFamily="monospace">
                                    {fmtShort(val)}
                                </text>
                            </g>
                        );
                    })}

                    {/* Y-axis title */}
                    <text textAnchor="middle" fontSize={10} fontWeight={700} fill={bpTxtColor}
                        transform={`translate(${padL - 52}, ${padT + plotH / 2}) rotate(-90)`}>
                        {metricLabel}
                    </text>

                    {/* One box per category */}
                    {boxData.map((row, i) => {
                        const cx   = xCat(i);
                        const minY = yPx(row.min);
                        const q1Y  = yPx(row.q1);
                        const medY = yPx(row.median);
                        const q3Y  = yPx(row.q3);
                        const maxY = yPx(row.max);
                        const color = colors[i % colors.length];
                        const iqrH  = Math.max(2, q1Y - q3Y);
                        return (
                            <g key={i}>
                                {/* Whisker stems (dashed) */}
                                <line x1={cx} y1={q1Y} x2={cx} y2={minY}
                                    stroke={bpWColor} strokeWidth={1.5} strokeDasharray="3 2" />
                                <line x1={cx} y1={q3Y} x2={cx} y2={maxY}
                                    stroke={bpWColor} strokeWidth={1.5} strokeDasharray="3 2" />
                                {/* Whisker end caps */}
                                <line x1={cx - boxW * 0.28} y1={minY} x2={cx + boxW * 0.28} y2={minY}
                                    stroke={bpWColor} strokeWidth={2} strokeLinecap="round" />
                                <line x1={cx - boxW * 0.28} y1={maxY} x2={cx + boxW * 0.28} y2={maxY}
                                    stroke={bpWColor} strokeWidth={2} strokeLinecap="round" />
                                {/* Min/Max value labels */}
                                <text x={cx + boxW * 0.5 + 5} y={minY + 3} fontSize={9} fill={bpTxtColor} dominantBaseline="middle">
                                    {fmtShort(row.min)}
                                </text>
                                <text x={cx + boxW * 0.5 + 5} y={maxY + 3} fontSize={9} fill={bpTxtColor} dominantBaseline="middle">
                                    {fmtShort(row.max)}
                                </text>
                                {/* Box body (gradient fill) */}
                                <rect x={cx - boxW / 2} y={q3Y} width={boxW} height={iqrH}
                                    fill={`url(#bp-grad-${i})`} stroke={color} strokeWidth={2} rx={4} />
                                {/* Subtle side accent */}
                                <rect x={cx - boxW / 2} y={q3Y} width={3} height={iqrH}
                                    fill={color} opacity={0.7} rx={2} />
                                {/* Median line */}
                                <line x1={cx - boxW / 2 + 3} y1={medY} x2={cx + boxW / 2} y2={medY}
                                    stroke={color} strokeWidth={3} strokeLinecap="round" />
                                {/* Median value badge */}
                                <rect x={cx - 18} y={medY - 8} width={36} height={14} rx={7}
                                    fill={color} opacity={0.9} />
                                <text x={cx} y={medY + 1} textAnchor="middle" fontSize={8.5} fontWeight={700}
                                    fill="white" dominantBaseline="middle">
                                    {fmtShort(row.median)}
                                </text>
                                {/* Category label */}
                                <text x={cx} y={svgH - padB + 16} textAnchor="middle" fontSize={11}
                                    fontWeight={700} fill={isDark ? '#e2e8f0' : '#374151'}>
                                    {row.cat}
                                </text>
                                {/* n= count */}
                                <text x={cx} y={svgH - padB + 30} textAnchor="middle" fontSize={9}
                                    fill={bpTxtColor}>
                                    {row.n.toLocaleString()} rides
                                </text>
                            </g>
                        );
                    })}

                    {/* Bottom legend - centered, plain English */}
                    {legendItems.map((item, i) => {
                        const lx = legX + i * legItemW + 10;
                        const ly = legY + 10;
                        const sampleColor = colors[0];
                        return (
                            <g key={i}>
                                {item.type === 'box' && (
                                    <rect x={lx} y={ly - 5} width={12} height={12}
                                        fill={isDark ? 'rgba(249,115,22,0.3)' : 'rgba(249,115,22,0.15)'}
                                        stroke={sampleColor} strokeWidth={1.5} rx={2} />
                                )}
                                {item.type === 'line' && (
                                    <line x1={lx} y1={ly + 1} x2={lx + 12} y2={ly + 1}
                                        stroke={sampleColor} strokeWidth={3} strokeLinecap="round" />
                                )}
                                {item.type === 'whisk' && (
                                    <>
                                        <line x1={lx + 6} y1={ly - 5} x2={lx + 6} y2={ly + 7}
                                            stroke={bpWColor} strokeWidth={1.5} strokeDasharray="3 2" />
                                        <line x1={lx + 2} y1={ly - 5} x2={lx + 10} y2={ly - 5}
                                            stroke={bpWColor} strokeWidth={2} strokeLinecap="round" />
                                        <line x1={lx + 2} y1={ly + 7} x2={lx + 10} y2={ly + 7}
                                            stroke={bpWColor} strokeWidth={2} strokeLinecap="round" />
                                    </>
                                )}
                                <text x={lx + 16} y={ly + 2} fontSize={10} fill={bpTxtColor} dominantBaseline="middle">
                                    {item.label}
                                </text>
                            </g>
                        );
                    })}
                </svg>
            </div>
        );
    }

    // ─── Combo Chart (Bar + Line, shared axis) ─────────────────────────────────
    if (chartType === 'combo') {
        return (
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart {...commonProps}>
                    {renderGrid()}
                    {renderAxes()}
                    <Tooltip
                        labelFormatter={formatAxisTick}
                        contentStyle={{ backgroundColor: isDark ? '#1e293b' : 'rgba(255,255,255,0.95)', borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0', borderRadius: '8px', fontSize: '12px', padding: '8px 12px' }}
                        itemStyle={{ fontWeight: 600 }}
                        cursor={{ stroke: isDark ? '#475569' : '#cbd5e1', strokeWidth: 1, strokeDasharray: '3 3' }}
                        labelStyle={{ color: isDark ? '#94a3b8' : '#64748b', marginBottom: '4px' }}
                    />
                    {renderLegend()}
                    {seriesCols.map((series, i) => {
                        const color = chartColors[i % chartColors.length];
                        const labelStyle = { fill: isDark ? '#e2e8f0' : '#475569', fontSize: 10, fontWeight: 600 };
                        const labelFmt = (v) => typeof v === 'number' && v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v;
                        if (i === 0) return (
                            <Bar key={series} dataKey={series} fill={color} radius={[4, 4, 0, 0]} fillOpacity={0.85} isAnimationActive={!noAnimation}>
                                {showLabels && <LabelList dataKey={series} position="top" style={labelStyle} formatter={labelFmt} />}
                            </Bar>
                        );
                        return (
                            <Line key={series} type="monotone" dataKey={series} stroke={color} strokeWidth={3} dot={showLabels ? { r: 3, fill: color, strokeWidth: 0 } : false} activeDot={{ r: 6 }} isAnimationActive={!noAnimation}>
                                {showLabels && <LabelList dataKey={series} position="top" style={labelStyle} formatter={labelFmt} />}
                            </Line>
                        );
                    })}
                </ComposedChart>
            </ResponsiveContainer>
        );
    }

    // ─── Dual-Axis Chart (Bar left + Line right) ─────────────────────────────────
    if (chartType === 'dual-axis') {
        const leftSeries = seriesCols[0];
        const rightSeries = seriesCols.length > 1 ? seriesCols.slice(1) : seriesCols; // fallback: same col on right
        return (
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 10, right: 55, left: 30, bottom: xAxisConfig.bottomMargin + 40 }}>
                    {renderGrid()}
                    <XAxis
                        dataKey={dimensionCol}
                        tick={{ ...axisStyle, textAnchor: xAxisConfig.textAnchor, fill: isDark ? '#94a3b8' : '#64748b' }}
                        axisLine={false} tickLine={false}
                        tickFormatter={formatAxisTick}
                        interval={xAxisConfig.interval}
                        height={xAxisConfig.height}
                        padding={{ left: 10, right: 10 }}
                    >
                        <Label value={formatAxisTitle(dimensionCol)} offset={0} position="insideBottom" dy={10} style={{ ...axisStyle, textAnchor: 'middle', fontWeight: 600 }} />
                    </XAxis>
                    <YAxis
                        yAxisId="left"
                        orientation="left"
                        tick={{ ...axisStyle, fill: isDark ? '#94a3b8' : '#64748b' }}
                        axisLine={false} tickLine={false}
                        tickFormatter={formatAxisTick}
                        width={45}
                    >
                        <Label value={formatAxisTitle(leftSeries)} angle={-90} position="insideLeft" dx={-8} style={{ ...axisStyle, textAnchor: 'middle', fontWeight: 600, fill: chartColors[0] }} />
                    </YAxis>
                    <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ ...axisStyle, fill: isDark ? '#94a3b8' : '#64748b' }}
                        axisLine={false} tickLine={false}
                        tickFormatter={formatAxisTick}
                        width={50}
                    >
                        <Label value={formatAxisTitle(rightSeries[0])} angle={90} position="insideRight" dx={8} style={{ ...axisStyle, textAnchor: 'middle', fontWeight: 600, fill: chartColors[1 % chartColors.length] }} />
                    </YAxis>
                    <Tooltip
                        labelFormatter={formatAxisTick}
                        contentStyle={{ backgroundColor: isDark ? '#1e293b' : 'rgba(255,255,255,0.95)', borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0', borderRadius: '8px', fontSize: '12px', padding: '8px 12px' }}
                        itemStyle={{ fontWeight: 600 }}
                        cursor={{ stroke: isDark ? '#475569' : '#cbd5e1', strokeWidth: 1, strokeDasharray: '3 3' }}
                        labelStyle={{ color: isDark ? '#94a3b8' : '#64748b', marginBottom: '4px' }}
                    />
                    {renderLegend()}
                    <Bar yAxisId="left" dataKey={leftSeries} fill={chartColors[0]} radius={[4, 4, 0, 0]} fillOpacity={0.8} isAnimationActive={!noAnimation}>
                        {showLabels && <LabelList dataKey={leftSeries} position="top" style={{ fill: isDark ? '#e2e8f0' : '#475569', fontSize: 10, fontWeight: 600 }} formatter={(v) => typeof v === 'number' && v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v} />}
                    </Bar>
                    {rightSeries.map((series, i) => (
                        <Line key={series} yAxisId="right" type="monotone" dataKey={series} stroke={chartColors[(i + 1) % chartColors.length]} strokeWidth={3} dot={showLabels ? { r: 3, fill: chartColors[(i + 1) % chartColors.length], strokeWidth: 0 } : false} activeDot={{ r: 6 }} isAnimationActive={!noAnimation}>
                            {showLabels && <LabelList dataKey={series} position="top" style={{ fill: isDark ? '#e2e8f0' : '#475569', fontSize: 10, fontWeight: 600 }} formatter={(v) => typeof v === 'number' && v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v} />}
                        </Line>
                    ))}
                </ComposedChart>
            </ResponsiveContainer>
        );
    }

    // ─── Map ────────────────────────────────────────────────────────────────────
    if (chartType === 'map') {
        return (
            <div style={{ width: '100%', height: '100%', flex: 1, minHeight: 0 }}>
                <MapChart data={data} dimensionCol={dimensionCol} seriesCols={seriesCols} isDark={isDark} />
            </div>
        );
    }

    // ─── Metric / KPI card (no Recharts needed) ───────────────────────────────
    if (chartType === 'metric') {
        const metricRows = data.slice(0, 9);
        const cols = Object.keys(data[0] || {});
        const numericCols = cols.filter(c => typeof data[0][c] === 'number' || typeof data[0][c] === 'bigint');
        const labelCol = cols.find(c => !numericCols.includes(c));
        const tiles = labelCol
            ? metricRows.map(row => ({
                label: String(row[labelCol] ?? '-'),
                value: numericCols.length > 0 ? row[numericCols[0]] : null,
                col: numericCols[0] ?? '',
            }))
            : numericCols.map(col => ({ label: col, value: data[0][col], col }));
        const fmt = (v) => {
            if (v === null || v === undefined) return '-';
            const n = Number(v);
            if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
            if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
            return n.toLocaleString();
        };
        return (
            <div className="w-full h-[300px] flex flex-col justify-center">
                <div className={`grid gap-3 px-4 ${tiles.length === 1 ? 'grid-cols-1 max-w-xs mx-auto w-full' : tiles.length <= 2 ? 'grid-cols-2' : tiles.length <= 4 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                    {tiles.map((tile, i) => (
                        <div key={i} className={`relative overflow-hidden rounded-xl border transition-all ${isDark ? 'bg-[#1e293b] border-white/10 hover:border-white/20' : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'}`}>
                            <div className="absolute top-0 left-0 right-0 h-0.5" style={{ backgroundColor: chartColors[i % chartColors.length] }} />
                            <div className="p-4">
                                <p className={`text-[11px] font-medium uppercase tracking-wider truncate mb-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{tile.label}</p>
                                <p className="text-2xl font-bold tabular-nums leading-none" style={{ color: chartColors[i % chartColors.length] }}>{fmt(tile.value)}</p>
                                {showLabels && tile.value !== null && (
                                    <p className={`text-[10px] mt-1 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{Number(tile.value).toLocaleString()}</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <ResponsiveContainer width="100%" height="100%">
            <ComposedChart {...commonProps} stackOffset={chartType === 'stacked-bar' ? 'expand' : 'none'}>
                {renderGrid()}
                {renderAxes()}
                <Tooltip
                    labelFormatter={formatAxisTick}
                    contentStyle={{
                        backgroundColor: isDark ? '#1e293b' : 'rgba(255, 255, 255, 0.95)',
                        borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        fontSize: '12px',
                        padding: '8px 12px'
                    }}
                    itemStyle={{ fontWeight: 600 }}
                    cursor={{ stroke: isDark ? '#475569' : '#cbd5e1', strokeWidth: 1, strokeDasharray: '3 3' }}
                    labelStyle={{ color: isDark ? '#94a3b8' : '#64748b', marginBottom: '4px' }}
                />
                {renderLegend()}
                {isMultiSeries ? (
                    seriesCols.map((series, i) => {
                        const color = chartColors[i % chartColors.length];
                        const labelStyle = { fill: isDark ? '#e2e8f0' : '#475569', fontSize: 10, fontWeight: 600 };
                        const labelFmt = (v) => typeof v === 'number' && v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v;
                        if (chartType === 'line') return (
                            <Line key={series} type="monotone" dataKey={series} stroke={color} strokeWidth={3} dot={showLabels ? { r: 3, fill: color, strokeWidth: 0 } : false} activeDot={{ r: 6 }} isAnimationActive={!noAnimation}>
                                {showLabels && <LabelList dataKey={series} position="top" style={labelStyle} formatter={labelFmt} />}
                            </Line>
                        );
                        if (chartType === 'area') return (
                            <Area key={series} type="monotone" dataKey={series} stackId="1" fill={color} fillOpacity={0.6} stroke={color} strokeWidth={1.5} isAnimationActive={!noAnimation}>
                                {showLabels && <LabelList dataKey={series} position="top" style={labelStyle} formatter={labelFmt} />}
                            </Area>
                        );
                        return (
                            <Bar key={series} dataKey={series} fill={color} radius={[4, 4, 0, 0]} stackId={(stacked || chartType === 'stacked-bar') ? '1' : undefined} isAnimationActive={!noAnimation}>
                                {showLabels && <LabelList dataKey={series} position={isHorizontal ? "right" : "top"} style={labelStyle} formatter={labelFmt} />}
                            </Bar>
                        );
                    })
                ) : (
                    <>
                        {chartType === 'line' && (
                            <Line type="monotone" dataKey={seriesCols[0]} stroke={chartColors[0]} strokeWidth={3} dot={showLabels ? { r: 3, fill: chartColors[0], strokeWidth: 0 } : false} activeDot={{ r: 6 }} isAnimationActive={!noAnimation}>
                                {showLabels && <LabelList dataKey={seriesCols[0]} position="top" style={{ fill: isDark ? '#e2e8f0' : '#475569', fontSize: 10, fontWeight: 600 }} formatter={(v) => typeof v === 'number' && v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v} />}
                            </Line>
                        )}
                        {chartType === 'area' && (
                            <Area type="monotone" dataKey={seriesCols[0]} fill={chartColors[0]} stroke={chartColors[0]} fillOpacity={0.3} strokeWidth={2} isAnimationActive={!noAnimation}>
                                {showLabels && <LabelList dataKey={seriesCols[0]} position="top" style={{ fill: isDark ? '#e2e8f0' : '#475569', fontSize: 10, fontWeight: 600 }} formatter={(v) => typeof v === 'number' && v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v} />}
                            </Area>
                        )}
                        {chartType === 'bar' && <Bar dataKey={seriesCols[0]} fill={chartColors[0]} radius={isHorizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]} isAnimationActive={!noAnimation}>
                            {showLabels && <LabelList dataKey={seriesCols[0]} position={isHorizontal ? "right" : "top"} style={{ fill: isDark ? '#e2e8f0' : '#475569', fontSize: 10, fontWeight: 600 }} formatter={(v) => typeof v === 'number' && v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v} />}
                        </Bar>}
                    </>
                )}
            </ComposedChart>
        </ResponsiveContainer>
    );

};

// ─── Chart Requirements Panel ──────────────────────
const ChartRequirements = ({ challenge }) => {
    const { answer, expectedChartType, goal } = challenge;
    const isPie = expectedChartType === 'pie';

    // X-axis / slice label
    const xReq = answer.groupBy && answer.groupBy.length > 0
        ? answer.groupBy
            .map(g => g.granularity ? `${g.column} (${g.granularity})` : g.column)
            .join(', ')
        : '-';

    // Y-axis / slice value
    const yReq = answer.aggregation
        ? `${answer.aggregation.fn}${answer.aggregation.column !== '*' ? ` of ${answer.aggregation.column}` : ''}`
        : '-';

    return (
        <div className="mb-4 bg-orange-50/50 dark:bg-orange-500/5 border border-orange-100 dark:border-orange-500/20 rounded-xl p-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                {/* Column 1: Context */}
                <div className="md:col-span-1 border-r border-orange-100 dark:border-orange-500/20 pr-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Lightbulb size={13} className="text-orange-500" />
                        <h3 className="text-[10px] font-bold text-orange-700 dark:text-orange-300 uppercase tracking-wider">Context</h3>
                    </div>
                    <p
                        className="text-[11px] text-gray-600 dark:text-gray-400 leading-relaxed"
                        dangerouslySetInnerHTML={{
                            __html: challenge.teach
                                .replace(/\*\*(.*?)\*\*/g, '___BOLD_START___$1___BOLD_END___')
                                .replace(/"([^"]*)"/g, '<span class="font-medium text-gray-800 dark:text-gray-200 border-b border-gray-300 dark:border-gray-600 pb-[1px]">$1</span>')
                                .replace(/`([^`]+)`/g, '<code class="bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-500/30 rounded px-1 py-0 font-mono font-semibold text-[10px]">$1</code>')
                                .replace(/___BOLD_START___/g, '<strong class="text-gray-900 dark:text-white font-bold">')
                                .replace(/___BOLD_END___/g, '</strong>')
                        }}
                    />
                </div>

                {/* Column 2: Visualization Requirements */}
                <div className="md:col-span-2">
                    <div className="flex items-center gap-2 mb-2">
                        <Target size={13} className="text-orange-500" />
                        <h3 className="text-[10px] font-bold text-orange-700 dark:text-orange-300 uppercase tracking-wider">Visualization Requirements</h3>
                    </div>

                    {/* 3-col spec row */}
                    <div className="grid grid-cols-3 gap-3 text-xs mb-3">
                        <div>
                            <span className="text-gray-400 dark:text-gray-500 block mb-0.5 text-[10px] uppercase tracking-wider">Chart type</span>
                            <span className="font-semibold text-gray-800 dark:text-gray-200 capitalize flex items-center gap-1.5">
                                {expectedChartType === 'bar' && <BarChart3 size={11} className="text-blue-500" />}
                                {expectedChartType === 'histogram' && <AlignJustify size={11} className="text-blue-500" />}
                                {expectedChartType === 'line' && <LineChartIcon size={11} className="text-green-500" />}
                                {expectedChartType === 'area' && <AreaChartIcon size={11} className="text-purple-500" />}
                                {expectedChartType === 'combo' && <Combine size={11} className="text-blue-500" />}
                                {expectedChartType === 'dual-axis' && <ArrowLeftRight size={11} className="text-indigo-500" />}
                                {expectedChartType === 'pie' && <PieChartIcon size={11} className="text-orange-500" />}
                                {expectedChartType === 'metric' && <Hash size={11} className="text-orange-500" />}
                                {expectedChartType === 'map' && <Map size={11} className="text-blue-500" />}
                                {expectedChartType === 'scatter' && <Crosshair size={11} className="text-purple-500" />}
                                {expectedChartType === 'boxplot' && <BoxSelect size={11} className="text-purple-500" />}
                                {expectedChartType === 'heatmap' && <Grid3x3 size={11} className="text-teal-500" />}
                                {expectedChartType}
                            </span>
                        </div>
                        <div>
                            <span className="text-gray-400 dark:text-gray-500 block mb-0.5 text-[10px] uppercase tracking-wider">
                                {isPie ? 'Slice label' : 'X-axis'}
                            </span>
                            <span className="font-semibold text-gray-800 dark:text-gray-200 font-mono truncate block">{xReq}</span>
                        </div>
                        <div>
                            <span className="text-gray-400 dark:text-gray-500 block mb-0.5 text-[10px] uppercase tracking-wider">
                                {isPie ? 'Slice value' : 'Y-axis'}
                            </span>
                            <span className="font-semibold text-gray-800 dark:text-gray-200 font-mono truncate block">{yReq}</span>
                        </div>
                    </div>

                    {/* Expected Goal */}
                    {goal && (
                        <div className="bg-white dark:bg-black/10 rounded-lg p-2 border border-orange-100 dark:border-orange-500/10">
                            <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-snug">
                                <strong className="text-orange-600 dark:text-orange-400">Goal:</strong> <span dangerouslySetInnerHTML={{ __html: goal.replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-900 dark:text-white font-bold">$1</strong>') }} />
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ─── Chart Controls Toolbar ────────────────────────────
// Groups mirror industry BI tools (Tableau / Looker / Metabase pattern):
//   Comparison | Trend | Distribution | Part-of-Whole | Geo | KPI
const CHART_TYPE_GROUPS = [
    { label: 'Compare', types: [
        { type: 'bar',         icon: BarChart3,       label: 'Bar' },
        { type: 'stacked-bar', icon: Layers,          label: 'Stacked' },
        { type: 'combo',       icon: Combine,         label: 'Combo' },
        { type: 'dual-axis',   icon: ArrowLeftRight,  label: 'Dual Axis' },
    ]},
    { label: 'Trend', types: [
        { type: 'line',        icon: LineChartIcon,   label: 'Line' },
        { type: 'area',        icon: AreaChartIcon,   label: 'Area' },
    ]},
    { label: 'Distribution', types: [
        { type: 'scatter',     icon: Crosshair,       label: 'Scatter' },
        { type: 'histogram',   icon: AlignJustify,    label: 'Hist.' },
        { type: 'boxplot',     icon: BoxSelect,       label: 'Box' },
        { type: 'heatmap',     icon: Grid3x3,         label: 'Heatmap' },
    ]},

    { label: 'Part of Whole', types: [
        { type: 'pie',         icon: PieChartIcon,    label: 'Pie' },
    ]},
    { label: 'Geo', types: [
        { type: 'map',         icon: Map,             label: 'Map' },
    ]},
    { label: 'KPI', types: [
        { type: 'metric',      icon: Hash,            label: 'Metric' },
    ]},
];

// Small on/off toggle switch used inside ChartControls
const ToggleSwitch = ({ checked, onChange, label, icon: Icon }) => (
    <button
        onClick={() => onChange(!checked)}
        className={`group flex items-center gap-1.5 pl-2 pr-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all border ${
            checked
                ? 'bg-orange-50 dark:bg-orange-500/15 border-orange-200 dark:border-orange-500/30 text-orange-600 dark:text-orange-400'
                : 'bg-gray-50 dark:bg-white/[0.04] border-gray-200 dark:border-white/[0.08] text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-white/20 hover:text-gray-700 dark:hover:text-gray-200'
        }`}
    >
        {/* Mini toggle track */}
        <span className={`relative inline-flex h-3.5 w-6 shrink-0 rounded-full border transition-colors ${
            checked ? 'bg-orange-500 border-orange-500' : 'bg-gray-200 dark:bg-white/10 border-gray-300 dark:border-white/10'
        }`}>
            <span className={`absolute top-0.5 h-2 w-2 rounded-full bg-white shadow transition-transform ${
                checked ? 'translate-x-[10px]' : 'translate-x-0.5'
            }`} />
        </span>
        {Icon && <Icon size={11} className="flex-shrink-0" />}
        {label}
    </button>
);

const ChartControls = ({
    chartType, setChartType,
    xAxis, setXAxis,
    yAxis, setYAxis,
    columns,
    showGrid, setShowGrid,
    showLabels, setShowLabels,
    stacked, setStacked,
    orientation, setOrientation,
    legendPosition, setLegendPosition,
    orderBy, setOrderBy,
}) => {
    // Which controls are relevant for this chart type
    const hasXAxis      = !['pie', 'map', 'metric', 'scatter'].includes(chartType);
    const hasYAxis      = !['pie', 'map', 'metric', 'scatter', 'heatmap'].includes(chartType);
    const hasXYScatter  = chartType === 'scatter';
    const hasStack      = ['bar', 'area', 'stacked-bar'].includes(chartType);
    const hasOrient     = ['bar', 'histogram'].includes(chartType);
    const hasLabels     = chartType !== 'map';
    const hasLegend     = !['map', 'metric', 'scatter'].includes(chartType);

    const currentGroup = CHART_TYPE_GROUPS.find(g => g.types.some(t => t.type === chartType));

    return (
        <div className="mt-3 rounded-xl border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-[#111827] overflow-hidden shadow-sm">

            {/* ── Row 1: Chart type picker ── */}
            <div className="px-3 pt-2.5 pb-2 border-b border-gray-100 dark:border-white/[0.06]">
                <div className="flex items-center gap-1 flex-wrap">
                    {CHART_TYPE_GROUPS.map((group, gi) => (
                        <React.Fragment key={group.label}>
                            {/* Group separator (not before first) */}
                            {gi > 0 && (
                                <span className="w-px h-6 bg-gray-200 dark:bg-white/[0.08] mx-0.5 self-center flex-shrink-0" />
                            )}
                            {group.types.map(({ type, icon: Icon, label }) => {
                                const active = chartType === type;
                                return (
                                    <button
                                        key={type}
                                        onClick={() => setChartType(type)}
                                        title={label}
                                        className={`relative flex flex-col items-center justify-center w-[48px] h-[44px] rounded-lg transition-all flex-shrink-0 ${
                                            active
                                                ? 'bg-orange-500 text-white shadow-md shadow-orange-500/25'
                                                : 'text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-gray-700 dark:hover:text-gray-200'
                                        }`}
                                    >
                                        <Icon size={16} strokeWidth={active ? 2.2 : 1.8} />
                                        <span className={`mt-0.5 text-[9px] font-semibold leading-none truncate w-full text-center px-0.5 ${
                                            active ? 'text-white/90' : 'text-gray-400 dark:text-gray-500'
                                        }`}>{label}</span>
                                    </button>
                                );
                            })}
                        </React.Fragment>
                    ))}

                    {/* Active type breadcrumb - far right */}
                    <div className="ml-auto flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500 font-medium pl-2">
                        <span className="text-orange-500 font-bold">
                            {currentGroup && CHART_TYPE_GROUPS.map(g => g.types.find(t => t.type === chartType)).find(Boolean)?.label}
                        </span>
                    </div>
                </div>
            </div>

            {/* ── Row 2: Axis + Display options ── */}
            <div className="px-3 py-2 flex items-start gap-4 flex-wrap">

                {/* Axis selectors */}
                {((hasXAxis || hasXYScatter) && columns && columns.length > 0) && (
                    <div className="flex items-end gap-2 flex-wrap">
                        {hasXAxis && (
                            <div className="min-w-[100px]">
                                <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1">X&nbsp;Axis</p>
                                <CustomSelect
                                    value={xAxis}
                                    onChange={setXAxis}
                                    options={[{ value: '', label: 'Auto' }, ...columns.map(c => ({ value: c, label: c }))]}
                                    buttonClass="text-[11px] py-1.5 px-2.5 bg-gray-50 dark:bg-black/20 border-gray-200 dark:border-white/10 rounded-lg text-gray-600 dark:text-gray-300 min-w-[100px]"
                                />
                            </div>
                        )}
                        {hasYAxis && (
                            <div className="min-w-[100px]">
                                <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1">Y&nbsp;Axis</p>
                                <CustomSelect
                                    value={yAxis}
                                    onChange={setYAxis}
                                    options={[{ value: '', label: 'Auto' }, ...columns.map(c => ({ value: c, label: c }))]}
                                    buttonClass="text-[11px] py-1.5 px-2.5 bg-gray-50 dark:bg-black/20 border-gray-200 dark:border-white/10 rounded-lg text-gray-600 dark:text-gray-300 min-w-[100px]"
                                />
                            </div>
                        )}
                        {hasXYScatter && (
                            <>
                                <div className="min-w-[100px]">
                                    <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1">X&nbsp;(numeric)</p>
                                    <CustomSelect
                                        value={xAxis}
                                        onChange={setXAxis}
                                        options={[{ value: '', label: 'Auto' }, ...columns.map(c => ({ value: c, label: c }))]}
                                        buttonClass="text-[11px] py-1.5 px-2.5 bg-gray-50 dark:bg-black/20 border-gray-200 dark:border-white/10 rounded-lg text-gray-600 dark:text-gray-300 min-w-[100px]"
                                    />
                                </div>
                                <div className="min-w-[100px]">
                                    <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1">Y&nbsp;(numeric)</p>
                                    <CustomSelect
                                        value={yAxis}
                                        onChange={setYAxis}
                                        options={[{ value: '', label: 'Auto' }, ...columns.map(c => ({ value: c, label: c }))]}
                                        buttonClass="text-[11px] py-1.5 px-2.5 bg-gray-50 dark:bg-black/20 border-gray-200 dark:border-white/10 rounded-lg text-gray-600 dark:text-gray-300 min-w-[100px]"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Vertical divider - only when axes are shown */}
                {((hasXAxis || hasXYScatter) && columns && columns.length > 0) && (
                    <div className="self-stretch w-px bg-gray-100 dark:bg-white/[0.06] flex-shrink-0" />
                )}

                {/* Display toggles */}
                <div className="flex items-center gap-1.5 flex-wrap pt-4">
                    {hasLabels && (
                        <ToggleSwitch checked={showLabels} onChange={setShowLabels} label="Labels" />
                    )}
                    {hasLegend && setLegendPosition && (
                        <ToggleSwitch
                            checked={legendPosition !== 'none'}
                            onChange={(v) => setLegendPosition(v ? 'top' : 'none')}
                            label="Legend"
                        />
                    )}
                    {hasStack && (
                        <ToggleSwitch checked={stacked} onChange={setStacked} label="Stack" />
                    )}
                    {hasOrient && (
                        <ToggleSwitch
                            checked={orientation === 'horizontal'}
                            onChange={(v) => setOrientation(v ? 'horizontal' : 'vertical')}
                            label="Flip"
                        />
                    )}
                </div>

                {/* Sort by */}
                {setOrderBy && columns && columns.length > 0 && (
                    <>
                        <div className="self-stretch w-px bg-gray-100 dark:bg-white/[0.06] flex-shrink-0 mt-4" />
                        <div className="flex items-end gap-1.5 pt-0">
                            <div className="flex flex-col">
                                <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1">Sort</p>
                                <CustomSelect
                                    value={orderBy?.column || ''}
                                    onChange={(col) => setOrderBy(col ? { column: col, direction: orderBy?.direction || 'DESC' } : null)}
                                    options={[
                                        { value: '', label: 'Auto' },
                                        ...columns.map(c => ({ value: c, label: c }))
                                    ]}
                                    buttonClass="text-[11px] py-1.5 px-2.5 bg-gray-50 dark:bg-black/20 border-gray-200 dark:border-white/10 rounded-lg text-gray-600 dark:text-gray-300 min-w-[110px]"
                                />
                            </div>
                            {orderBy?.column && (
                                <button
                                    onClick={() => setOrderBy(prev => ({ ...prev, direction: prev.direction === 'ASC' ? 'DESC' : 'ASC' }))}
                                    title="Toggle sort direction"
                                    className="flex items-center gap-1 pl-2 pr-2.5 py-1.5 rounded-lg text-[10px] font-bold border bg-gray-50 dark:bg-black/20 border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:border-orange-300 dark:hover:border-orange-500/40 hover:text-orange-500 dark:hover:text-orange-400 transition-colors"
                                >
                                    {orderBy.direction === 'ASC'
                                        ? <ArrowUp size={11} className="flex-shrink-0" />
                                        : <ArrowDown size={11} className="flex-shrink-0" />}
                                    <span>{orderBy.direction}</span>
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// ─── Tour Components ───────────────────────────────────
const TOUR_STEPS = [
    {
        id: 'welcome',
        title: 'Welcome to Data Visualization!',
        content: 'This guided course will teach you how to turn raw data into insights. Let\'s take a quick tour of the interface.',
        target: null, // Center
        position: 'center'
    },
    {
        id: 'question',
        title: 'Your Mission',
        content: 'Each level presents a specific question. Read this carefully to understand what data you need to find.',
        target: 'tour-question',
        position: 'right'
    },
    {
        id: 'schema',
        title: 'Database Schema',
        content: 'This is your map. It shows available tables and their columns. Reference this to know what data is available.',
        target: 'tour-schema',
        position: 'left'
    },
    {
        id: 'table',
        title: '1. Select Table',
        content: 'Start by choosing the primary table that contains the data you need.',
        target: 'tour-table',
        position: 'right'
    },
    {
        id: 'join',
        title: '2. Join Tables (Optional)',
        content: 'Need data from another table? Use a Join to combine them based on a common column.',
        target: 'tour-join',
        position: 'right'
    },
    {
        id: 'filter',
        title: '3. Filter Data',
        content: 'Narrow down your results. Add specific conditions to include only relevant data rows.',
        target: 'tour-filter',
        position: 'right'
    },
    {
        id: 'metric',
        title: '4. Choose Metric',
        content: 'Decide what to calculate. Count rows, sum values, or find averages.',
        target: 'tour-metric',
        position: 'right'
    },
    {
        id: 'groupby',
        title: '5. Group By',
        content: 'Organize your results into categories. This is crucial for creating charts like Bar or Pie charts.',
        target: 'tour-groupby',
        position: 'right'
    },
    {
        id: 'run',
        title: 'Run Query',
        content: 'Click here to execute your query. The results will appear in the panel below.',
        target: 'tour-run',
        position: 'bottom'
    },
    {
        id: 'results',
        title: 'Results & Charts',
        content: 'Your data appears here. You can switch between Table view and different Chart types to visualize the answer.',
        target: 'tour-results',
        position: 'left'
    }
];

const TourOverlay = ({ stepIndex, onNext, onPrev, onClose }) => {
    const step = TOUR_STEPS[stepIndex];
    const [targetRect, setTargetRect] = useState(null);

    React.useEffect(() => {
        const updateRect = () => {
            if (step.target) {
                const el = document.getElementById(step.target);
                if (el) {
                    const rect = el.getBoundingClientRect();
                    setTargetRect({
                        top: rect.top,
                        left: rect.left,
                        width: rect.width,
                        height: rect.height
                    });
                }
            } else {
                setTargetRect(null);
            }
        };

        // Initial scroll and update
        if (step.target) {
            const el = document.getElementById(step.target);
            if (el) {
                // Use auto to ensure immediate correct positioning, avoids sync issues with smooth scroll
                el.scrollIntoView({ behavior: 'auto', block: 'center' });
            }
        }
        updateRect();

        // Listen for resize and scroll to keep spotlight synced
        window.addEventListener('resize', updateRect);
        window.addEventListener('scroll', updateRect, { capture: true });

        return () => {
            window.removeEventListener('resize', updateRect);
            window.removeEventListener('scroll', updateRect, { capture: true });
        };
    }, [step]);

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[100] overflow-hidden"
            >
                {/* Transparent backdrop that captures clicks to close */}
                <div className="absolute inset-0" onClick={onClose} />

                {/* Spotlight using massive box-shadow */}
                {targetRect && (
                    <motion.div
                        className="absolute bg-transparent ring-4 ring-orange-500 rounded-xl shadow-[0_0_0_9999px_rgba(0,0,0,0.7)] pointer-events-none"
                        initial={false}
                        animate={{
                            top: targetRect.top - 8,
                            left: targetRect.left - 8,
                            width: targetRect.width + 16,
                            height: targetRect.height + 16,
                        }}
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                )}

                {/* Card */}
                <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                    <motion.div
                        key={step.id}
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.3 }}
                        className={`pointer-events-auto bg-white dark:bg-[#1e293b] w-96 rounded-2xl shadow-2xl border border-gray-200 dark:border-white/10 p-6 absolute
                            ${!targetRect ? 'relative' : ''}
                        `}
                        // Improved positioning logic
                        style={targetRect ? {
                            top: step.position === 'bottom'
                                ? Math.min(window.innerHeight - 250, targetRect.top + targetRect.height + 24)
                                : step.position === 'center' ? 'auto' : Math.max(20, Math.min(window.innerHeight - 300, targetRect.top)),
                            left: step.position === 'right'
                                ? Math.min(window.innerWidth - 420, targetRect.left + targetRect.width + 24)
                                : step.position === 'left'
                                    ? Math.max(20, targetRect.left - 408)
                                    : 'auto',
                        } : {}}
                    >
                        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                            <X size={16} />
                        </button>

                        <div className="flex items-center gap-2 mb-3">
                            <span className="bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 text-[10px] font-bold px-2 py-0.5 rounded-full">
                                STEP {stepIndex + 1}/{TOUR_STEPS.length}
                            </span>
                        </div>

                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{step.title}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
                            {step.content}
                        </p>

                        <div className="flex items-center justify-between">
                            <button
                                onClick={onPrev}
                                disabled={stepIndex === 0}
                                className={`text-sm font-medium ${stepIndex === 0 ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`}
                            >
                                Back
                            </button>
                            <button
                                onClick={onNext}
                                className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 rounded-lg text-sm font-bold shadow-lg shadow-orange-500/20 transition-all flex items-center gap-2"
                            >
                                {stepIndex === TOUR_STEPS.length - 1 ? 'Get Started' : 'Next'}
                                <ArrowRight size={14} />
                            </button>
                        </div>
                    </motion.div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
};

// ═══════════════════════════════════════════════════════
// ─── Main Component ────────────────────────────────────
// ═══════════════════════════════════════════════════════
const DataVizTool = ({ moduleId = 'DATA_VIZ_1', DATA_VIZ_CHALLENGES, DATA_VIZ_LEVELS, validateAnswer, moduleMetadata, allModules = [] }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { isReady, error: loadError, loadingProgress, schemas, executeQuery } = useDataViz(moduleId);

    const availableTables = Object.keys(schemas || {}).map(t => ({
        value: t,
        label: t.charAt(0).toUpperCase() + t.slice(1)
    }));

    // Challenge state
    const initialIndex = useMemo(() => {
        const q = new URLSearchParams(window.location.search).get('q');
        const idx = parseInt(q || '1', 10) - 1;
        return (!isNaN(idx) && idx >= 0 && idx < DATA_VIZ_CHALLENGES.length) ? idx : 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    // Intro is shown when ?q=intro or no ?q param at all - URL-driven so refresh works
    const showIntro = !searchParams.get('q') || searchParams.get('q') === 'intro';
    const setShowIntro = (visible) => setSearchParams(visible ? { q: 'intro' } : { q: String(currentIndex + 1) });
    const [completedChallenges, setCompletedChallenges] = useState(new Set());
    // Progress for modules OTHER than the current one - used to show completion in the sidebar
    const [allModulesProgress, setAllModulesProgress] = useState({});

    // Derive a stable case ID from the moduleId prop (e.g. DATA_VIZ_INTRO → data-viz-intro)
    // All DataViz modules share the same exercise_id, differentiated by caseId - mirrors SQL track.
    const exerciseId = 'data-viz';
    const caseId = useMemo(() => moduleId.toLowerCase().replace(/_/g, '-'), [moduleId]);

    // Load persisted progress on mount
    const progressLoaded = useRef(false);
    React.useEffect(() => {
        progressLoaded.current = false;
        let cancelled = false;
        progressService.loadExerciseProgress(exerciseId).then(progress => {
            if (cancelled) return;
            const key = `case_${caseId}`;
            const rawIds = progress.completedChallenges?.[key] || [];
            // Filter out stale/legacy IDs that no longer match current challenges
            // (e.g. numeric IDs from before challenges were renamed to slug strings)
            const validIds = new Set(DATA_VIZ_CHALLENGES.map(c => String(c.id)));
            const ids = rawIds.filter(id => validIds.has(String(id)));
            if (ids.length > 0) setCompletedChallenges(new Set(ids));
            progressLoaded.current = true;
        }).catch(() => { progressLoaded.current = true; });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseId]);

    // Load progress for all other modules in one query - all share exercise_id='data-viz'
    React.useEffect(() => {
        if (!allModules.length) return;
        progressService.loadExerciseProgress('data-viz').then(progress => {
            const map = {};
            allModules.forEach(m => {
                const mid = m.id.toLowerCase().replace(/_/g, '-');
                const key = `case_${mid}`;
                map[m.id] = new Set(progress.completedChallenges?.[key] || []);
            });
            setAllModulesProgress(map);
        }).catch(() => {});
    }, [allModules]);

    // Save progress whenever completedChallenges changes (mirrors SQL module pattern).
    // Gated by progressLoaded so we don't overwrite with an empty set on mount.
    React.useEffect(() => {
        if (!progressLoaded.current) return;
        const ids = Array.from(completedChallenges);
        const isAllDone = ids.length === DATA_VIZ_CHALLENGES.length;
        progressService.saveExerciseProgress(exerciseId, {
            completedCases: isAllDone ? [caseId] : [],
            completedChallenges: { [`case_${caseId}`]: ids },
            totalCases: 1,
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [completedChallenges, exerciseId]);
    const [showHint, setShowHint] = useState(false);
    const [showSolution, setShowSolution] = useState(false);

    // Query builder state
    const [table, setTable] = useState(DATA_VIZ_CHALLENGES[initialIndex]?.answer?.table || 'users');
    const [filters, setFilters] = useState([]);
    const [metrics, setMetrics] = useState([]);
    const [groupBy, setGroupBy] = useState([]);
    const dragGroupByIdx = useRef(null);
    const [join, setJoin] = useState(null);
    const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);

    // ─── Helpers ───
    const highlightSQL = (text) => {
        return text.replace(/(\bSELECT\b|\bFROM\b|\bWHERE\b|\bORDER BY\b|\bGROUP BY\b|\bHAVING\b|\bLIMIT\b|\bOFFSET\b|\bJOIN\b|\bLEFT JOIN\b|\bRIGHT JOIN\b|\bINNER JOIN\b|\bON\b|\bAND\b|\bOR\b|\bNOT\b|\bIN\b|\bLIKE\b|\bBETWEEN\b|\bCASE\b|\bWHEN\b|\bTHEN\b|\bELSE\b|\bEND\b|\bCOUNT\b|\bSUM\b|\bAVG\b|\bMIN\b|\bMAX\b|\bAS\b|\bDISTINCT\b|\bROUND\b|\bCAST\b|'[^']*'|\d+(?:\.\d+)?|\*)/g, (token) => {
            if (/^(SELECT|FROM|WHERE|ORDER BY|GROUP BY|HAVING|LIMIT|OFFSET|JOIN|LEFT JOIN|RIGHT JOIN|INNER JOIN|ON|AND|OR|NOT|IN|LIKE|BETWEEN|CASE|WHEN|THEN|ELSE|END)$/.test(token)) {
                return `<span class="text-blue-600 dark:text-blue-400 font-bold">${token}</span>`;
            }
            if (/^(COUNT|SUM|AVG|MIN|MAX|DISTINCT|ROUND|CAST)$/.test(token)) {
                return `<span class="text-purple-600 dark:text-yellow-200 font-bold">${token}</span>`;
            }
            if (/^AS$/.test(token)) {
                return `<span class="text-blue-500 dark:text-blue-400">${token}</span>`;
            }
            if (/^'[^']*'$/.test(token)) {
                return `<span class="text-orange-600 dark:text-orange-300">${token}</span>`;
            }
            if (/^\d+(?:\.\d+)?$/.test(token)) {
                return `<span class="text-emerald-600 dark:text-emerald-300">${token}</span>`;
            }
            if (token === '*') {
                return `<span class="text-pink-600 dark:text-cyan-300">${token}</span>`;
            }
            return `<span class="text-gray-800 dark:text-gray-300">${token}</span>`;
        });
    };

    // Renders hint text: numbered steps → styled list, bold, inline code pills
    const renderHint = (text) => {
        if (!text) return '';
        const transformInline = (str) => str
            .replace(/\*\*(.+?)\*\*/g, '<strong class="font-bold text-amber-900 dark:text-amber-200">$1</strong>')
            .replace(/\*([^*]+)\*/g, '<em>$1</em>')
            .replace(/`([^`]+)`/g, '<code class="bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-500/30 rounded px-1 font-mono font-semibold text-[10px]">$1</code>');
        const lines = text.split('\n');
        let html = '';
        let inList = false;
        lines.forEach(line => {
            const trimmed = line.trim();
            const stepMatch = trimmed.match(/^(\d+)\.\s+(.*)$/);
            if (stepMatch) {
                if (!inList) { html += '<ol class="space-y-1.5 mt-1.5 list-none p-0">';
                    inList = true; }
                html += `<li class="flex gap-2 leading-relaxed"><span class="flex-shrink-0 w-4 h-4 rounded-full bg-amber-200 dark:bg-amber-500/30 text-amber-800 dark:text-amber-300 text-[9px] font-bold flex items-center justify-center mt-0.5">${stepMatch[1]}</span><span class="text-[11px] text-amber-800 dark:text-amber-300">${transformInline(stepMatch[2])}</span></li>`;
            } else {
                if (inList) { html += '</ol>'; inList = false; }
                if (trimmed) html += `<p class="text-[11px] text-amber-800 dark:text-amber-300 leading-relaxed mb-1">${transformInline(trimmed)}</p>`;
            }
        });
        if (inList) html += '</ol>';
        return html;
    };

    const renderRichText = (text) => {
        if (!text) return null;

        // Remove ALL emojis and unicode symbols aggressively
        let html = text.replace(/[\u{1F000}-\u{1FFFF}]|\u2705|\u26A1|\uD83D\uDC41\uFE0F|\uD83D\uDD11|\uD83C\uDFAF|\uD83D\uDCA1|\uD83D\uDCCB/gu, '').trim();

        // Code blocks - Extract to placeholders FIRST
        const codeBlocks = [];
        html = html.replace(/```(\w+)?\n([\s\S]+?)\n```/g, (match, lang, code) => {
            const placeholder = `<<<CODE_BLOCK_${codeBlocks.length}>>>`;
            codeBlocks.push(code);
            return placeholder;
        });

        // PRESERVE blue callout divs
        const styledDivs = [];
        html = html.replace(/<div style="background: #f0f9ff[^>]*>([\s\S]+?)<\/div>/g, (match, content) => {
            const placeholder = `<<<STYLED_DIV_${styledDivs.length}>>>`;
            styledDivs.push(content.trim());
            return placeholder;
        });

        // Remove other legacy divs if any
        html = html.replace(/<div style="background: linear-gradient[^>]*>[\s\S]*?<\/div>/g, '');
        html = html.replace(/\*\*[📋🌎🔤🔝📊💡🎯⚡✨🔑👁️💭].*?\*\*/g, '');
        html = html.replace(/^- [✅⚡👁️🎯💡🔑📋].*/gm, '');

        const isValidParagraph = (p) => p && p.trim().length > 0;

        const formatBlock = (blockText) => {
            if (blockText.includes('<<<CODE_BLOCK_')) {
                return blockText.replace(/<<<CODE_BLOCK_(\d+)>>>/g, (match, index) => {
                    const code = codeBlocks[parseInt(index)];
                    const coloredCode = highlightSQL(code);
                    return `<pre class="bg-gray-50 dark:bg-[#1e293b] text-gray-800 dark:text-gray-200 p-4 rounded-lg font-mono text-sm overflow-x-auto my-3 border border-gray-200 dark:border-gray-700 shadow-sm whitespace-pre-wrap leading-relaxed">${coloredCode}</pre>`;
                });
            }

            if (blockText.includes('<<<STYLED_DIV_')) {
                return blockText.replace(/<<<STYLED_DIV_(\d+)>>>/g, (match, index) => {
                    const innerContent = styledDivs[parseInt(index)];
                    const processedInner = innerContent.split('\n\n').filter(isValidParagraph).map(formatBlock).join('');
                    return `<div class="p-5 rounded-xl bg-gradient-to-br from-white to-blue-50/50 dark:from-[#1e293b] dark:to-[#0f172a] border border-blue-100 dark:border-blue-900/30 shadow-sm mb-4">${processedInner}</div>`;
                });
            }

            const lines = blockText.split('\n').filter(line => line.trim());

            if (lines.length >= 3 && lines[0].trim().startsWith('|') && lines[1].trim().includes('---')) {
                const headers = lines[0].split('|').filter(cell => cell.trim() !== '').map(cell => cell.trim());
                const rows = lines.slice(2).map(line =>
                    line.split('|').filter(cell => cell.trim() !== '').map(cell => cell.trim())
                );
                return '<div class="overflow-x-auto my-4 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm">' +
                    '<table class="w-full text-left border-collapse bg-white dark:bg-[#0f172a] text-sm">' +
                    '<thead>' +
                    '<tr class="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">' +
                    headers.map(h => `<th class="p-3 font-bold text-gray-700 dark:text-gray-300 uppercase text-xs tracking-wider">${h.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')}</th>`).join('') +
                    '</tr>' +
                    '</thead>' +
                    '<tbody class="divide-y divide-gray-100 dark:divide-gray-800">' +
                    rows.map(row =>
                        `<tr class="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors">` +
                        row.map(cell => {
                            let cellContent = cell;
                            cellContent = cellContent.replace(/\*\*(.+?)\*\*/g, '<strong class="text-gray-900 dark:text-white font-bold">$1</strong>');
                            cellContent = cellContent.replace(/\*([^*]+)\*/g, '<em class="italic">$1</em>');
                            cellContent = cellContent.replace(/\b_([^_]+)_\b/g, '<em class="italic">$1</em>');
                            cellContent = cellContent.replace(/`([^`]+)`/g, (m, code) =>
                                `<code class="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded font-mono text-xs font-semibold border border-gray-200 dark:border-gray-700 text-purple-600 dark:text-purple-300">${code}</code>`
                            );
                            return `<td class="p-3 text-gray-600 dark:text-gray-400 align-top">${cellContent}</td>`;
                        }).join('') +
                        `</tr>`
                    ).join('') +
                    '</tbody>' +
                    '</table>' +
                    '</div>';
            }

            const bulletCount = lines.filter(line => line.trim().startsWith('-') || line.trim().startsWith('•')).length;

            if (bulletCount > 0 && bulletCount === lines.length) {
                return '<ul class="list-disc pl-6 space-y-2 my-3">' +
                    lines.map(item => {
                        let itemText = item.replace(/^[-•]\s*/, '').trim();
                        itemText = itemText.replace(/`([^`]+)`/g, (m, code) =>
                            `<code class="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded font-mono text-xs font-semibold border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200">${highlightSQL(code)}</code>`
                        );
                        itemText = itemText.replace(/\*\*(.+?)\*\*/g, '<strong class="text-gray-900 dark:text-white font-bold">$1</strong>');
                        return `<li class="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">${itemText}</li>`;
                    }).join('') +
                    '</ul>';
            }

            let paraText = blockText;
            const boldOnlyMatch = paraText.match(/^\*\*(.+?)\*\*$/);
            if (boldOnlyMatch) {
                return `<p class="text-gray-900 dark:text-white font-bold text-base my-3">${boldOnlyMatch[1]}</p>`;
            }

            const inlineCodeMap = [];
            paraText = paraText.replace(/`([^`]+)`/g, (m, code) => {
                const placeholder = `<<<CODEBLOCK${inlineCodeMap.length}>>>`;
                inlineCodeMap.push(code);
                return placeholder;
            });

            paraText = paraText.replace(/\*\*(.+?)\*\*/g, '<strong class="text-gray-900 dark:text-white font-bold">$1</strong>');
            paraText = paraText.replace(/\*([^*]+)\*/g, '<em class="italic text-gray-800 dark:text-gray-200">$1</em>');
            paraText = paraText.replace(/\b_([^_]+)_\b/g, '<em class="italic text-gray-800 dark:text-gray-200">$1</em>');

            paraText = paraText.replace(/<<<CODEBLOCK(\d+)>>>/g, (match, index) => {
                const code = inlineCodeMap[parseInt(index)];
                return `<code class="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded font-mono text-xs font-semibold border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200">${highlightSQL(code)}</code>`;
            });

            return `<p class="text-gray-700 dark:text-gray-300 text-sm mb-2 leading-relaxed">${paraText.replace(/\n/g, '<br/>')}</p>`;
        };

        html = html.split('\n\n').filter(isValidParagraph).map(formatBlock).join('');
        return <div dangerouslySetInnerHTML={{ __html: html }} />;
    };
    const [limit, setLimit] = useState(null);

    // Results state
    const [results, setResults] = useState(null);
    const [queryError, setQueryError] = useState(null);
    const [rowLimitWarning, setRowLimitWarning] = useState(null);
    const [executionTime, setExecutionTime] = useState(null);
    const [isRunning, setIsRunning] = useState(false);
    const [validation, setValidation] = useState(null);
    const [mobilePanel, setMobilePanel] = useState('builder');

    // View state
    const [viewMode, setViewMode] = useState('table');
    const [chartType, setChartType] = useState('bar');
    const [xAxis, setXAxis] = useState('');
    const [yAxis, setYAxis] = useState('');

    // Advanced Chart Options
    const [showGrid, setShowGrid] = useState(true);
    const [legendPosition, setLegendPosition] = useState('right'); // top, bottom, right, none
    const [showLabels, setShowLabels] = useState(false);
    const [stacked, setStacked] = useState(false);
    const [orientation, setOrientation] = useState('vertical'); // vertical, horizontal
    const [orderBy, setOrderBy] = useState(null); // { column: string, direction: 'ASC' | 'DESC' } | null
    const [previewMode, setPreviewMode] = useState(null); // 'source', 'filter', 'group', null

    // Client-side sort - applied instantly when orderBy changes, no re-query needed
    const sortedResults = useMemo(() => {
        if (!results || !results.length || !orderBy?.column) return results;
        const col = orderBy.column;
        const dir = orderBy.direction === 'ASC' ? 1 : -1;
        // Parse numeric start from bucket labels like "0-50", "1.2K-1.3K", "3.5M-4M"
        const parseBucketStart = (v) => {
            const m = String(v).match(/^(\d+(?:\.\d+)?)\s*([KkMm]?)/);
            if (!m) return NaN;
            const n = parseFloat(m[1]);
            const suffix = m[2].toUpperCase();
            if (suffix === 'K') return n * 1_000;
            if (suffix === 'M') return n * 1_000_000;
            return n;
        };
        return [...results].sort((a, b) => {
            const av = a[col], bv = b[col];
            if (av === null || av === undefined) return dir;
            if (bv === null || bv === undefined) return -dir;
            if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
            // Bucket labels: both start with a digit → sort numerically by bucket start
            const an = parseBucketStart(av), bn = parseBucketStart(bv);
            if (!isNaN(an) && !isNaN(bn)) return (an - bn) * dir;
            return String(av).localeCompare(String(bv)) * dir;
        });
    }, [results, orderBy]);

    const [menuOpen, setMenuOpen] = useState(false);
    const [menuExpandedModules, setMenuExpandedModules] = useState(() => new Set([moduleId.toLowerCase()]));

    const toggleMenuModule = (id) => {
        setMenuExpandedModules(prev => {
            const next = new Set(prev);
            if (next.has(id)) { next.delete(id); } else { next.add(id); }
            return next;
        });
    };

    // ─── localStorage Cache ───────────────────────────────────────────────────
    const cacheTimerRef = useRef(null);

    // Load cached query state when challenge changes
    React.useEffect(() => {
        const challengeId = DATA_VIZ_CHALLENGES[currentIndex]?.id;
        if (!challengeId) return;
        const cached = localStorage.getItem(`dataviz_cache_v1_${moduleId}_${challengeId}`);
        if (cached) {
            try {
                const d = JSON.parse(cached);
                if (d.table != null)     setTable(d.table);
                if (d.filters != null)   setFilters(d.filters);
                if (d.metrics != null)   setMetrics(d.metrics);
                if (d.groupBy != null)   setGroupBy(d.groupBy);
                if (d.join !== undefined) setJoin(d.join);
                if (d.chartType)         setChartType(d.chartType);
                if (d.xAxis != null)     setXAxis(d.xAxis);
                if (d.yAxis != null)     setYAxis(d.yAxis);
                if (d.showGrid != null)      setShowGrid(d.showGrid);
                if (d.showLabels != null)     setShowLabels(d.showLabels);
                if (d.legendPosition != null) setLegendPosition(d.legendPosition);
                if (d.stacked != null)        setStacked(d.stacked);
                if (d.orientation)            setOrientation(d.orientation);
                if (d.orderBy !== undefined)  setOrderBy(d.orderBy);
            } catch (e) { /* ignore corrupt cache */ }
        }
    }, [currentIndex]); // eslint-disable-line react-hooks/exhaustive-deps

    // Record challenge start time when challenge changes
    React.useEffect(() => {
        const challenge = DATA_VIZ_CHALLENGES[currentIndex];
        if (!challenge) return;
        progressService.startChallenge(exerciseId, caseId, challenge.id);
    }, [currentIndex, exerciseId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Debounced save - fires 600ms after any query/chart state change
    React.useEffect(() => {
        const challengeId = DATA_VIZ_CHALLENGES[currentIndex]?.id;
        if (!challengeId) return;
        clearTimeout(cacheTimerRef.current);
        cacheTimerRef.current = setTimeout(() => {
            localStorage.setItem(
                `dataviz_cache_v1_${moduleId}_${challengeId}`,
                JSON.stringify({ table, filters, metrics, groupBy, join, chartType, xAxis, yAxis, showGrid, showLabels, legendPosition, stacked, orientation, orderBy })
            );
        }, 600);
        return () => clearTimeout(cacheTimerRef.current);
    }, [currentIndex, table, filters, metrics, groupBy, join, chartType, xAxis, yAxis, showGrid, showLabels, legendPosition, stacked, orientation, orderBy]); // eslint-disable-line react-hooks/exhaustive-deps

    // Tour State
    const [tourIndex, setTourIndex] = useState(-1);

    React.useEffect(() => {
        // Check if tour has been seen - only show on actual questions, not the intro screen
        const seen = localStorage.getItem('datahelix_tour_seen');
        if (!seen && isReady && !showIntro) {
            setTourIndex(0);
        }
    }, [isReady, showIntro]);

    const handleTourNext = () => {
        if (tourIndex < TOUR_STEPS.length - 1) {
            setTourIndex(prev => prev + 1);
        } else {
            completeTour();
        }
    };

    const handleTourPrev = () => {
        if (tourIndex > 0) {
            setTourIndex(prev => prev - 1);
        }
    };

    const completeTour = () => {
        setTourIndex(-1);
        localStorage.setItem('datahelix_tour_seen', 'true');
    };

    const startTour = () => {
        setMenuOpen(false);
        setTourIndex(0);
    };

    const challenge = DATA_VIZ_CHALLENGES[currentIndex];
    const level = DATA_VIZ_LEVELS.find(l => l.id === challenge.level);

    // Cross-module navigation helpers
    const currentModuleIndex = allModules.findIndex(m => m.id === moduleId.toLowerCase());
    const nextModule = allModules[currentModuleIndex + 1] || null;
    const prevModule = allModules[currentModuleIndex - 1] || null;
    const isLastChallenge = currentIndex === DATA_VIZ_CHALLENGES.length - 1;

    // Derive columns
    const columns = useMemo(() => getAvailableColumns(table, join, schemas), [table, join, schemas]);

    // Build SQL
    const queryState = useMemo(() => ({
        table, join, filters, metrics, groupBy, orderBy, limit
    }), [table, join, filters, metrics, groupBy, orderBy, limit]);

    const { displaySQL, execSQL } = useMemo(() => buildSQL(queryState, schemas), [queryState, schemas]);

    const resetQueryState = useCallback(() => {
        setFilters([]);
        setGroupBy([]);
        setJoin(null);
        setLimit(null);
        setMetrics([]);
        setOrderBy(null);
        setResults(null);
        setQueryError(null);
        setValidation(null);
        setShowHint(false);
        setShowSolution(false);
        setViewMode('table');
    }, []);

    const applySolution = useCallback(async () => {
        const challenge = DATA_VIZ_CHALLENGES[currentIndex];
        if (!challenge?.answer || !isReady) return;
        const { answer } = challenge;

        // Apply solution to query builder state
        const solTable    = answer.table;
        const solMetrics  = answer.metrics
            ? answer.metrics
            : answer.aggregation ? [answer.aggregation] : [];
        const solGroupBy  = answer.groupBy  || [];
        const solFilters  = answer.filters  || [];
        const solJoin     = answer.join     || null;

        setTable(solTable);
        setMetrics(solMetrics);
        setGroupBy(solGroupBy);
        setFilters(solFilters);
        setJoin(solJoin);
        setChartType(challenge.expectedChartType || 'bar');
        setXAxis('');
        setYAxis('');
        setShowSolution(false);
        setShowHint(false);

        // Build SQL directly from solution state and run immediately
        const solQueryState = { table: solTable, join: solJoin, filters: solFilters, metrics: solMetrics, groupBy: solGroupBy, orderBy: null, limit: null };
        const { execSQL: solSQL } = buildSQL(solQueryState, schemas);

        setIsRunning(true);
        setQueryError(null);
        setRowLimitWarning(null);
        setValidation(null);
        setShowSuccessOverlay(false);
        try {
            const { rows, executionTime: time } = await executeQuery(solSQL);
            const normalizedRows = rows.map(row => {
                const out = {};
                for (const key of Object.keys(row)) {
                    const v = row[key];
                    out[key] = typeof v === 'bigint' ? Number(v) : v;
                }
                return out;
            });
            setResults(normalizedRows);
            setExecutionTime(time);
            setViewMode(normalizedRows.length > 0 ? 'chart' : 'table');
        } catch (err) {
            setQueryError(err.message);
            setResults(null);
        } finally {
            setIsRunning(false);
        }
    }, [DATA_VIZ_CHALLENGES, currentIndex, isReady, executeQuery, schemas]);

    // Sync State from URL
    const currentQ = searchParams.get('q');
    React.useEffect(() => {
        if (!currentQ || currentQ === 'intro') return; // intro has its own screen; nothing to sync
        const idx = parseInt(currentQ, 10) - 1;
        const validIndex = (!isNaN(idx) && idx >= 0 && idx < DATA_VIZ_CHALLENGES.length) ? idx : 0;

        if (validIndex !== currentIndex) {
            // Update index and reset all query state
            setCurrentIndex(validIndex);
            setShowSuccessOverlay(false);

            const ch = DATA_VIZ_CHALLENGES[validIndex];
            setTable(ch.answer.table);
            setChartType(ch.expectedChartType);
            setXAxis('');
            setYAxis('');

            // Reset advanced options
            setShowGrid(true);
            setLegendPosition('top');
            setShowLabels(false);
            setStacked(false);
            setOrientation('vertical');

            resetQueryState();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentQ, currentIndex, resetQueryState]);

    const handleNavigate = useCallback((index) => {
        setSearchParams({ q: (index + 1).toString() });
        setMobilePanel('builder'); // Return to Builder on mobile when moving to a new question
    }, [setSearchParams]);

    const handleTableChange = useCallback((newTable) => {
        setTable(newTable);
        setFilters([]);
        setGroupBy([]);
        setResults(null);
        setQueryError(null);
        setValidation(null);
    }, []);

    const handleAddFilter = useCallback(() => {
        setFilters(prev => [...prev, { column: '', operator: '=', value: '' }]);
    }, []);
    const handleUpdateFilter = useCallback((index, updated) => {
        setFilters(prev => prev.map((f, i) => i === index ? updated : f));
    }, []);
    const handleRemoveFilter = useCallback((index) => {
        setFilters(prev => prev.filter((_, i) => i !== index));
    }, []);

    const handleAddMetric = useCallback(() => {
        setMetrics(prev => [...prev, { fn: null, column: null }]);
    }, []);
    const handleUpdateMetric = useCallback((index, updated) => {
        setMetrics(prev => prev.map((m, i) => i === index ? updated : m));
    }, []);
    const handleRemoveMetric = useCallback((index) => {
        setMetrics(prev => prev.filter((_, i) => i !== index));
    }, []);

    const handleToggleGroupBy = useCallback((colName) => {
        setGroupBy(prev => {
            const existing = prev.find(g => g.column === colName);
            if (existing) return prev.filter(g => g.column !== colName);
            return [...prev, { column: colName }];
        });
    }, []);

    const handleGranularityChange = useCallback((colName, granularity) => {
        setGroupBy(prev => prev.map(g =>
            g.column === colName ? { ...g, granularity } : g
        ));
    }, []);

    const handleBucketChange = useCallback((colName, size) => {
        setGroupBy(prev => prev.map(g =>
            g.column === colName ? { ...g, bucket: size ? { size: Number(size) } : undefined } : g
        ));
    }, []);

    const handleFetchDistinctValues = useCallback(async (columnName) => {
        if (!isReady) return [];
        try {
            let sourceTable = table;

            // If there's a join, check if the column belongs to the joined table
            if (join && join.table && schemas[join.table]) {
                const inJoin = schemas[join.table].some(c => c.name === columnName);
                const inMain = schemas[table] && schemas[table].some(c => c.name === columnName);
                if (inJoin && !inMain) {
                    sourceTable = join.table;
                }
            }

            // Safe distinct query - cast to VARCHAR so booleans/numbers return as strings the UI can render
            const query = `SELECT DISTINCT CAST("${columnName}" AS VARCHAR) AS "${columnName}" FROM "${sourceTable}" WHERE "${columnName}" IS NOT NULL ORDER BY 1 LIMIT 50`;
            const { rows } = await executeQuery(query);
            return rows.map(r => r[columnName]).filter(v => v !== null && v !== undefined);
        } catch (e) {
            console.error("Failed to fetch distinct values", e);
            return [];
        }
    }, [isReady, executeQuery, table, join, schemas]);

    const handleVisualize = useCallback(async () => {
        if (!isReady) return;
        setIsRunning(true);
        setQueryError(null);
        setRowLimitWarning(null);
        setValidation(null);
        setShowSuccessOverlay(false);

        try {
            setPreviewMode(null); // Clear preview mode
            const { rows, executionTime: time } = await executeQuery(execSQL);

            // Post-process: coerce BigInt values (returned by DuckDB for SUM/COUNT on integer columns)
            // to regular JS numbers so chart pivot logic can detect them with typeof === 'number'
            const normalizedRows = rows.map(row => {
                const out = {};
                for (const key of Object.keys(row)) {
                    const v = row[key];
                    out[key] = typeof v === 'bigint' ? Number(v) : v;
                }
                return out;
            });

            // Post-process: replace bucket column raw numbers with human-readable ranges
            const formatBucketValue = (n, size) => {
                const lo = Number(n);
                const hi = lo + size;
                const fmt = (v) =>
                    v >= 1_000_000 ? `${+(v / 1_000_000).toFixed(1)}M`
                    : v >= 1_000   ? `${+(v / 1_000).toFixed(1)}K`
                    : String(v);
                return `${fmt(lo)}-${fmt(hi)}`;
            };
            const hasBuckets = groupBy && groupBy.some(g => g.bucket?.size);
            const processedRows = hasBuckets
                ? normalizedRows.map(row => {
                    const out = { ...row };
                    groupBy.forEach(g => {
                        if (g.bucket?.size) {
                            const alias = `${g.column}_bucket`;
                            if (out[alias] !== undefined && out[alias] !== null) {
                                out[alias] = formatBucketValue(out[alias], Number(g.bucket.size));
                            }
                        }
                    });
                    return out;
                })
                : normalizedRows;

            const ROW_LIMIT = 10_000;
            const cappedRows = processedRows.length > ROW_LIMIT ? processedRows.slice(0, ROW_LIMIT) : processedRows;
            if (processedRows.length > ROW_LIMIT) {
                setRowLimitWarning(`Showing first ${ROW_LIMIT.toLocaleString()} of ${processedRows.length.toLocaleString()} rows. Add a Limit or refine your filters to reduce the result.`);
            } else {
                setRowLimitWarning(null);
            }
            setResults(cappedRows);
            setExecutionTime(time);

            posthog.capture('viz_query_executed', {
                module_id: moduleId,
                rows_returned: cappedRows.length,
                chart_type: chartType,
            });

            // Auto-switch to chart if we have data
            if (cappedRows.length > 0) {
                setViewMode('chart');
            } else {
                setViewMode('table');
            }
        } catch (err) {
            setQueryError(err.message.replace(/vartchar/g, 'text').replace(/viz1_/g, '').replace(/Binder Error: ?/i, ''));
            setResults(null);
        } finally {
            setIsRunning(false);
        }
    }, [isReady, execSQL, executeQuery, groupBy, chartType, moduleId]);

    const handleSubmit = useCallback(() => {
        if (!results) {
            setValidation({ passed: false, feedback: 'Please visualize your data first.' });
            return;
        }

        const result = validateAnswer({ table, join, filters, metrics, groupBy, limit, chartType }, challenge);
        setValidation({
            ...result,
            feedback: result.feedback ? result.feedback.replace(/viz1_/g, '') : result.feedback
        });

        // On mobile, switch to results panel so user sees the chart + feedback
        setMobilePanel('results');

        if (result.passed) {
            posthog.capture('challenge_completed', {
                challenge_id: challenge.id,
                module_id: moduleId,
                case_id: caseId,
                chart_type: chartType,
            });
            setCompletedChallenges(prev => {
                const next = new Set([...prev, challenge.id]);
                // Persist to Supabase - fire and forget
                progressService.completeChallenge(exerciseId, caseId, challenge.id);
                if (next.size === DATA_VIZ_CHALLENGES.length) {
                    progressService.completeCase(exerciseId, caseId);
                }
                return next;
            });
            setShowSuccessOverlay(true);
        }
    }, [results, table, join, filters, metrics, groupBy, limit, chartType, challenge, exerciseId, caseId, validateAnswer, moduleId, DATA_VIZ_CHALLENGES.length]);

    const handlePreview = useCallback(async (mode) => {
        if (!isReady) return;

        // Toggle off if clicking the same mode
        if (previewMode === mode) {
            setPreviewMode(null);
            setResults(null);
            // If we have a valid query built, maybe go back to chart? 
            // For now, let's just clear results to be safe or revert to previous state?
            // Actually, the user just wants to "close" it.
            return;
        }

        setIsRunning(true);
        setQueryError(null);
        setValidation(null);
        setPreviewMode(mode);

        try {
            let previewState = { table, join: null, filters: [], aggregation: null, groupBy: [] };

            switch (mode) {
                case 'source':
                    // Just table. Join is null.
                    break;
                case 'join':
                    previewState.join = join;
                    break;
                case 'filter':
                    previewState.join = join;
                    previewState.filters = filters;
                    break;
                case 'group':
                    previewState.join = join;
                    previewState.filters = filters;
                    previewState.groupBy = groupBy;
                    // Use current metrics if grouping is active
                    if (groupBy.length > 0 && (!metrics || metrics.length === 0)) {
                        // If grouping but no metrics, let buildSQL handle it (selects group cols)
                    }
                    previewState.metrics = metrics;
                    break;
                default:
                    break;
            }

            // Generate SQL for preview
            const { execSQL: previewSQL } = buildSQL(previewState, schemas);

            // If source/filter, force LIMIT 100 to avoid huge dumps. 
            // If group, use the potentially aggregated query (which handles its own limit naturally or we force one)
            let finalSQL = previewSQL;
            if (!previewSQL.toUpperCase().includes('LIMIT') && (mode === 'source' || mode === 'filter')) {
                finalSQL = previewSQL.replace(';', '') + ' LIMIT 100;';
            }

            const { rows, executionTime: time } = await executeQuery(finalSQL);
            setResults(rows);
            setExecutionTime(time);
            setViewMode('table'); // Force table view for preview
        } catch (err) {
            setQueryError(err.message.replace(/viz1_/g, '').replace(/Binder Error: ?/i, ''));
            setResults(null);
        } finally {
            setIsRunning(false);
        }
    }, [isReady, executeQuery, table, join, filters, metrics, groupBy, schemas, previewMode]);



    // ─── Loading ────────────────────────────────
    if (!isReady) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-[#0a0e27] flex items-center justify-center">
                <div className="text-center">
                    <Loader2 size={40} className="text-orange-500 animate-spin mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Loading Visualizing Data</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{loadingProgress || 'Preparing your workspace...'}</p>
                    {loadError && (
                        <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 max-w-md mx-auto">
                            <p className="text-sm text-red-700 dark:text-red-300">{loadError}</p>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Filter completedChallenges to only count IDs that exist in the current challenge list
    // This prevents ghost/legacy IDs inflating the count above 100%
    const validChallengeIds = new Set(DATA_VIZ_CHALLENGES.map(c => String(c.id)));
    const validCompletedCount = [...completedChallenges].filter(id => validChallengeIds.has(String(id))).length;
    const completionPercentage = Math.round((validCompletedCount / DATA_VIZ_CHALLENGES.length) * 100);

    const introScreen = (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden fade-in font-inter">
            {/* Full-width hero header - mirrors ExerciseHeader */}
            <div className="relative overflow-hidden bg-gradient-to-r from-primary-dark to-primary px-6 py-8 flex-shrink-0">
                <ParticleBackground particleCount={35} maxDist={95} speed={0.18} />
                <div className="relative z-10 max-w-6xl mx-auto">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-500/20 text-orange-300 text-sm font-bold mb-4 shadow-sm border border-orange-500/20">
                        <Layers size={16} /> {moduleMetadata?.badge || 'Module'}
                    </div>
                    <h2 className="text-3xl md:text-4xl font-extrabold text-white leading-tight">
                        {moduleMetadata?.title || 'Data Visualization Module'}
                    </h2>
                    <p className="mt-4 text-gray-200 text-lg leading-relaxed max-w-3xl">
                        {moduleMetadata?.scenario || 'Investigate the data by writing queries and visualizing the results.'}
                    </p>
                </div>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto bg-white dark:bg-[#162032]">
                <div className="max-w-6xl mx-auto px-6 py-8">
                    <div className="prose prose-slate dark:prose-invert max-w-none text-gray-700 dark:text-gray-300">
                        <p>
                                For each question, use the Query Builder to shape the data, then pick the chart type that best communicates the answer. The hints explain <em>why</em> a particular chart works - not just how to build it.
                            </p>
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-l-4 border-blue-500 p-6 my-8 rounded-r-2xl flex flex-col items-start text-left shadow-sm">
                            <h3 className="text-xl font-bold text-blue-900 dark:text-blue-100 m-0 flex items-center gap-2">
                                <Target size={20} className="text-blue-600 dark:text-blue-400" /> Your Mission
                            </h3>
                            <p className="text-blue-800 dark:text-blue-200 mt-3 mb-0">
                                {moduleMetadata?.introMission || 'Answer the 10 questions step by step to identify the root cause of the problem.'}
                            </p>
                        </div>
                    </div>

                    {/* Start button - centred, matching SQL intro style */}
                    <div className="text-center py-8">
                        <button
                            onClick={() => setSearchParams({ q: '1' })}
                            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#f97316] to-[#ea580c] text-white text-lg font-bold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all"
                        >
                            {moduleMetadata?.ctaLabel || 'Start Challenges'} <ChevronRight size={20} />
                        </button>
                        <p className="mt-4 text-sm text-gray-400 flex items-center justify-center gap-1.5">
                            <Clock size={14} /> ~30 Mins
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    // ─── Layout ─────────────────────────────────
    return (
        <div className="h-screen flex flex-col bg-gray-50 dark:bg-[#0a0e27] overflow-hidden">

            {tourIndex >= 0 && (
                <TourOverlay
                    stepIndex={tourIndex}
                    onNext={handleTourNext}
                    onPrev={handleTourPrev}
                    onClose={completeTour}
                />
            )}

            {/* ═══ Questions Menu Modal ═══ */}
            {menuOpen && (
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40"
                        onClick={() => setMenuOpen(false)}
                    />
                    {/* Modal */}
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                        <div className="bg-white dark:bg-[#162032] rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden pointer-events-auto flex flex-col border border-gray-200 dark:border-gray-700">
                            {/* Modal Header */}
                            <div className="bg-gray-100 dark:bg-gradient-to-r dark:from-[#1e3a5f] dark:to-[#0a1220] p-6 text-gray-900 dark:text-white flex-shrink-0 border-b border-gray-200 dark:border-gray-700">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-2xl font-bold bg-gradient-to-r from-amber-300 via-orange-500 to-red-500 text-transparent bg-clip-text inline-block">
                                        Visualizing Data
                                    </h2>
                                    <button
                                        onClick={() => setMenuOpen(false)}
                                        className="text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white transition-colors text-2xl leading-none w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-200 dark:hover:bg-white/5"
                                    >
                                        ✕
                                    </button>
                                </div>
                                {/* Progress Summary */}
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="flex items-center gap-2 text-gray-600 dark:text-gray-300 font-medium">
                                            <Trophy size={14} className="text-amber-500" />
                                            Progress
                                        </span>
                                        <span className="font-bold text-gray-900 dark:text-white">{completionPercentage}%</span>
                                    </div>
                                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                                        <div
                                            className="bg-gradient-to-r from-orange-500 to-red-500 h-full rounded-full transition-all duration-500"
                                            style={{ width: `${completionPercentage}%` }}
                                        />
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {validCompletedCount} of {DATA_VIZ_CHALLENGES.length} questions completed
                                    </div>
                                </div>
                            </div>

                            {/* Module + Questions List */}
                            <nav className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
                                {allModules.map((mod, modIdx) => {
                                    const isCurrentModule = mod.id === moduleId.toLowerCase();
                                    const isExpanded = menuExpandedModules.has(mod.id);
                                    const modChallenges = mod.challenges || [];
                                    return (
                                        <div key={mod.id} className="space-y-1">
                                            {/* Module Row */}
                                            <div
                                                className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all ${
                                                    isCurrentModule
                                                        ? 'bg-orange-50 border-orange-200 dark:bg-orange-500/10 dark:border-orange-500/30'
                                                        : 'bg-white border-gray-100 hover:bg-gray-50 hover:border-gray-200 dark:bg-[#0a1220] dark:border-gray-800 dark:hover:bg-[#1e293b] dark:hover:border-gray-700'
                                                }`}
                                            >
                                                {/* Clickable title area */}
                                                <button
                                                    onClick={() => {
                                                        if (!isCurrentModule) {
                                                            navigate(mod.route);
                                                            setMenuOpen(false);
                                                        } else {
                                                            setShowIntro(true);
                                                            setMenuOpen(false);
                                                        }
                                                    }}
                                                    className="flex items-center gap-3 flex-1 text-left min-w-0"
                                                >
                                                    <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm transition-colors ${
                                                        isCurrentModule
                                                            ? 'bg-gradient-to-br from-orange-500 to-red-500 text-white shadow-lg shadow-orange-500/20'
                                                            : 'bg-gray-100 text-gray-500 border border-gray-200 dark:bg-gray-800 dark:text-gray-500 dark:border-gray-700'
                                                    }`}>
                                                        {(() => { const progressSet = isCurrentModule ? completedChallenges : allModulesProgress[mod.id]; const total = isCurrentModule ? DATA_VIZ_CHALLENGES.length : (mod.challenges?.length ?? 0); const done = (progressSet?.size ?? 0) >= total && total > 0; return done ? <CheckCircle2 size={14} className={isCurrentModule ? 'text-white' : 'text-gray-400 dark:text-gray-500'} /> : modIdx + 1; })()}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className={`font-bold text-sm truncate ${
                                                            isCurrentModule ? 'text-orange-900 dark:text-white' : 'text-gray-700 dark:text-gray-400'
                                                        }`}>
                                                            {mod.title.includes(':') ? mod.title.split(':')[1].trim() : mod.title}
                                                        </div>
                                                        <div className={`mt-1 inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                                                            isCurrentModule
                                                                ? 'bg-orange-100 text-orange-800 border border-orange-200 dark:bg-orange-500/20 dark:text-orange-400 dark:border-orange-500/30'
                                                                : 'bg-gray-100 text-gray-500 border border-gray-200 dark:bg-gray-800 dark:text-gray-500 dark:border-gray-700'
                                                        }`}>
                                                            {mod.category}
                                                        </div>
                                                    </div>
                                                </button>
                                                {/* Expand / Collapse chevron */}
                                                <button
                                                    onClick={() => toggleMenuModule(mod.id)}
                                                    className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-md transition-colors ${
                                                        isCurrentModule
                                                            ? 'text-orange-500 hover:bg-orange-100 dark:hover:bg-orange-500/20'
                                                            : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-300'
                                                    }`}
                                                    title={isExpanded ? 'Collapse' : 'Expand'}
                                                >
                                                    <ChevronDown
                                                        size={14}
                                                        className={`transition-transform duration-200 ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
                                                    />
                                                </button>
                                            </div>

                                            {/* Questions list (shown when expanded) */}
                                            {isExpanded && modChallenges.length > 0 && (
                                                <div className="ml-5 pl-3 border-l-2 border-orange-200 dark:border-orange-500/20 space-y-1 pt-1 pb-1">
                                                    {modChallenges.map((ch, chIdx) => {
                                                        const isCurrent = isCurrentModule && DATA_VIZ_CHALLENGES.indexOf(ch) === currentIndex;
                                                        const isCompleted = isCurrentModule
                                                            ? completedChallenges.has(ch.id)
                                                            : (allModulesProgress[mod.id]?.has(ch.id) ?? false);
                                                        return (
                                                            <button
                                                                key={ch.id}
                                                                onClick={() => {
                                                                    if (isCurrentModule) {
                                                                        handleNavigate(DATA_VIZ_CHALLENGES.indexOf(ch));
                                                                    } else {
                                                                        navigate(`${mod.route}?q=${chIdx + 1}`);
                                                                    }
                                                                    setMenuOpen(false);
                                                                }}
                                                                className={`w-full text-left flex items-center justify-between gap-3 py-2 px-3 rounded-lg transition-colors ${
                                                                    isCurrent
                                                                        ? 'bg-orange-50 text-orange-700 border border-orange-100 dark:bg-orange-500/10 dark:text-orange-400 dark:border-orange-500/20'
                                                                        : isCompleted
                                                                            ? 'text-gray-600 dark:text-gray-400 hover:bg-green-50 dark:hover:bg-green-500/5'
                                                                            : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-300'
                                                                }`}
                                                            >
                                                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                                                    <span className={`flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold ${
                                                                        isCurrent
                                                                            ? 'bg-gradient-to-br from-orange-500 to-red-500 text-white shadow'
                                                                            : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500'
                                                                    }`}>
                                                                        {chIdx + 1}
                                                                    </span>
                                                                    <div className="flex flex-col min-w-0">
                                                                        <span className="text-xs truncate font-medium">{ch.title.replace(/^Q\d+[:.\s]*/, '')}</span>
                                                                        {ch.expectedChartType && (
                                                                            <span className="text-[9px] font-medium text-gray-400 dark:text-gray-600 truncate leading-tight">
                                                                                {({'bar':'Bar','stacked-bar':'Stacked Bar','line':'Line','area':'Area','pie':'Pie','histogram':'Histogram','heatmap':'Heatmap','map':'Map','boxplot':'Box Plot','scatter':'Scatter','dual-axis':'Dual Axis','combo':'Combo','metric':'Metric','treemap':'Treemap'})[ch.expectedChartType] || ch.expectedChartType}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                {isCompleted && (
                                                                    <CheckCircle2 size={12} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </nav>

                            {/* Modal Footer */}
                            <div className="p-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#0a1220]/50 flex-shrink-0">
                                <div className="text-xs text-gray-500 text-center flex items-center justify-center gap-2">
                                    <Lightbulb size={12} className="text-amber-500" />
                                    <span>Your progress is automatically saved</span>
                                </div>
                                <div className="mt-3 border-t border-gray-200 dark:border-white/5 pt-3">
                                    <button
                                        onClick={startTour}
                                        className="text-xs font-bold text-orange-500 hover:text-orange-600 flex items-center justify-center gap-2 mx-auto"
                                    >
                                        <Play size={10} /> Restart Tour
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ═══ Header (FIXED) ═══ */}
            <header className="flex-shrink-0 bg-white dark:bg-[#1e293b] border-b border-gray-200 dark:border-white/10 px-4 py-2.5 flex items-center justify-between z-40 relative overflow-hidden shadow-sm">
                <ParticleBackground particleCount={20} maxDist={75} speed={0.12} />

                {/* Left: Logo + Title breadcrumb */}
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Link to="/" className="flex items-center gap-2 group flex-shrink-0">
                        <img src={logo} alt="DataHelix" className="h-6 w-auto group-hover:rotate-[360deg] transition-transform duration-500 invert dark:invert-0" />
                        <span className="font-bold text-gray-900 dark:text-white hidden sm:inline tracking-tight">DataHelix</span>
                    </Link>

                    <div className="h-5 w-px bg-gray-200 dark:bg-white/10 hidden sm:block flex-shrink-0"></div>

                    <div className="min-w-0 flex items-center gap-2">
                        <Link
                            to="/exercises"
                            className="text-sm hidden sm:block font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors flex-shrink-0"
                        >
                            Courses
                        </Link>
                        <ChevronRight size={14} className="text-gray-400 hidden sm:block flex-shrink-0" />
                        <Link
                            to="/exercises/data-viz"
                            className="text-sm hidden sm:block font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors flex-shrink-0"
                        >
                            Visualizing Data
                        </Link>
                        <ChevronRight size={14} className="text-gray-400 hidden lg:block flex-shrink-0" />
                        <p className="text-xs truncate font-bold text-gray-900 dark:text-white hidden md:block">
                            {moduleMetadata?.title || 'Visualizing Data'}
                        </p>
                    </div>
                </div>

                {/* Center: Prev / Questions / Next */}
                <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                        onClick={() => {
                            if (showIntro) {
                                if (prevModule) {
                                    const lastQ = prevModule.challenges?.length ?? 1;
                                    navigate(`${prevModule.route}?q=${lastQ}`);
                                }
                            } else if (currentIndex === 0) {
                                setSearchParams({ q: 'intro' });
                            } else {
                                handleNavigate(currentIndex - 1);
                            }
                        }}
                        disabled={showIntro && !prevModule}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all border ${
                            showIntro && !prevModule
                                ? 'bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed dark:bg-gray-900 dark:border-gray-800 dark:text-gray-700'
                                : 'bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 border-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-300 dark:hover:text-white dark:border-gray-700'
                        }`}
                        title={showIntro ? (prevModule ? `Back to: ${prevModule.title}` : 'No previous module') : currentIndex === 0 ? 'Back to Introduction' : 'Previous Question'}
                    >
                        <ArrowLeft size={12} />
                    </button>

                    <button
                        onClick={() => setMenuOpen(true)}
                        className="flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg hover:shadow-lg hover:shadow-orange-500/20 transition-all font-bold text-xs tracking-wide uppercase leading-none h-8"
                    >
                        <Menu size={12} className="relative -top-[0.5px]" />
                        <span className="relative top-[0.5px]">Questions</span>
                    </button>

                    <button
                        onClick={() => {
                            if (!isLastChallenge) { handleNavigate(currentIndex + 1); }
                            else if (nextModule) { navigate(nextModule.route); }
                        }}
                        disabled={isLastChallenge && !nextModule}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all ${(!isLastChallenge || nextModule)
                            ? 'bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 border border-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-300 dark:hover:text-white dark:border-gray-700'
                            : 'bg-gray-50 border border-gray-100 text-gray-300 cursor-not-allowed dark:bg-gray-900 dark:border-gray-800 dark:text-gray-700'
                            }`}
                        title={isLastChallenge && nextModule ? `Next: ${nextModule.title}` : 'Next Question'}
                    >
                        <ArrowRight size={12} />
                    </button>
                </div>

                {/* Right: Tour + Progress + Theme */}
                <div className="flex items-center gap-3 flex-1 justify-end">
                    {!showIntro && (
                        <button onClick={startTour} className="hidden sm:flex text-[10px] font-bold text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-500/10 px-2 py-1 rounded border border-orange-200 dark:border-orange-500/20 transition-colors gap-1 items-center">
                            <HelpCircle size={12} /> Tour
                        </button>
                    )}

                    <div className="hidden md:flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-gray-100 dark:bg-white/10 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-orange-500 to-red-500 transition-all duration-500 ease-out"
                                style={{ width: `${(validCompletedCount / DATA_VIZ_CHALLENGES.length) * 100}%` }}
                            />
                        </div>
                        <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400">{validCompletedCount}/{DATA_VIZ_CHALLENGES.length}</span>
                    </div>

                    <div className="h-5 w-px bg-gray-200 dark:bg-white/10"></div>

                    <ThemeToggle />
                </div>
            </header>

            {/* ═══ Main Content (SCROLLABLE SPLIT PANELS) ═══ */}
            {/* ═══ Mobile Panel Toggle (visible only on small screens) ═══ */}
            {!showIntro && (
                <div className="flex lg:hidden flex-shrink-0 border-b border-gray-200 dark:border-white/10 bg-white dark:bg-[#1e293b]">
                    <button
                        onClick={() => setMobilePanel('builder')}
                        className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wide transition-all ${
                            mobilePanel === 'builder'
                                ? 'text-orange-500 border-b-2 border-orange-500'
                                : 'text-gray-400 dark:text-gray-500'
                        }`}
                    >
                        Builder
                    </button>
                    <button
                        onClick={() => setMobilePanel('results')}
                        className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wide transition-all ${
                            mobilePanel === 'results'
                                ? 'text-orange-500 border-b-2 border-orange-500'
                                : 'text-gray-400 dark:text-gray-500'
                        }`}
                    >
                        Results
                    </button>
                </div>
            )}

            {showIntro ? introScreen : (
                <div className="flex-1 flex flex-col lg:flex-row overflow-hidden relative">

                    {/* ═══ LEFT COLUMN: Query Builder (Scrollable) ═══ */}
                    <div id="tour-builder" className={`w-full lg:w-[450px] flex-shrink-0 flex-col bg-white dark:bg-[#1e293b] border-r border-gray-200 dark:border-white/10 relative z-30 ${mobilePanel !== 'builder' ? 'hidden lg:flex' : 'flex'}`}>

                        {showSuccessOverlay && (
                            <SuccessOverlay
                                challenge={challenge}
                                onNext={() => {
                                    setShowSuccessOverlay(false);
                                    if (!isLastChallenge) {
                                        handleNavigate(currentIndex + 1);
                                    } else if (nextModule) {
                                        navigate(nextModule.route);
                                    }
                                }}
                                onClose={() => setShowSuccessOverlay(false)}
                                renderRichText={renderRichText}
                                showCode={false}
                            />
                        )}

                        <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-4 pb-20">

                            {/* Question Card */}
                            <div id="tour-question" className="bg-gray-50 dark:bg-[#0a0e27]/50 rounded-xl border border-gray-200 dark:border-white/10 p-4 shadow-sm">
                                <div className="flex items-center gap-1.5 mb-2">
                                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: level?.color }}></span>
                                    <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: level?.color }}>{level?.title}</span>
                                </div>
                                {challenge.title && (
                                    <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-2 leading-snug">
                                        {challenge.title.replace(/^Q\d+[:. ]+/i, '')}
                                    </h3>
                                )}
                                {challenge.businessContext ? (
                                    <p
                                        className="text-xs text-gray-700 dark:text-gray-200 leading-relaxed font-medium mb-1"
                                        dangerouslySetInnerHTML={{
                                            __html: challenge.businessContext
                                                .replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-900 dark:text-white font-bold">$1</strong>')
                                                .replace(/\*(.*?)\*/g, '<em>$1</em>')
                                                .replace(/`([^`]+)`/g, '<code class="bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-500/30 rounded px-1 py-0 font-mono font-semibold text-[10px]">$1</code>')
                                        }}
                                    />
                                ) : challenge.question ? (
                                    <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed mb-1">{challenge.question}</p>
                                ) : null}
                                <div className="relative mt-2.5">
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={() => { setShowHint(!showHint); setShowSolution(false); }}
                                            className="flex items-center gap-1.5 text-[11px] text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 font-medium transition-colors"
                                        >
                                            <Lightbulb size={12} /> {showHint ? 'Hide Hint' : 'Show Hint'}
                                        </button>
                                        {DATA_VIZ_CHALLENGES[currentIndex]?.answer && (
                                            <button
                                                onClick={() => { setShowSolution(!showSolution); setShowHint(false); }}
                                                className="flex items-center gap-1.5 text-[11px] text-orange-500 dark:text-orange-400 hover:text-orange-600 dark:hover:text-orange-300 font-medium transition-colors"
                                            >
                                                <Eye size={12} /> {showSolution ? 'Hide Solution' : 'Show Solution'}
                                            </button>
                                        )}
                                    </div>

                                    {showHint && (
                                        <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-[#1c1f2e] shadow-xl">
                                            <div className="p-2.5">
                                                <div className="flex items-center justify-between mb-1">
                                                    <div className="flex items-center gap-1.5">
                                                        <Lightbulb size={11} className="text-amber-500" />
                                                        <span className="text-[10px] font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wider">Hint</span>
                                                    </div>
                                                    <button onClick={() => setShowHint(false)} className="text-amber-400 hover:text-amber-600 dark:hover:text-amber-200 transition-colors">
                                                        <X size={12} />
                                                    </button>
                                                </div>
                                                <div className="text-[11px] text-amber-800 dark:text-amber-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: renderHint(challenge.hint) }} />
                                            </div>
                                        </div>
                                    )}
                                    {showSolution && challenge.answer && (
                                        <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-xl border border-orange-200 dark:border-orange-500/20 bg-white dark:bg-[#0f1c2e] shadow-2xl overflow-hidden">
                                            {/* Header */}
                                            <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-orange-500 to-amber-500">
                                                <div className="flex items-center gap-1.5">
                                                    <Eye size={11} className="text-white/80" />
                                                    <span className="text-[10px] font-black text-white uppercase tracking-widest">Solution</span>
                                                </div>
                                                <button onClick={() => setShowSolution(false)} className="text-white/70 hover:text-white transition-colors">
                                                    <X size={12} />
                                                </button>
                                            </div>
                                            {/* Rows */}
                                            <div className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                                                <div className="flex items-center gap-2.5 px-3 py-2">
                                                    <div className="flex-shrink-0 w-5 h-5 rounded-md bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
                                                        <Table size={9} className="text-blue-500" />
                                                    </div>
                                                    <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 w-14 flex-shrink-0">Table</span>
                                                    <code className="text-[11px] font-mono font-semibold text-gray-800 dark:text-gray-100">{challenge.answer.table}</code>
                                                </div>
                                                {challenge.answer.aggregation && (
                                                    <div className="flex items-center gap-2.5 px-3 py-2">
                                                        <div className="flex-shrink-0 w-5 h-5 rounded-md bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center">
                                                            <Sigma size={9} className="text-orange-500" />
                                                        </div>
                                                        <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 w-14 flex-shrink-0">Metric</span>
                                                        <code className="text-[11px] font-mono font-semibold text-gray-800 dark:text-gray-100">{challenge.answer.aggregation.fn}(<span className="text-orange-500">{challenge.answer.aggregation.column}</span>)</code>
                                                    </div>
                                                )}
                                                {challenge.answer.groupBy?.length > 0 && (
                                                    <div className="flex items-start gap-2.5 px-3 py-2">
                                                        <div className="flex-shrink-0 w-5 h-5 rounded-md bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mt-0.5">
                                                            <Group size={9} className="text-blue-500" />
                                                        </div>
                                                        <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 w-14 flex-shrink-0 mt-0.5">Group by</span>
                                                        <div className="flex flex-wrap gap-1">
                                                            {challenge.answer.groupBy.map((g, i) => (
                                                                <span key={i} className="inline-flex items-center gap-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800/50 rounded px-1.5 py-0.5 text-[10px] font-mono font-semibold">
                                                                    {g.column}{g.granularity ? <span className="text-blue-400 dark:text-blue-500 font-normal"> {g.granularity}</span> : ''}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {challenge.expectedChartType && (
                                                    <div className="flex items-center gap-2.5 px-3 py-2">
                                                        <div className="flex-shrink-0 w-5 h-5 rounded-md bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
                                                            <BarChart3 size={9} className="text-amber-500" />
                                                        </div>
                                                        <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 w-14 flex-shrink-0">Chart type</span>
                                                        <span className="inline-flex items-center gap-1 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border border-amber-100 dark:border-amber-800/50 rounded px-1.5 py-0.5 text-[10px] font-semibold capitalize">
                                                            {challenge.expectedChartType}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            {/* Apply button */}
                                            <div className="px-3 py-2.5 bg-gray-50 dark:bg-white/[0.03] border-t border-gray-100 dark:border-white/[0.05]">
                                                <button
                                                    onClick={applySolution}
                                                    className="w-full flex items-center justify-center gap-1.5 text-[11px] font-bold bg-gradient-to-r from-orange-500 to-amber-500 hover:opacity-90 text-white rounded-lg px-2 py-1.5 transition-all shadow-sm shadow-orange-900/20"
                                                >
                                                    <Play size={10} /> Apply Solution
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {/* Query Builder Controls */}
                            <div className="space-y-2">

                                {/* ── Group 1: Table + Join ── */}
                                <div className="rounded-xl border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-[#1e293b]">

                                    {/* Table */}
                                    <div id="tour-table" className="p-3">
                                        <div className="flex items-center justify-between mb-3 bg-gray-50 dark:bg-white/5 mx-[-12px] mt-[-12px] px-3 py-2 border-b border-gray-100 dark:border-white/5 rounded-t-xl">
                                            <div className="flex items-center gap-2">
                                                <Table size={14} className="text-amber-500" />
                                                <span className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Table</span>
                                            </div>
                                            <button onClick={() => handlePreview('source')} className={`text-[10px] font-medium flex items-center gap-1 transition-all px-2 py-0.5 rounded border ${previewMode === 'source' ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-700/50 shadow-sm' : 'text-gray-400 hover:text-amber-600 border-transparent hover:bg-amber-50 dark:hover:bg-amber-500/10'}`} title={previewMode === 'source' ? "Close Preview" : "Preview raw data (Limit 100)"}>
                                                {previewMode === 'source' ? <X size={12} /> : <Eye size={12} />}
                                                {previewMode === 'source' ? 'Close' : 'Preview'}
                                            </button>
                                        </div>
                                        <div className="flex gap-2">
                                            {availableTables.map(opt => (
                                                <button
                                                    key={opt.value}
                                                    onClick={() => handleTableChange(opt.value)}
                                                    className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all flex-1 ${table === opt.value
                                                        ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                                                        : 'bg-gray-50 dark:bg-white/5 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-white/10 hover:border-amber-300'
                                                        }`}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Join */}
                                    <div id="tour-join" className="p-3 border-t border-gray-100 dark:border-white/5">
                                        <div className="flex items-center gap-2 mb-3 bg-gray-50 dark:bg-white/5 mx-[-12px] mt-[-12px] px-3 py-2 border-b border-gray-100 dark:border-white/5">
                                            <Link2 size={14} className="text-amber-500" />
                                            <span className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Join</span>
                                            <span className="text-[10px] text-gray-400 font-normal ml-auto">Optional</span>
                                        </div>
                                        {!join ? (
                                            <button
                                                onClick={() => setJoin({
                                                    table: availableTables.find(t => t.value !== table)?.value || '',
                                                    type: 'JOIN',
                                                    leftCol: schemas[table]?.[0]?.name || 'id',
                                                    rightCol: schemas[availableTables.find(t => t.value !== table)?.value]?.[0]?.name || 'id'
                                                })}
                                                className="flex items-center gap-1.5 text-xs text-amber-500 hover:text-amber-600 font-semibold py-1 px-2.5 border border-dashed border-amber-200 dark:border-amber-500/30 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-500/5 transition-all w-full justify-center"
                                            >
                                                <Plus size={12} /> Add Join
                                            </button>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <JoinTablePicker
                                                    join={join}
                                                    tables={availableTables}
                                                    joinTypes={JOIN_TYPES}
                                                    onChangeTable={val => { const firstCol = schemas[val]?.[0]?.name || 'id'; setJoin(prev => ({ ...prev, table: val, rightCol: firstCol })); }}
                                                    onChangeType={val => setJoin(prev => ({ ...prev, type: val }))}
                                                    mainTable={table}
                                                />
                                                <JoinColumnPicker
                                                    join={join}
                                                    leftCols={(schemas[table] || []).map(c => ({ value: c.name, label: c.name }))}
                                                    rightCols={(schemas[join.table] || []).map(c => ({ value: c.name, label: c.name }))}
                                                    onChangeLeftCol={val => setJoin(prev => ({ ...prev, leftCol: val }))}
                                                    onChangeRightCol={val => setJoin(prev => ({ ...prev, rightCol: val }))}
                                                    mainTableLabel={getDisplayName(table)}
                                                    joinTableLabel={getDisplayName(join.table)}
                                                />
                                                <button onClick={() => { setJoin(null); setResults(null); setValidation(null); }} className="text-gray-400 hover:text-red-500 transition-colors p-0.5 flex-shrink-0">
                                                    <X size={13} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>{/* end Group 1 */}

                                {/* ── Group 2: Filter ── */}
                                <div className="rounded-xl border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-[#1e293b]">

                                    {/* Filter */}
                                    <div id="tour-filter" className="p-2">
                                        <div className="flex items-center justify-between mb-2 bg-gray-50 dark:bg-white/5 mx-[-8px] mt-[-8px] px-3 py-1.5 border-b border-gray-100 dark:border-white/5 rounded-t-xl">
                                            <div className="flex items-center gap-2">
                                                <Filter size={14} className="text-amber-500" />
                                                <span className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Filter</span>
                                            </div>
                                            <button onClick={() => handlePreview('filter')} className={`text-[10px] font-medium flex items-center gap-1 transition-all px-2 py-0.5 rounded border ${previewMode === 'filter' ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-700/50 shadow-sm' : 'text-gray-400 hover:text-amber-600 border-transparent hover:bg-amber-50 dark:hover:bg-amber-500/10'}`}>
                                                {previewMode === 'filter' ? <X size={12} /> : <Eye size={12} />}
                                                {previewMode === 'filter' ? 'Close' : 'Preview'}
                                            </button>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-1.5">
                                            {filters.map((f, i) => (
                                                <FilterRow
                                                    key={i}
                                                    filter={f}
                                                    index={i}
                                                    columns={columns}
                                                    onUpdate={handleUpdateFilter}
                                                    onRemove={handleRemoveFilter}
                                                    onFetchValues={handleFetchDistinctValues}
                                                    join={join}
                                                />
                                            ))}
                                            <button
                                                onClick={handleAddFilter}
                                                className="flex items-center gap-1 text-[11px] text-amber-500 hover:text-amber-600 font-semibold px-2 py-1 border border-dashed border-amber-200 dark:border-amber-500/30 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-500/10 transition-colors"
                                            >
                                                <Plus size={10} /> {filters.length === 0 ? 'Add Filter' : 'Add'}
                                            </button>
                                        </div>
                                    </div>
                                </div>{/* end Group 2 */}

                                {/* ── Group 3: Metrics + Group By ── */}
                                <div className="rounded-xl border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-[#1e293b]">

                                    {/* Metrics */}
                                    <div id="tour-metric" className="p-3">
                                        <div className="flex items-center gap-2 mb-2 bg-gray-50 dark:bg-white/5 mx-[-12px] mt-[-12px] px-3 py-2 border-b border-gray-100 dark:border-white/5 rounded-t-xl">
                                            <Layers size={14} className="text-amber-500" />
                                            <span className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Metrics</span>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-1.5">
                                            {metrics.map((m, i) => (
                                                <MetricRow
                                                    key={i}
                                                    metric={m}
                                                    index={i}
                                                    columns={columns}
                                                    onUpdate={handleUpdateMetric}
                                                    onRemove={handleRemoveMetric}
                                                    join={join}
                                                    canRemove={true}
                                                />
                                            ))}
                                            <button
                                                onClick={handleAddMetric}
                                                className="flex items-center gap-1 text-[11px] text-amber-500 hover:text-amber-600 font-semibold px-2 py-1 border border-dashed border-amber-200 dark:border-amber-500/30 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-500/10 transition-colors"
                                            >
                                                <Plus size={10} /> {metrics.length === 0 ? 'Add Metric' : 'Add'}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Group By */}
                                    <div id="tour-groupby" className="p-3 border-t border-gray-100 dark:border-white/5">
                                        <div className="flex items-center justify-between mb-3 bg-gray-50 dark:bg-white/5 mx-[-12px] mt-[-12px] px-3 py-2 border-b border-gray-100 dark:border-white/5">
                                            <div className="flex items-center gap-2">
                                                <Group size={14} className="text-amber-500" />
                                                <span className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Group By</span>
                                            </div>
                                            <button onClick={() => handlePreview('group')} className={`text-[10px] font-medium flex items-center gap-1 transition-all px-2 py-0.5 rounded border ${previewMode === 'group' ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-700/50 shadow-sm' : 'text-gray-400 hover:text-amber-600 border-transparent hover:bg-amber-50 dark:hover:bg-amber-500/10'}`} title={previewMode === 'group' ? "Close Preview" : "Preview grouped data (Limit 100)"}>
                                                {previewMode === 'group' ? <X size={12} /> : <Eye size={12} />}
                                                {previewMode === 'group' ? 'Close' : 'Preview'}
                                            </button>
                                        </div>
                                        {/* Chips + Add dropdown in one row */}
                                        <div className="flex flex-wrap items-center gap-1.5">
                                            {groupBy.map(g => {
                                                const col = columns.find(c => c.name === g.column);
                                                const colCat = col ? getColumnCategory(col.type) : 'text';
                                                const isDate = colCat === 'date';
                                                const isNumeric = colCat === 'number' && !g.column.toLowerCase().includes('id');
                                                return (
                                                    <div
                                                        key={g.column}
                                                        draggable
                                                        onDragStart={() => { dragGroupByIdx.current = groupBy.indexOf(g); }}
                                                        onDragOver={e => e.preventDefault()}
                                                        onDrop={() => {
                                                            const fromIdx = dragGroupByIdx.current;
                                                            const toIdx = groupBy.indexOf(g);
                                                            if (fromIdx === null || fromIdx === toIdx) return;
                                                            const arr = [...groupBy];
                                                            arr.splice(toIdx, 0, arr.splice(fromIdx, 1)[0]);
                                                            setGroupBy(arr);
                                                            dragGroupByIdx.current = null;
                                                        }}
                                                        className="flex items-center gap-1 bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30 rounded-lg px-2 py-0.5 cursor-grab active:cursor-grabbing select-none"
                                                    >
                                                        <GripVertical size={10} className="text-amber-400 flex-shrink-0" />
                                                        <span className="text-[11px] font-mono">{g.column}</span>
                                                        {isDate && (
                                                            <div className="w-16">
                                                                <CustomSelect
                                                                    value={g.granularity || 'day'}
                                                                    onChange={val => handleGranularityChange(g.column, val)}
                                                                    options={DATE_GRANULARITIES.map(dg => ({ value: dg.value, label: dg.label }))}
                                                                    buttonClass="!bg-transparent !border-none !p-0 !h-auto !text-orange-600 dark:!text-orange-400 !font-bold !text-[10px] !uppercase !tracking-wider"
                                                                    className="inline-block w-auto border-l border-orange-200 dark:border-orange-500/30 pl-2 ml-1"
                                                                />
                                                            </div>
                                                        )}
                                                        {isNumeric && (
                                                            <div className="flex items-center gap-1 border-l border-amber-200 dark:border-amber-500/30 pl-1.5 ml-0.5">
                                                                <span className="text-[9px] font-bold text-amber-500 uppercase tracking-wider">bucket</span>
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    placeholder="size"
                                                                    value={g.bucket?.size || ''}
                                                                    onChange={e => handleBucketChange(g.column, e.target.value)}
                                                                    className="w-14 text-[10px] bg-white dark:bg-slate-800 border border-amber-200 dark:border-amber-500/30 rounded px-1.5 py-0.5 text-amber-700 dark:text-amber-300 font-mono focus:outline-none focus:ring-1 focus:ring-orange-400"
                                                                />
                                                            </div>
                                                        )}
                                                        <button onClick={() => handleToggleGroupBy(g.column)} className="text-amber-400 hover:text-red-500 transition-colors ml-1 p-0.5 hover:bg-amber-100 dark:hover:bg-amber-500/20 rounded">
                                                            <X size={11} />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                            <GroupByPicker
                                                columns={columns}
                                                groupBy={groupBy}
                                                onToggle={handleToggleGroupBy}
                                                join={join}
                                                getColumnCategory={getColumnCategory}
                                            />
                                        </div>
                                    </div>
                                </div>{/* end Group 3 */}

                            </div>{/* end space-y-2 */}

                            {/* Limit Results - shown only when challenge allows it */}
                            {challenge.availableControls?.limit && (
                                <div className="p-3 border-t border-gray-100 dark:border-white/5">
                                    <div className="flex items-center gap-2 mb-2 bg-gray-50 dark:bg-white/5 mx-[-12px] mt-[-12px] px-3 py-2 border-b border-gray-100 dark:border-white/5">
                                        <Hash size={14} className="text-orange-500" />
                                        <span className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Limit</span>
                                    </div>
                                    <input
                                        type="number"
                                        min="1"
                                        max="1000"
                                        value={limit || ''}
                                        onChange={e => setLimit(e.target.value ? parseInt(e.target.value, 10) : null)}
                                        placeholder="Top N results..."
                                        className="w-full text-xs bg-white dark:bg-slate-800 border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-1 focus:ring-orange-500 outline-none"
                                    />
                                </div>
                            )}

                            {/* Buttons: Visualize & Submit */}
                            <div className="p-3 bg-gray-50 dark:bg-white/5 rounded-b-xl border-t border-gray-100 dark:border-white/5 flex gap-2">
                                <button
                                    id="tour-run"
                                    onClick={handleVisualize}
                                    disabled={isRunning}
                                    className={`flex-1 py-2 rounded-lg font-bold flex items-center justify-center gap-2 transition-all shadow-lg shadow-orange-500/10 ${isRunning && !previewMode
                                        ? 'bg-gray-200 dark:bg-gray-800 cursor-not-allowed text-gray-500'
                                        : 'bg-white dark:bg-slate-700 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-slate-600'
                                        }`}
                                >
                                    {isRunning && !previewMode ? <Loader2 size={18} className="animate-spin" /> : <Play size={18} className="text-orange-500" fill="currentColor" />}
                                    {isRunning && !previewMode ? 'Running...' : 'Visualize'}
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={isRunning}
                                    className="flex-1 py-2 rounded-lg font-bold flex items-center justify-center gap-2 transition-all shadow-lg shadow-green-500/20 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white transform hover:scale-[1.02] active:scale-[0.98]"
                                >
                                    <CheckCircle2 size={18} />
                                    Submit
                                </button>
                            </div>

                        </div>
                    </div>

                    {/* ═══ RIGHT COLUMN: Results & Visualization (Scrollable) ═══ */}
                    <div id="tour-results" className={`flex-1 flex-col bg-gray-50 dark:bg-[#0a0e27] overflow-hidden lg:border-l border-gray-200 dark:border-white/10 relative ${mobilePanel !== 'results' ? 'hidden lg:flex' : 'flex'}`}>

                        <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-4">

                            {/* Visualization Goals (Always Visible) */}
                            <ChartRequirements challenge={challenge} />

                            {/* Success / Error Overlay */}
                            {validation && !validation.passed && (
                                <div className="absolute top-0 left-0 right-0 z-50 flex justify-center pointer-events-none">
                                    <div className={`pointer-events-auto mt-4 mx-4 px-4 py-3 border rounded-xl shadow-xl flex items-start gap-3 animate-in fade-in slide-in-from-top-4 backdrop-blur-md max-w-lg ${validation.passed
                                        ? 'bg-white/95 dark:bg-[#1e293b]/95 border-green-200 dark:border-green-500/30'
                                        : 'bg-white/95 dark:bg-[#1e293b]/95 border-red-200 dark:border-red-500/30'
                                        }`}>
                                        <div className={`p-1.5 rounded-full mt-0.5 ${validation.passed
                                            ? 'bg-green-100 dark:bg-green-500/20 text-green-500 dark:text-green-400'
                                            : 'bg-red-100 dark:bg-red-500/20 text-red-500 dark:text-red-400'
                                            }`}>
                                            {validation.passed ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className={`text-sm font-bold ${validation.passed ? 'text-green-900 dark:text-white' : 'text-red-900 dark:text-white'}`}>
                                                {validation.passed ? 'Correct!' : 'Not quite right'}
                                            </h3>
                                            <p className={`text-xs mt-1 leading-relaxed ${validation.passed ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}
                                                dangerouslySetInnerHTML={{ __html: (validation.feedback || '')
                                                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                                    .replace(/`([^`]+)`/g, '<code class="font-mono bg-black/10 dark:bg-white/10 px-1 rounded">$1</code>')
                                                }}
                                            />
                                            {validation.passed && (!isLastChallenge || nextModule) && (
                                                <button
                                                    onClick={() => {
                                                        setValidation(null);
                                                        if (!isLastChallenge) { handleNavigate(currentIndex + 1); }
                                                        else if (nextModule) { navigate(nextModule.route); }
                                                    }}
                                                    className="mt-2.5 flex items-center gap-1.5 text-xs font-bold text-white bg-green-500 hover:bg-green-600 px-3 py-1.5 rounded-lg transition-colors shadow-sm"
                                                >
                                                    {isLastChallenge && nextModule ? `Next Module` : 'Next Question'} <ChevronRight size={14} />
                                                </button>
                                            )}
                                        </div>
                                        <button onClick={() => setValidation(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors p-1">
                                            <X size={14} />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Row limit warning toast */}
                            {rowLimitWarning && (
                                <div className="absolute top-0 left-0 right-0 z-50 flex justify-center pointer-events-none">
                                    <div className="pointer-events-auto mt-4 mx-4 px-4 py-3 border rounded-xl shadow-xl flex items-start gap-3 animate-in fade-in slide-in-from-top-4 backdrop-blur-md max-w-lg bg-white/95 dark:bg-[#1e293b]/95 border-amber-200 dark:border-amber-500/30">
                                        <div className="p-1.5 rounded-full mt-0.5 bg-amber-100 dark:bg-amber-500/20 text-amber-500 dark:text-amber-400 flex-shrink-0">
                                            <AlertCircle size={16} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-sm font-bold text-amber-900 dark:text-white">Result Truncated</h3>
                                            <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5 leading-relaxed">{rowLimitWarning}</p>
                                        </div>
                                        <button onClick={() => setRowLimitWarning(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors p-1 flex-shrink-0">
                                            <X size={14} />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Query error toast - same overlay pattern as validation */}
                            {queryError && (
                                <div className="absolute top-0 left-0 right-0 z-50 flex justify-center pointer-events-none">
                                    <div className="pointer-events-auto mt-4 mx-4 px-4 py-3 border rounded-xl shadow-xl flex items-start gap-3 animate-in fade-in slide-in-from-top-4 backdrop-blur-md max-w-lg bg-white/95 dark:bg-[#1e293b]/95 border-red-200 dark:border-red-500/30">
                                        <div className="p-1.5 rounded-full mt-0.5 bg-red-100 dark:bg-red-500/20 text-red-500 dark:text-red-400 flex-shrink-0">
                                            <AlertCircle size={16} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-sm font-bold text-red-900 dark:text-white">Query Error</h3>
                                            <pre className="text-xs text-red-700 dark:text-red-300 font-mono whitespace-pre-wrap mt-1 leading-relaxed">{queryError}</pre>
                                        </div>
                                        <button onClick={() => setQueryError(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors p-1 flex-shrink-0">
                                            <X size={14} />
                                        </button>
                                    </div>
                                </div>
                            )}



                            {/* Tab Bar: Table | Chart | Schema + SQL Button */}
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div className="flex items-center gap-2">
                                    <div className="flex items-center gap-1 bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-white/10 p-1">
                                        {/* Chart & Table Swapped */}
                                        {[
                                            { mode: 'chart', icon: BarChart3, label: 'Chart' },
                                            { mode: 'table', icon: Table, label: 'Table' },
                                            { mode: 'schema', icon: Database, label: 'Schema' },
                                        ].map(({ mode, icon: Icon, label }) => (
                                            <button
                                                key={mode}
                                                id={mode === 'schema' ? 'tour-schema' : undefined}
                                                onClick={() => setViewMode(mode)}
                                                className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md transition-all ${viewMode === mode
                                                    ? 'bg-orange-500 text-white shadow'
                                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                                                    }`}
                                            >
                                                <Icon size={13} /> {label}
                                            </button>
                                        ))}
                                    </div>

                                    {viewMode === 'chart' && (
                                        <div className="hidden"></div> // Hidden as controls moved to bottom
                                    )}
                                </div>

                                <div className="flex items-center gap-3">
                                    {results && (
                                        <button
                                            onClick={() => setViewMode('sql')}
                                            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all ${viewMode === 'sql'
                                                ? 'bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-200 dark:border-orange-500/30'
                                                : 'bg-white dark:bg-slate-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-white/10 hover:border-orange-300'
                                                }`}
                                        >
                                            <Code size={13} /> SQL
                                        </button>
                                    )}
                                    {results && results.length > 0 && (
                                        <span className="text-[11px] text-gray-400 font-mono">
                                            {results.length} rows {previewMode ? <span className="text-blue-500 font-bold ml-1">(Preview)</span> : ''} • {executionTime}s
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Tab Content */}
                            <div className="bg-white dark:bg-[#1e293b] rounded-xl border border-gray-200 dark:border-white/10 overflow-hidden">
                                {viewMode === 'table' && (
                                    results && results.length > 0 ? (
                                        <ResultsTable rows={results} executionTime={executionTime} />
                                    ) : (
                                        <div className="flex flex-col items-center justify-center py-20 text-center">
                                            <BarChart3 size={48} className="text-gray-300 dark:text-gray-600 mb-4" />
                                            <h3 className="text-lg font-bold text-gray-500 dark:text-gray-400 mb-2">No Results Yet</h3>
                                            <p className="text-sm text-gray-400 dark:text-gray-500 max-w-sm">
                                                Configure the query on the left, then hit <strong>Run Query</strong>.
                                            </p>
                                        </div>
                                    )
                                )}
                                {viewMode === 'chart' && (
                                    results && results.length > 0 ? (
                                        <div className={`flex flex-col ${chartType === 'map' ? 'p-0' : 'px-6 pt-6 pb-4'}`}>
                                            <div className={chartType === 'map' ? 'h-[500px]' : 'h-[440px]'}>
                                                <ChartRenderer
                                                    data={sortedResults ?? results}
                                                    chartType={chartType}
                                                    xAxis={xAxis}
                                                    yAxis={yAxis}
                                                    metrics={metrics}
                                                    showGrid={showGrid}
                                                    legendPosition={legendPosition}
                                                    showLabels={showLabels}
                                                    stacked={stacked}
                                                    orientation={orientation}
                                                />
                                            </div>
                                            <ChartControls
                                                chartType={chartType}
                                                setChartType={setChartType}
                                                xAxis={xAxis}
                                                setXAxis={setXAxis}
                                                yAxis={yAxis}
                                                setYAxis={setYAxis}
                                                columns={results && results.length > 0 ? Object.keys(results[0]) : []}
                                                showGrid={showGrid}
                                                setShowGrid={setShowGrid}
                                                showLabels={showLabels}
                                                setShowLabels={setShowLabels}
                                                stacked={stacked}
                                                setStacked={setStacked}
                                                orientation={orientation}
                                                setOrientation={setOrientation}
                                                legendPosition={legendPosition}
                                                setLegendPosition={setLegendPosition}
                                                orderBy={orderBy}
                                                setOrderBy={setOrderBy}
                                            />
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center justify-center py-20 text-center">
                                            <BarChart3 size={48} className="text-gray-300 dark:text-gray-600 mb-4" />
                                            <h3 className="text-lg font-bold text-gray-500 dark:text-gray-400 mb-2">No Data to Chart</h3>
                                            <p className="text-sm text-gray-400 dark:text-gray-500 max-w-sm">
                                                Run a query first to visualize results.
                                            </p>
                                        </div>
                                    )
                                )}
                                {viewMode === 'sql' && (
                                    <div className="bg-gray-50 dark:bg-[#0a1220] border border-gray-200 dark:border-white/10 p-5 rounded-lg">
                                        <div className="flex items-center gap-2 mb-3">
                                            <Code size={12} className="text-orange-400" />
                                            <span className="text-[10px] font-bold text-orange-400 uppercase tracking-wider">Generated SQL</span>
                                        </div>
                                        <pre className="text-sm text-gray-700 dark:text-green-400 font-mono whitespace-pre-wrap leading-relaxed">{displaySQL}</pre>
                                    </div>
                                )}
                                {viewMode === 'schema' && (
                                    <div className="p-4">
                                        <SchemaPanel schemas={schemas} executeQuery={executeQuery} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataVizTool;
